// ── WNY corn yield model — THE model, shared by the page and the cron ────────
//
// Lifted out of src/App.jsx (it lived there, browser-only) so that two callers
// can run the identical code. The lift itself was verbatim — script-generated,
// not retyped; the only deliberate addition since is the `overreach` block in
// miComputeYield and its payload summary. So that two callers can run it:
//   • MarketIntelligenceView's Yield tab  — import { ... } from "./miYieldModel"
//   • api/_mi-snapshot.js (daily cron)    — require("../src/miYieldModel.js")
//
// WHY THIS MOVED. mi_model_snapshot is what the daily email brief renders, and
// it was written ONLY by the browser page, on load. So the model was as fresh as
// the last time a human happened to open the Yield tab — the snapshot sat at
// Aug 9 while the brief kept mailing it, and mi_yield_daily has holes on every
// day nobody visited (Jul 17, 27, 29, 31, Aug 3...). The cron now recomputes it
// each morning. See api/_mi-snapshot.js.
//
// CommonJS on purpose: the same file is require()d by a Vercel function and
// import-ed by webpack. Same pattern as src/grainTargets.js.
//
// PURE. No React, no JSX, no sbFetch, no window — every input is passed in, so
// the browser and the server can feed it byte-identical rows. Keep it that way:
// the moment this file reaches for a global, the two callers can diverge.
// UI pieces that lived among these functions (StageRail, the SVG chart builder)
// deliberately stayed behind in App.jsx.


// ── Corn final planting date ──────────────────────────────────────────────
// Moved here with the model (it reads NY's FPD). App.jsx imports it back, so
// there is still exactly one table.
// ── 2026 RMA Final Planting Dates by State ──────────────────────────────────
// Source: RMA Dates Programs map viewer (public-rma.fpac.usda.gov/apps/mapviewer/dates).
// Corn is state-uniform across the agency's book. Soybeans split in PA along
// roughly the I-80 line — northern counties get the earlier date. OH/MI/IN soy
// dates are approximate carry-forwards; verify against the map before locking.
const CORN_FPD_BY_STATE = {
  NY: "June 10", PA: "June 10", OH: "June 5", MI: "June 5",
  IN: "June 5",  MD: "June 5",
};
function getCornFPD(state)   { return CORN_FPD_BY_STATE[(state||"").toUpperCase()] || null; }

// ── Client-side corn yield model (ported from api/_mi-yield.js) ──────────────
// Runs in the browser via sbFetch (same direct-Supabase pattern the rest of the
// app uses) so the Yield tab works on localhost AND deployed with no /api
// dependency. Trend baseline + statewide pace use nass_data (browser-readable);
// weather layer activates automatically once weather_county_daily is populated.
// ── The counties this model covers ───────────────────────────────────────────
//
// 39 New York counties. Was 16 (Western NY); the other 23 were admitted
// 2026-08-13 once they could be admitted at FULL PARITY. The rule they had to
// meet is Kevin's, from the morning of the same day:
//
//   "I want all counties to read the same or we go back to just the 16."
//
// A 52-county version had shipped and been reverted hours earlier because it
// failed exactly that: the added counties had no grid cells, so they resolved
// from a county centroid (wx_source `county_power`) while the 16 kept 4km
// sub-county resolution. One page, two meanings — and the map showed it plainly,
// because the high-def layer could only paint the west.
//
// "READ THE SAME" HAS TWO TIERS BEHIND IT. Only one of them is buildable, and
// conflating them is what produced the reverted version.
//
//   TIER 1 — the 4km grid. BUILT. wny_grid_cells went 338 -> 1,271 (933 new
//     cells over 35 counties): CDL corn acres, SSURGO nccpi/awc, gridMET daily
//     2021-2026 and the 1995-2025 per-cell climatology. Every county below now
//     reports `grid_mech_cornsoil` — verified per county, not assumed.
//
//   TIER 2 — NASS county yield history. NOT buildable; it is what USDA
//     publishes. The 16 all carry 6-11 yield years (floor: Orleans and Allegany
//     at 6), 14 of 16 current through 2024-25. That floor IS the bar, so the bar
//     is >= 6 NASS yield years. Eastern NY splits 23 / 8 / 4 against it.
//
// THE 12 HELD BACK, AND WHY EACH IS A DIFFERENT PROBLEM:
//   1-5 yield years (8): CHEMUNG 5, DELAWARE 5, FRANKLIN 5, SCHOHARIE 5,
//     ALBANY 4, FULTON 2, CLINTON 1, SULLIVAN 1. These have cells and would
//     render — miBaseline() does NOT refuse them, it returns
//     `mean (N yrs, trend not fittable)` at low confidence. Clinton's would be a
//     2026 forecast off its single 2017 observation, in the same colour scale as
//     Genesee's 11-year trend. Disclosed, but not the same claim.
//   0 yield years (4): WARREN, ESSEX, GREENE, SCHENECTADY. miComputeYield
//     already refuses these outright with `error: "no NASS yield history"` and a
//     null forecast, so they would be blank counties on the map.
//   Their cells are BUILT and stay built. Only the list gates them, so if NASS
//     publishes more years a county graduates by being added here — nothing to
//     re-ingest.
//
// The planting-pace factor still comes from OUR customers' survey, which is a WNY
// book — counties outside it fall back to NASS statewide pace. That is a
// difference in an INPUT's provenance, not in the model tier, and it predates
// this expansion.
//
// NEW YORK ONLY on purpose. Ten of this model's inputs are state-level (NASS
// statewide planting pace, the dairy-herd silage displacement, topsoil moisture,
// crop condition) and are fetched `state=eq.NY`. Adding Pennsylvania counties to
// this list WITHOUT making that context state-aware would run PA ground against
// New York's dairy herd and New York's planting pace — it would render, look
// plausible, and be wrong. Source: api/_counties.js (MI_COUNTIES).
//
// ── THIS LIST GATES THE YIELD MODEL, NOT THE MAP (2026-08-20) ───────────────
// It used to gate both, so the 12 counties held back above were missing from
// corn density, soil and the gridMET weather surfaces too — layers that never
// touch a NASS yield row. CLINTON is 24,269 CDL corn acres, more than Erie or
// Wayne, and it was simply not drawn. The Market Intelligence map now draws every
// NY county outside NYC and decides per LAYER which of them carry a value:
// MI_CELL_DATA_LAYERS in src/App.jsx (guarded by tools/check-mi-layers.js) marks
// the layers that resolve per grid cell, and those reach all 51 counties with
// cells. Anything a layer cannot speak for is hatched as no-data, never omitted.
// Adding a county HERE is still the full-parity decision described above.
//
// Worth knowing before that decision is revisited: Clinton and Franklin fail the
// >= 6 NASS yield-year bar on GRAIN while carrying 10 and 7 years of county
// SILAGE yield (measured 2026-08-20). The bar is grain because the model forecasts
// bu/ac. It is not a claim that they grow little corn.
//
// TO ADD A COUNTY: build its cells first (c:/tmp/east_gen_cells.js ->
// east_cdl.js -> east_soil.js -> east_insert_cells.js -> east_gridmet.js),
// confirm it reports `grid_mech_cornsoil` AND carries >= 6 NASS yield years,
// THEN add it here. In that order, or the page goes back to meaning two
// different things at once.
const MI_MODEL_COUNTIES = [
  // `yieldYears` = NASS county corn GRAIN yield years on file, MEASURED, not
  // recalled. It used to be a trailing comment on the eastern entries only and
  // was absent on the WNY 16 — so the bar the list is gated on could not be
  // checked against the list itself. Re-derive with tools/mi-county-years.js.
  // ── The Western NY core: the ground the book is written on.
  { fips: "36003", name: "ALLEGANY", wny: true, yieldYears: 6 },
  { fips: "36009", name: "CATTARAUGUS", wny: true, yieldYears: 9 },
  { fips: "36011", name: "CAYUGA", wny: true, yieldYears: 10 },
  { fips: "36013", name: "CHAUTAUQUA", wny: true, yieldYears: 9 },
  { fips: "36029", name: "ERIE", wny: true, yieldYears: 11 },
  { fips: "36037", name: "GENESEE", wny: true, yieldYears: 10 },
  { fips: "36051", name: "LIVINGSTON", wny: true, yieldYears: 11 },
  { fips: "36055", name: "MONROE", wny: true, yieldYears: 11 },
  { fips: "36063", name: "NIAGARA", wny: true, yieldYears: 9 },
  { fips: "36069", name: "ONTARIO", wny: true, yieldYears: 11 },
  { fips: "36073", name: "ORLEANS", wny: true, yieldYears: 6 },
  { fips: "36099", name: "SENECA", wny: true, yieldYears: 10 },
  { fips: "36101", name: "STEUBEN", wny: true, yieldYears: 11 },
  { fips: "36117", name: "WAYNE", wny: true, yieldYears: 11 },
  { fips: "36121", name: "WYOMING", wny: true, yieldYears: 11 },
  { fips: "36123", name: "YATES", wny: true, yieldYears: 8 },
  // ── Admitted 2026-08-13 at full parity, all >= 6 yield years.
  { fips: "36007", name: "BROOME", wny: false, yieldYears: 9 },
  { fips: "36017", name: "CHENANGO", wny: false, yieldYears: 9 },
  { fips: "36021", name: "COLUMBIA", wny: false, yieldYears: 8 },
  { fips: "36023", name: "CORTLAND", wny: false, yieldYears: 9 },
  { fips: "36027", name: "DUTCHESS", wny: false, yieldYears: 6 },
  { fips: "36043", name: "HERKIMER", wny: false, yieldYears: 9 },
  { fips: "36045", name: "JEFFERSON", wny: false, yieldYears: 11 },
  { fips: "36049", name: "LEWIS", wny: false, yieldYears: 10 },
  { fips: "36053", name: "MADISON", wny: false, yieldYears: 11 },
  { fips: "36057", name: "MONTGOMERY", wny: false, yieldYears: 10 },
  { fips: "36065", name: "ONEIDA", wny: false, yieldYears: 11 },
  { fips: "36067", name: "ONONDAGA", wny: false, yieldYears: 11 },
  { fips: "36071", name: "ORANGE", wny: false, yieldYears: 7 },
  { fips: "36075", name: "OSWEGO", wny: false, yieldYears: 8 },
  { fips: "36077", name: "OTSEGO", wny: false, yieldYears: 8 },
  { fips: "36083", name: "RENSSELAER", wny: false, yieldYears: 11 },
  { fips: "36089", name: "ST LAWRENCE", wny: false, yieldYears: 10 },
  { fips: "36091", name: "SARATOGA", wny: false, yieldYears: 9 },
  { fips: "36097", name: "SCHUYLER", wny: false, yieldYears: 7 },
  { fips: "36107", name: "TIOGA", wny: false, yieldYears: 6 },
  { fips: "36109", name: "TOMPKINS", wny: false, yieldYears: 6 },
  { fips: "36111", name: "ULSTER", wny: false, yieldYears: 9 },
  { fips: "36115", name: "WASHINGTON", wny: false, yieldYears: 9 },
  // ── Admitted 2026-09-25 (Kevin: "all NY counties are not reading in the NY
  //    yield prediction model"). The first four sit at FIVE grain years — one
  //    short of the old bar, which he lowered deliberately rather than leaving
  //    them hatched. They are thin, not absent, and `MI_THIN_YIELD_YEARS`
  //    below is what keeps the page saying which is which.
  //    SUFFOLK clears 6 on its own (7) and was simply never in the list — the
  //    open question api/_counties.js has carried since 2026-08-20.
  //    All five were confirmed to hold grid cells FIRST, in the order this
  //    file requires: cells -> CDL corn -> soil -> then admission.
  { fips: "36025", name: "DELAWARE", wny: false, yieldYears: 5 },
  { fips: "36095", name: "SCHOHARIE", wny: false, yieldYears: 5 },
  { fips: "36033", name: "FRANKLIN", wny: false, yieldYears: 5 },
  { fips: "36015", name: "CHEMUNG", wny: false, yieldYears: 5 },
  { fips: "36103", name: "SUFFOLK", wny: false, yieldYears: 7 },
];
// ── HOW MANY YEARS A COUNTY HAS TO HAVE, AND WHEN TO SAY IT IS THIN ─────────
//
// Kevin, 2026-09-25: *"all NY counties are not reading in the NY yield prediction
// model."* Measured that day, of the 51 NY counties the map draws, 39 were in the
// model and 12 were not — and NOT ONE of the 12 cleared the old six-year bar on
// NASS county corn GRAIN yield. The best four sat at five.
//
// He lowered the bar to five rather than leave them hatched. That is a judgment
// about how thin a history may be, not a measurement, so it is a NAMED CONSTANT
// here instead of a number inside a comment — and the four counties it admits are
// derived from `yieldYears`, never listed by hand.
//
// ⚠ ADMITTING A COUNTY AND TRUSTING IT EQUALLY ARE DIFFERENT DECISIONS. A county
// resting on five years must not look on screen like one resting on eleven, so
// `MI_THIN_YIELD_YEARS` is what the page uses to say so. Dropping that label would
// leave the map asserting the same confidence for both, which is the half of this
// change that could quietly mislead.
//
// The bar is GRAIN because the model forecasts bu/ac. Several of the counties
// still outside it are dairy counties that chop nearly everything — CLINTON
// carries 10 years of county SILAGE yield and 1 of grain, SULLIVAN 9 and 1. That
// is not a claim they grow little corn; it is a different crop, in a different
// unit, and folding ton/ac into a bu/ac surface is the units mistake this repo
// forbids outright.
const MI_MIN_YIELD_YEARS = 5;   // below this a county is not modelled at all
const MI_THIN_YIELD_YEARS = 6;  // at or above this the history is not flagged thin
const miCountyIsThin = (c) => !!c && Number(c.yieldYears) < MI_THIN_YIELD_YEARS;
// Names, so a renderer can label without re-deriving the rule.
const MI_THIN_COUNTY_NAMES = new Set(
  MI_MODEL_COUNTIES.filter(miCountyIsThin).map((c) => String(c.name).toUpperCase()));

// The Western NY core — the 16 counties on the 4km grid, and the ground the book
// is actually written on. Derived FROM the list above rather than kept as a second
// list, so a county can never be in one and not the other.
const MI_WNY_FIPS = new Set(MI_MODEL_COUNTIES.filter((c) => c.wny).map((c) => c.fips));
// The same 16 counties BY NAME, because NASS keys its rows on the county name and
// has no FIPS. Derived here for the same reason the FIPS set is: miCowSilage used
// to carry its OWN hardcoded 16-county "WNY" list, and the two had drifted — that
// one held ONONDAGA and SCHUYLER and lacked STEUBEN and YATES. So "Western NY"
// meant one set of counties for yield and a different set for the dairy herd, in
// the same result object, and nothing could tell you which.
const MI_WNY_NAMES = new Set(MI_MODEL_COUNTIES.filter((c) => c.wny).map((c) => c.name.toUpperCase()));
// Every modelled county by name — the "All New York" region's county set.
const MI_NY_NAMES = new Set(MI_MODEL_COUNTIES.map((c) => c.name.toUpperCase()));
// ── THE MODEL IS NEW YORK, AND THE GRID NO LONGER IS ─────────────────────────
// wny_grid_cells held one state from the day it was built, so every reader that
// wanted "all of New York" wrote "all of the table". Pennsylvania's 1,231 cells
// (2026-09-02) make that read wrong in three ways at once, and none of them
// announce themselves:
//
//   - the daily grid fetch ASCENDS by obs_date and its cap is sized in cells, so
//     passing it drops the NEWEST days and the page reads FROZEN at a date;
//   - the map's IDW control points come from wny_grid_season_anomalies, so PA
//     cells would bleed PA weather into the Southern Tier counties they border;
//   - SIXTEEN county names exist in both states (ERIE, MONROE, WARREN, CLINTON,
//     FRANKLIN, WYOMING, WASHINGTON, GREENE, SULLIVAN, TIOGA, JEFFERSON, WAYNE,
//     COLUMBIA, DELAWARE, FULTON, MONTGOMERY), so anything keyed on the bare
//     county_name silently merges a PA county into its NY namesake.
//
// One filter string, both callers (this page and api/_mi-snapshot.js), for the
// reason this file already exists: a rule defined twice drifts once. The model's
// own counties are all state 36 by construction — MI_MODEL_COUNTIES carries the
// FIPS — so this narrows the read to exactly the ground the model was fit on and
// nothing else.
//
// FIELD-LEVEL readers do NOT take this filter and must not: the Field Weather
// page, the Corn Maturity view and api/_jd.js all resolve the NEAREST cell to one
// field, which is precisely the thing a Pennsylvania grower needs to find.
const MI_GRID_STATE_FIPS = "36";
const MI_GRID_STATE_FILTER = `state_fips=eq.${MI_GRID_STATE_FIPS}`;
// Region key -> the county-name set NASS-keyed aggregates (the dairy herd) sum over.
// Keep in step with MI_REGIONS at the bottom of this file; a region missing here
// gets no regional dairy figure rather than another region's.
const MI_REGION_NAMES = { ny: MI_NY_NAMES, wny: MI_WNY_NAMES };

const MI_COEF = {
  GDD_SENS: 0.15, GDD_CLAMP: 0.12, DRY_SENS: 0.35, WET_THRESH: 0.35, WET_SENS: 0.25,
  WEATHER_FLOOR: 0.70, WEATHER_CEIL: 1.20, PACE_SENS: 0.15, PACE_FLOOR: 0.94, PACE_CEIL: 1.02,
  // ── Beneficial-rain reward (2026-06-03). Corn's yield response to growing-season
  //    moisture is an inverted-U, not one-way: a deficit hurts, but near-to-moderate
  //    surplus in the water-hungry silk/fill windows HELPS (it's not just "avoid drought").
  //    Backtest (2019-2024, 76 county-years vs NASS) showed the old downside-only precip
  //    term injected a chronic ~8% haircut (mean factor 0.924, never >1.0) and a -10 bu
  //    bias; adding upside cut MAE 19.6->17.4 and bias -10.4->-3.8. See project_weather_factor_backtest.
  //    Benefit ramps 0->WET_GAIN at +WET_OPT surplus, diminishes back to 0 by WET_THRESH,
  //    then the existing excess-moisture penalty (WET_SENS) takes over. DRY_CAP bounds the
  //    deficit penalty so one noisy dry window can't dominate. GDD term is already symmetric.
  WET_OPT: 0.15, WET_GAIN: 0.07, DRY_CAP: 0.15,
  // ── Soil-water sensitivity (2026-06-03). SSURGO available-water-storage (AWS
  //    0-150cm, cm) per cell scales the PRECIP effect: gravelly/low-AWC ground
  //    dries out faster (steeper drought penalty) AND can't bank water, so timely
  //    rain helps it more (bigger reward) — symmetric. S = clamp(1 + AWC_SENS×
  //    (AWC_REF − awc)/AWC_REF, AWC_S_MIN, AWC_S_MAX). Applied PER CELL in
  //    miGridShadowWeather (where gravelly pockets matter); county NASS can't
  //    validate sub-county pockets, but the backtest showed it's neutral-to-positive
  //    at county level (sens 1.0 sweet spot). See project_weather_factor_backtest.
  AWC_REF: 17.5, AWC_SENS: 1.0, AWC_S_MIN: 0.70, AWC_S_MAX: 1.45,
  // ── Phase 1 stress terms (2026-05-29). All evidence-gated: counted only on
  //    observed/forecast days, so future windows still filled with climatology
  //    contribute zero penalty until real data arrives. Tunable in Model Lab.
  // #5 Grain-fill heat: NY corn loses yield to senescence/respiration when
  //    grain-fill days run hot. Counts fill-window days with Tmax > threshold.
  HEAT_STRESS_TMAX_F: 90, HEAT_STRESS_PER_DAY: 0.004, HEAT_STRESS_MAX: 0.10,
  // #6 Heat×drought interaction: a precip deficit hurts far more when the same
  //    window is also hot. Amplifies the drought penalty by up to this factor.
  HEAT_DROUGHT_INTER: 0.5,
  // #8 Killing freeze: a hard freeze in the grain-fill (pre-black-layer) window
  //    truncates fill — devastating for late/silage-redirected cohorts.
  FREEZE_TMIN_F: 28, FREEZE_PER_DAY: 0.02, FREEZE_MAX: 0.20,
  // ── Soil-water BUCKET (2026-06-06). A daily FAO-56 water balance that REPLACES
  //    the old precip-anomaly + cold/wet early-stress terms as the single moisture
  //    signal. Tracks a root-depth-scaled soil-water bank (SSURGO AWC), drawn down
  //    by Hargreaves ET × crop coefficient and refilled by rain. Stress accrues
  //    only when the bank draws below the readily-available threshold, and
  //    accelerates as it empties — so a dry spell over a FULL profile costs ~0,
  //    while a dry spell over a DRAWN-DOWN profile bites and compounds (Kevin's
  //    "fine line"). Re-centered: a NORMAL season (own climatology) reads ~1.0,
  //    so no chronic haircut and no overnight swing. Evidence-gated: future days
  //    are climatology-filled and add no marginal stress until real data lands,
  //    which makes the headline glide (~0.3-0.5%/stressed-day) rather than step.
  //    CALIBRATION: fit on 2019-2024 county-years (MAE 15.85->12.49, 2022 29.3->11.9),
  //    HELD OUT on 2025 (trend 165.6 -> 138, realized 140). See c:/tmp/bucket_*.mjs
  //    + project_yield_water_bucket memory. K_DRY is the one fitted knob.
  //    2026-06-18: re-validated against the now-published 2025 county yields as a true
  //    county-level OOS test — bucket cut 2025 error 27.9->11.1 bu MAE (14 WNY counties);
  //    leave-one-year-out CV over 2019-2025 confirms K_DRY=0.006-0.007 optimal. Held.
  BKT_PFRAC: 0.5,        // readily-available water fraction (FAO-56 p for corn) before stress starts
  BKT_START_FRAC: 0.95,  // soil-water at planting as frac of capacity (wet NY spring)
  BKT_DEEP_FRAC: 0.85,   // moisture frac of newly root-accessed deeper soil (wet profile)
  BKT_K_DRY: 0.006,      // yield drag per unit (stage-weighted) dry-stress index — THE fitted knob
  // ── WET ARM REBUILT 2026-08-08. The old arm was UNREACHABLE in the window that
  //    decides yield, so heavy rain could only ever help. It fired on
  //    (exc > 0.4in AND ETdemand < 0.12 in/day) — but in silk/fill Kc=1.15 and NY
  //    July ET0 is 0.17-0.25, so demand is 0.20-0.29 and the ET gate could never
  //    pass. Separately `exc` was discarded the same day, so a drawn-down profile
  //    absorbed a 3.6in downpour with exc=0. Audited over 16 counties x 2021-26:
  //    of 3,096 heavy (>=1in) days landing in silk/fill the term fired 101 times
  //    (3.3%), max drag ~0.2% of yield. Orleans CY2026 — hammered, 5-10in in spots —
  //    scored 1.116 with 6/10 cells PEGGED AT THE CEILING, its best moisture reading
  //    in six years and the LOWEST wet index of all 16 counties. That is the bug.
  //    Two changes, both standard soil-water accounting:
  //      • water above field capacity now accumulates in a SATURATION STORE and
  //        drains at BKT_DRAIN in/day instead of vanishing, so one big rain leaves
  //        the profile saturated for SEVERAL days;
  //      • a day counts as waterlogged on DURATION (satExc > BKT_SAT_THRESH), with
  //        no ET gate — waterlogging is anaerobic root days, not a same-day
  //        evaporation coincidence — so it can finally fire at silking.
  //    An infiltration cap was also tested and REJECTED: it never improved LOYO MAE
  //    (best config had it disabled), and unvalidated physics is what caused this.
  //    REFIT: leave-one-year-out CV, 2019-2025, 90 WNY county-years, per-county
  //    recentering (mirrors act.dry-nrm.dry). MAE 11.64 -> 10.96, bias +2.31 -> +2.04.
  //    Every wet/normal year improved (2021 8.7->6.1, 2024 7.8->6.7, 2023 11.8->10.9)
  //    and BOTH drought years were untouched (2022 15.0->15.1, 2025 10.8->10.9) —
  //    the dry calibration is preserved, which is what makes this safe to ship.
  //    K_WET has a genuine interior optimum (MAE 11.68 at 0 -> 11.05 at .028 -> 11.17
  //    at .040), so it is signal, not noise. See c:/tmp/wetarm_fit.mjs.
  BKT_K_WET: 0.028,      // yield drag per unit waterlog index (was 0.004 against a dead arm)
  BKT_DRAIN: 0.5,        // gravitational drainage out of the saturated store, in/day
  BKT_SAT_THRESH: 0.10,  // satExc (in) above field capacity that makes a day waterlogged
  BKT_FLOOR: 0.70,
  // Moisture-upside cap. Was 1.05 (+5%), which artificially clipped favorable years:
  // backtest (2026-06-18, c:/tmp/upside_arm.mjs) showed the bucket's OWN computed
  // factor wants +8 to +11% in good moisture years (2021 all 16 counties up to 1.114,
  // 2024/2023 6/16 & 5/16) — and validation vs RMA finals confirmed banner years are
  // under-predicted. Raised to 1.12 (≈ the bucket's physical max) so a great-moisture
  // year isn't clipped. Crash years (2022/2025 factor 0.86-0.92) are nowhere near the
  // ceiling, so verified ZERO effect on downside accuracy. Asymmetric vs FLOOR by
  // design — drought devastates, surplus only modestly helps. Temperature upside is
  // separate (cohort factor, WEATHER_CEIL=1.20).
  // 2026-08-08: trimmed 1.12 -> 1.10. The 1.12 premise ("good moisture years are
  // being clipped") was formed while the wet arm was DEAD, so every wet year read as
  // a good year and the ceiling absorbed the difference. Re-swept under the rebuilt
  // arm: MAE 11.06 / 10.96 / 11.05 at CEIL 1.08 / 1.10 / 1.12, and bias falls
  // monotonically as the cap comes down (+2.36 at 1.12 -> +2.04 at 1.10 -> +1.41 at
  // 1.08). 1.10 is the MAE optimum, so this is a data-chosen trim, not a haircut.
  BKT_CEIL: 1.10,
  // Trend-extrapolation damping for the yield baseline (see miBaseline). The raw
  // recent-anchored OLS slope systematically OVER-projects: backtested on the full
  // NY county corn history (47 counties, 209 county-years 2015-2025) the undamped
  // baseline carries a +5.3 bu over-prediction bias, worst in down years (2022,
  // 2025) it most needs to get right. Halving the slope cuts that bias to +2.8
  // baseline-only, and combined with the bucket (joint LOYO-CV re-tune 2026-06-18)
  // drives the model's net bias from +2.2 to ~0 at the same MAE (11.9), K_DRY
  // unchanged at 0.006. Genetic-gain upside is retained at half weight; exceptional
  // weather upside still rides on top via the bucket (≤ BKT_CEIL). 1.0 = legacy
  // undamped behavior. See c:/tmp/bucket_validate.mjs + baseline_fullnass.mjs.
  TREND_DAMP: 0.5,
  // Silage-yield weather sensitivity (vs grain). Corn SILAGE is whole-plant biomass
  // and is materially MORE weather-robust than grain bushels — grain takes the brunt
  // of pollination/grain-fill/dry-down stress, while the stalk-and-leaf tonnage holds
  // up (silage year-to-year CV ≈ 0.10-0.15 vs grain 0.15-0.25, ~half as variable). The
  // production model previously applied the FULL grain weather factor to silage yield,
  // which over-stated the silage shortfall in stress years — especially WET/cool years
  // where grain struggles but silage is fine — and that inflated the silage acres the
  // dairies "need", over-diverting grain acres and overstating the production hit. Damp
  // silage's weather reaction to ~half of grain's: silageFactor = 1 + DAMP×(grain−1).
  // 1.0 = legacy (silage tracks grain exactly); 0 = silage yield is weather-flat.
  SILAGE_WX_DAMP: 0.5,
};
// ── THE RAIN RESPONSE CURVE: what we know, and what we do not ────────────────
//
// The honest state of this, because it keeps getting re-derived wrong.
//
// WHAT THE DATA SHOWS. Response = actual yield / RMA-expected anchor (dividing by
// the anchor removes county level, soil and trend), against May-Aug precip, over
// 144 WNY county-years. Binned, assuming NO shape:
//
//     <280mm  0.931      330-380  1.029      430-480  1.064
//   280-330   0.975      380-430  1.032      480+     1.121
//
// So the DRY side is real and well identified: below ~330 mm yield starts to
// fall, and below 280 mm it is off ~7%. The good range runs from ~380 mm up.
//
// WHAT WE CANNOT SAY: where "too much" begins. The wettest bin is the BEST bin,
// and it is still climbing at the edge of the data. A quadratic reports a concave
// optimum at ~660 mm, but the observed range only reaches 587 mm — so that peak
// sits OUTSIDE the data and is an artifact of forcing a parabola through monotone
// points, not a measured turning point. Western NY has simply never been wet
// enough for its own record to contain the far side of the curve.
//
// WHAT WAS TRIED AND REMOVED. A hand-set taper (Z0/Z1/Z2 on the z-score, upside
// scaled toward zero past +2 sigma, penalty toward Li et al.'s -17%). It "worked"
// — Orleans 199.2 -> 193.0 — but it was an assertion wearing a constant's
// clothes: it suppressed a number the data does not say is wrong, and it would
// equally suppress a genuinely excellent season. Removed the same day. If a year
// is real, the model must be allowed to print it.
//
// HOW TO ACTUALLY FIX IT: fit the curve on a sample that CONTAINS the wet tail.
// rma_county_yields already holds 229 counties x 2017-2026 (OH 88, PA 66, NY 52,
// MD 23; 2,061 final yields) — Ohio and Maryland run much wetter summers than WNY
// and Ohio carries the poorly drained soils Li et al. found to be the vulnerable
// ones. The missing piece is May-Aug precip for those counties (gridMET by county
// centroid). Fit it there and the optimum becomes measured rather than asserted.
// Until then `wet_z` is REPORTED and changes nothing.
// Harnesses: c:/tmp/raincurve.mjs (this fit), wetacres.mjs, wetcurve.mjs.
const MI_WET = { Z0: 2.0, Z1: 3.0, Z2: 4.0, LMAX: 0.17 };   // retained for reference; NOT applied
// May-Aug precip, corn-acre weighted, from 11 yrs of gridMET (2015-2025), mm.
// Small-sample sigma — Li et al. used 1981-2016 — so treat z as indicative, which
// is all it is used for: deciding when we have left the observed range.
const MI_WET_CLIMO = {
  ALLEGANY: { m: 380, s: 74 }, CATTARAUGUS: { m: 430, s: 69 }, CAYUGA: { m: 394, s: 77 },
  CHAUTAUQUA: { m: 427, s: 51 }, ERIE: { m: 399, s: 52 }, GENESEE: { m: 358, s: 73 },
  LIVINGSTON: { m: 362, s: 80 }, MONROE: { m: 339, s: 72 }, NIAGARA: { m: 329, s: 76 },
  ONTARIO: { m: 368, s: 72 }, ORLEANS: { m: 326, s: 73 }, SENECA: { m: 386, s: 81 },
  STEUBEN: { m: 367, s: 87 }, WAYNE: { m: 371, s: 69 }, WYOMING: { m: 380, s: 75 },
  YATES: { m: 367, s: 79 },
};
// scale on the UPSIDE (1 = keep it all, 0 = none) and an absolute penalty beyond Z1.
function miWetTurndown(z) {
  if (z == null || !isFinite(z)) return { scale: 1, penalty: 0, z: null };
  const scale = z <= MI_WET.Z0 ? 1 : z >= MI_WET.Z1 ? 0 : 1 - (z - MI_WET.Z0) / (MI_WET.Z1 - MI_WET.Z0);
  const penalty = z <= MI_WET.Z1 ? 0 : MI_WET.LMAX * Math.min(1, (z - MI_WET.Z1) / (MI_WET.Z2 - MI_WET.Z1));
  return { scale: miR3(scale), penalty: miR3(penalty), z: miR3(z) };
}
// Per-county May-Aug precip z. Observed where we have it, climatology for days
// not yet run, corn-acre weighted — the same construction the fit used.
function miWetZByCounty(gridCells, gridDaily, gridClimo, year) {
  if (!gridCells || !gridCells.length || !gridClimo || !gridClimo.length) return {};
  const climo = new Map();
  for (const c of gridClimo) if (c.precip_mm != null) climo.set(`${c.cell_id}|${c.day_of_year}`, Number(c.precip_mm));
  const obs = new Map();
  for (const d of (gridDaily || [])) if (d.precip_mm != null && !d.is_forecast) obs.set(`${d.cell_id}|${d.obs_date}`, Number(d.precip_mm));
  const pad = (n) => String(n).padStart(2, "0");
  const acc = {};
  for (const cell of gridCells) {
    const w = cell.corn_acres != null ? Number(cell.corn_acres) : 0;
    if (!(w > 0)) continue;
    let tot = 0, have = false;
    for (let mo = 5; mo <= 8; mo++) {
      for (let d = 1; d <= 31; d++) {
        const dt = new Date(Date.UTC(year, mo - 1, d));
        if (dt.getUTCMonth() + 1 !== mo) continue;
        const doy = Math.floor((dt - Date.UTC(year, 0, 1)) / 86400000) + 1;
        const cl = climo.get(`${cell.id}|${doy}`);
        if (cl == null) continue;
        have = true;
        const o = obs.get(`${cell.id}|${year}-${pad(mo)}-${pad(d)}`);
        tot += o != null ? o : cl;
      }
    }
    if (!have) continue;
    const a = acc[cell.county_fips] || (acc[cell.county_fips] = { t: 0, w: 0 });
    a.t += tot * w; a.w += w;
  }
  const out = {};
  for (const [fips, a] of Object.entries(acc)) {
    if (!(a.w > 0)) continue;
    const meta = MI_MODEL_COUNTIES.find((m) => m.fips === String(fips));
    const cl = meta && MI_WET_CLIMO[meta.name];
    if (!cl || !(cl.s > 0)) continue;
    out[fips] = (a.t / a.w - cl.m) / cl.s;
  }
  return out;
}

const MI_SEASON = { start: "04-20", end: "09-30" };
const MI_DISCLAIMER = "Planning estimate only. Weather/pace effects use pooled agronomic heuristics (county yield history is too short to fit a statistical model). Not an official forecast — verify against scouting before acting.";
const miClamp = (x, lo, hi) => Math.max(lo, Math.min(hi, x));
const miR1 = (n) => (n == null || isNaN(n) ? null : Number(Number(n).toFixed(1)));
const miR3 = (n) => (n == null || isNaN(n) ? null : Number(Number(n).toFixed(3)));

// Corn planting-date yield-potential curve (NY/Northeast). Sourced:
//  • Cornell CALS field crops — late Apr–early May optimal; loss ramps from
//    ~0.3%/day early May to ~1%/day by end of May, accelerating into June.
//  • Penn State Agronomy Guide — Apr 30 = 100%, May 29 ≈ 81%.
//  • Purdue/U. Illinois (Irwin) — ~May 20 inflection, steepening through June.
// Tunable. Returns the fraction of max yield potential (floor..1.00) for corn
// physically planted on `dt`.
const MI_CORN_PLANTING = {
  optimalEndMonth: 6, optimalEndDay: 1,        // ≤ this date → 100% potential (NY-specific: short-RM hybrids, May plantings often sit in cold soil — Kevin's published curve)
  segments: [                                  // escalating daily loss after the window
    { m: 6, d: 10, rate: 0.005 },              // Jun 1–10 (through FPD)  0.5%/day
  ],
  tailRate: 0.010,                             // post-FPD  1.0%/day
  floor: 0.50,                                 // …but never below this (PP territory)
  projectionDays: 18,                          // # of WORKABLE days at PEAK effort to plant the unplanted book. Calibrated against historical PP rates: normal years 1-3% PP, wet years 5-10%, 2019-style very-wet 10-15%. 14d gave 0% PP (too aggressive), 30d gave 21% (too pessimistic). 18d balances: peak daily ~unplanted/18 leaves enough capacity that most acres fit in the willingness window, but late-emerging slow workdays + the steep willingness drop after Jun 10 still produce a realistic PP tail.
  rainBlockIn: 0.3,                            // a day with ≥ this much forecast rain is not field-workable
  soakIn: 0.5,                                 // a soaker ≥ this triggers a drydown
  drydownDays: 2,                              // no planting for this many days after a soaker
  maxHorizonDays: 90,                          // extended to early July to cover silage willingness tail
  silageShare: 0.35,                           // WNY silage corn ≈ 35% of total corn acres (rough — refined per year via cow demand model)
};
// Uncertainty model — the headline yield is a POINT estimate, but the range
// of plausible outcomes shrinks daily as the season unfolds and we replace
// projections with observations. Each model component carries its own
// uncertainty that itself depends on how much evidence is in:
//
//   σ_baseline:   trend baseline. Backtest MAE ≈ 14.5 bu/ac — fixed, doesn't
//                 shrink (it's the irreducible county-year noise).
//   σ_pace:       planting projection. Scales with unplanted fraction —
//                 0% unplanted = locked in, 100% = max uncertainty (~6 bu/ac).
//   σ_early:      early-season stress. Scales with how much of May-June
//                 weather is still ahead.
//   σ_silking:    silking + grain-fill weather — the BIG one. ~15-18 bu/ac
//                 when fully climo-filled (today). Drops to ~3 by harvest as
//                 cohort silk_evidence climbs.
//
// Total σ = sqrt(σ_baseline² + σ_pace² + σ_early² + σ_silking²)  (independence)
// Display: yield ± 1.5σ ≈ 87% confidence band.
//
// Phase + roadmap:
//   PLANTING (Apr-Jun 15): pace + early-stress unknown; silking unknown.
//                          Next sharpening: planting completion ~Jun 15-20.
//   GROWING (Jun 16-Jul 20): pace locked. Silking weather still unknown.
//                            Next sharpening: silking weather window ~Jul 25-Aug 10.
//   SILKING (Jul 20-Aug 15): silking weather observed in real-time.
//                            Next sharpening: grain fill ~Aug 20-Sep 10.
//   GRAIN FILL (Aug 15-Sep 20): late weather settles the call.
//                                Next sharpening: harvest ~Sep 25-Oct 30.
//   HARVEST: nearly known.
function miUncertainty(year, surveyPace, earlyStress, cohortSummary, backtestMae) {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const seasonStart = new Date(year, 3, 1);    // April 1
  const seasonEnd = new Date(year, 9, 31);     // October 31
  const SEASON_LEN_DAYS = Math.round((seasonEnd - seasonStart) / 86400000);   // ~213
  const dayOfSeason = Math.max(0, Math.min(SEASON_LEN_DAYS, Math.round((now - seasonStart) / 86400000)));
  const seasonElapsedPct = Math.round((dayOfSeason / SEASON_LEN_DAYS) * 100);
  // ── σ contributors (still used for the ±band) ─────────────────────
  const sBase = backtestMae != null && backtestMae > 0 ? Math.min(15, backtestMae) : 14;
  const unplantedFrac = surveyPace && surveyPace.intendedAc > 0
    ? Math.max(0, 1 - (surveyPace.plantedByFpdFrac || 1))
    : 0.5;
  const sPace = 6 * unplantedFrac;
  const planting_end_doy = Math.round((new Date(year, 5, 20) - seasonStart) / 86400000);
  const earlyDone = Math.min(1, dayOfSeason / Math.max(1, planting_end_doy));
  const sEarly = 3 * (1 - earlyDone);
  let silkEvidence = 0;
  if (cohortSummary && cohortSummary.weekly && cohortSummary.weekly.length) {
    const evs = cohortSummary.weekly.map((w) => w.silk_evidence || 0);
    silkEvidence = evs.reduce((a, b) => a + b, 0) / evs.length;
  }
  const sSilk = 18 * (1 - silkEvidence);
  const sTotal = Math.sqrt(sBase * sBase + sPace * sPace + sEarly * sEarly + sSilk * sSilk);
  // ── INFORMATION CAPTURED — the meaningful "confidence" metric ─────
  // What % of season information is OBSERVED vs still projected?
  // Weight by how much each driver actually determines final yield:
  //   pace + planting outcomes:   25%  (locked by ~Jun 20)
  //   early-stress weather:       15%  (locked by ~Jun 20)
  //   silking weather:            45%  (the biggest unknown today)
  //   grain-fill weather:         15%  (locked by ~Sep 10)
  // Goes from ~0% in early April to ~95% by harvest (the last 5% is
  // irreducible county-year noise — backtest MAE).
  const paceInfo = surveyPace?.plantedByFpdFrac != null ? Math.min(1, (1 - unplantedFrac)) : 0;
  const earlyInfo = earlyDone;     // 0 → 1 across Apr 1 → Jun 20
  const silkInfo = silkEvidence;   // 0 → 1 as cohort silking observed
  // Grain fill: linear ramp from Jul 20 (R2) to Sep 10 (R6)
  const fillStart = Math.round((new Date(year, 6, 20) - seasonStart) / 86400000);
  const fillEnd = Math.round((new Date(year, 8, 10) - seasonStart) / 86400000);
  const fillInfo = Math.max(0, Math.min(1, (dayOfSeason - fillStart) / Math.max(1, fillEnd - fillStart)));
  const infoCaptured = 0.25 * paceInfo + 0.15 * earlyInfo + 0.45 * silkInfo + 0.15 * fillInfo;
  // Confidence = info captured × 0.95 (leaves a 5% irreducible noise floor)
  const confidence = Math.min(0.95, infoCaptured * 0.95);
  // Current phase + next sharpening event with expected confidence at that point
  let phase, daysToNextSharpening, nextSharpeningLabel, nextSharpeningConfPct;
  const jun15 = new Date(year, 5, 15), jul20 = new Date(year, 6, 20), aug15 = new Date(year, 7, 15), sep25 = new Date(year, 8, 25);
  if (now < jun15)      { phase = "Planting";    daysToNextSharpening = Math.round((jun15 - now) / 86400000); nextSharpeningLabel = "Planting locks in (~Jun 15)"; nextSharpeningConfPct = 38; }
  else if (now < jul20) { phase = "Growing";     daysToNextSharpening = Math.round((jul20 - now) / 86400000); nextSharpeningLabel = "Silking window opens (~Jul 20)"; nextSharpeningConfPct = 55; }
  else if (now < aug15) { phase = "Silking";     daysToNextSharpening = Math.round((aug15 - now) / 86400000); nextSharpeningLabel = "Silking observed (~Aug 15)"; nextSharpeningConfPct = 80; }
  else if (now < sep25) { phase = "Grain fill";  daysToNextSharpening = Math.round((sep25 - now) / 86400000); nextSharpeningLabel = "Grain fill complete (~Sep 25)"; nextSharpeningConfPct = 92; }
  else                  { phase = "Harvest";     daysToNextSharpening = 0; nextSharpeningLabel = "Final NASS reads"; nextSharpeningConfPct = 95; }
  return {
    sigma_total: Math.round(sTotal * 10) / 10,
    sigma_baseline: Math.round(sBase * 10) / 10,
    sigma_pace: Math.round(sPace * 10) / 10,
    sigma_early: Math.round(sEarly * 10) / 10,
    sigma_silking: Math.round(sSilk * 10) / 10,
    confidence_pct: Math.round(confidence * 100),
    pace_info_pct: Math.round(paceInfo * 100),
    early_info_pct: Math.round(earlyInfo * 100),
    silk_info_pct: Math.round(silkInfo * 100),
    fill_info_pct: Math.round(fillInfo * 100),
    phase, days_to_next_sharpening: daysToNextSharpening, next_sharpening: nextSharpeningLabel,
    next_sharpening_conf_pct: nextSharpeningConfPct,
    day_of_season: dayOfSeason, season_len_days: SEASON_LEN_DAYS, season_elapsed_pct: seasonElapsedPct,
  };
}
// Continuous workability score (0-1). Replaces the old binary "rain=0, dry=1"
// check. Captures the reality that even a dry day can't dry out fully-
// saturated soils, and saturated soils prevent planting even with willing
// farmers. Three multiplicative factors:
//   (a) today_factor: this day's own rain forecast — 0 if ≥ rainBlockIn (0.3")
//   (b) drydown_factor: scales 0-1 based on accumulated recent precip (last
//       5 days). Heavy recent rain = mud = low workability even on dry day.
//   (c) soil_ceiling: NASS state-level topsoil %surplus caps daily capacity.
//       44% surplus = mud, you're at best 50% effective. 70% surplus = 30%.
// Returns 0-1, with 0 meaning "not field-workable at all" and 1 meaning
// "full peak-rate planting possible."
function miDailyWorkability(date, fcPrecip, soilSurplusPct, opts) {
  const o = opts || {};
  const rainBlock = o.rainBlockIn != null ? o.rainBlockIn : 0.3;
  const soak = o.soakIn != null ? o.soakIn : 0.5;
  const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  // (a) Today's rain — hard block
  const todayP = fcPrecip ? fcPrecip[ymd(date)] : null;
  if (todayP != null && todayP >= rainBlock) return 0;
  // (b) Drydown — recent 5-day cumulative precip
  let recent = 0;
  if (fcPrecip) {
    for (let j = 1; j <= 5; j++) {
      const pd = new Date(date); pd.setDate(pd.getDate() - j);
      const p = fcPrecip[ymd(pd)];
      if (p != null) recent += p;
    }
  }
  let drydownF = 1.0;
  if (recent >= 1.5)      drydownF = 0.0;    // saturated from heavy week — no go
  else if (recent >= 1.0) drydownF = 0.3;    // very wet — marginal
  else if (recent >= 0.7) drydownF = 0.6;    // moist but workable
  else if (recent >= 0.4) drydownF = 0.85;   // slightly wet
  // (c) Soil moisture ceiling — NASS state-level %surplus
  let soilF = 1.0;
  if (soilSurplusPct != null) {
    if (soilSurplusPct >= 60)      soilF = 0.3;
    else if (soilSurplusPct >= 45) soilF = 0.5;
    else if (soilSurplusPct >= 30) soilF = 0.75;
    else if (soilSurplusPct >= 15) soilF = 0.9;
  }
  return Math.max(0, Math.min(1, drydownF * soilF));
}

// Planting willingness curve — Kevin's spec, separate for grain vs silage.
// Step function on date ranges. Captures the dynamic that BOTH grain and
// silage take the same yield hit from late planting, but their WILLINGNESS
// to keep planting after FPD is very different:
//   GRAIN: stops fast — most operators walk to PP by mid-June.
//   SILAGE: dairies keep planting because they need feed for cows.
// Returns the fraction (0-1) of remaining unplanted acres of that crop
// type that will actually get planted on a given calendar day. The
// complement (1 - willingness) goes to PP / abandoned.
function miPlantingWillingness(dt, cropType) {
  const m = dt.getMonth() + 1, d = dt.getDate();
  const ymd_num = m * 100 + d;       // packed MMDD for clean range checks
  if (cropType === "silage") {
    if (ymd_num <= 610)         return 1.00;   // through June 10
    if (ymd_num <= 620)         return 0.90;   // Jun 10-20
    if (ymd_num <= 625)         return 0.25;   // Jun 20-25
    if (ymd_num <= 630)         return 0.10;   // Jun 25-30
    if (ymd_num <= 705)         return 0.05;   // Jul 1-5
    return 0;
  }
  // Grain: stays high through the FPD, then tapers across the late-planting
  // window. NY farmers actively plant corn (shorter-RM hybrids) right through
  // the third week of June in a catch-up year, so willingness is still
  // meaningful Jun 10-20; it's only AFTER Jun 20 that it goes very low (Kevin
  // 2026-05-30). The old curve collapsed to 0.10 right after Jun 10, which
  // mislabeled corn-that-gets-planted-late as prevent-plant. Late-planted acres
  // COUNT — their lower yield is carried by miCornPotential/paceFactor, not by
  // dropping them from the acre base.
  if (ymd_num <= 605)           return 1.00;   // through June 5
  if (ymd_num <= 610)           return 0.85;   // Jun 5-10 (push to beat FPD)
  if (ymd_num <= 615)           return 0.50;   // Jun 10-15 (late period — still going hard)
  if (ymd_num <= 620)           return 0.25;   // Jun 15-20 (tapering, but planting)
  if (ymd_num <= 625)           return 0.05;   // Jun 20-25 (nearly done — very low after Jun 20)
  return 0;                                    // after Jun 25
}
function miCornPotential(dt, pcfg) {
  const cfg = pcfg || MI_CORN_PLANTING;
  const y = dt.getFullYear();
  const optEnd = new Date(y, cfg.optimalEndMonth - 1, cfg.optimalEndDay);
  if (dt <= optEnd) return 1;
  const segEnds = cfg.segments.map((s) => ({ end: new Date(y, s.m - 1, s.d), rate: s.rate }));
  let loss = 0;
  const cur = new Date(optEnd); cur.setDate(cur.getDate() + 1);
  while (cur <= dt) {
    let rate = cfg.tailRate;
    for (const s of segEnds) { if (cur <= s.end) { rate = s.rate; break; } }
    loss += rate;
    if (1 - loss <= cfg.floor) return cfg.floor;
    cur.setDate(cur.getDate() + 1);
  }
  return Math.max(cfg.floor, 1 - loss);
}

// ──────────────────────────────────────────────────────────────────────────
// Corn mid-week nowcast
// ──────────────────────────────────────────────────────────────────────────
// Bridges the gap between Monday survey updates: takes the most recent
// survey observation (book acres planted to date) and projects today's
// implied acres planted using the same workability + willingness model
// the MI yield page uses. Each new Monday survey "trues up" the curve.
// Inputs:
//   surveyTimeSeries — [{date: 'M/D', pct, totalPlanted}] sorted asc
//   intendedAc       — sum of book intended corn acres
//   wxRows           — weather_county_daily rows (observed + 14d forecast)
//                      across WNY counties for current year
//   soilSurplusPct   — latest NASS NY TOPSOIL_PCT_SURPLUS (0-100), or null
//   asOfDate         — "today" (Date)
//   horizonDays      — how many days forward of "today" to project
// Returns null if there's nothing to nowcast (no observations, all done,
// or no weather data); otherwise an object with daily cumulative values
// and a summary card payload.
function computeCornMidWeekNowcast({ surveyTimeSeries, intendedAc, wxRows, soilSurplusPct, asOfDate, horizonDays = 7 }) {
  if (!surveyTimeSeries || surveyTimeSeries.length === 0) return null;
  if (!intendedAc || intendedAc <= 0) return null;
  if (!wxRows || wxRows.length === 0) return null;

  // Latest survey observation = anchor
  const lastObs = surveyTimeSeries[surveyTimeSeries.length - 1];
  const lastDate = new Date(`${asOfDate.getFullYear()}/${lastObs.date}`);
  lastDate.setHours(23, 59, 59, 999);   // end-of-day so we walk forward from next morning
  const lastPlanted = Number(lastObs.totalPlanted) || 0;
  const unplanted = Math.max(0, intendedAc - lastPlanted);
  if (unplanted <= 0) return null;     // already done — nothing to project

  // Build WNY-avg daily precip lookup
  const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  const acc = {};
  for (const w of wxRows) {
    if (w.precip_in == null) continue;
    const r = acc[w.obs_date] || (acc[w.obs_date] = { s: 0, n: 0, fc: false });
    r.s += Number(w.precip_in);
    r.n++;
    if (w.is_forecast) r.fc = true;
  }
  const fcPrecip = {};
  const isForecastDay = {};
  Object.keys(acc).forEach((d) => { fcPrecip[d] = acc[d].s / acc[d].n; isForecastDay[d] = acc[d].fc; });

  // Constants — same as the MI projection loop
  const PC = MI_CORN_PLANTING;
  const peakDays = PC.projectionDays || 18;
  const SILAGE_SHARE = (PC.silageShare != null) ? PC.silageShare : 0.35;
  const rainBlock = PC.rainBlockIn != null ? PC.rainBlockIn : 0.3;
  const soak = PC.soakIn != null ? PC.soakIn : 0.5;

  let remGrain = unplanted * (1 - SILAGE_SHARE);
  let remSilage = unplanted * SILAGE_SHARE;
  const peakGrain = remGrain / peakDays;
  const peakSilage = remSilage / peakDays;

  // Walk day by day starting the day AFTER the anchor observation.
  // Stop at asOfDate + horizonDays (or when remaining hits 0, or maxH).
  const maxH = 60;
  const stopDate = new Date(asOfDate); stopDate.setDate(stopDate.getDate() + horizonDays); stopDate.setHours(23, 59, 59, 999);
  const cur = new Date(lastDate); cur.setDate(cur.getDate() + 1); cur.setHours(0, 0, 0, 0);

  let cumPlanted = lastPlanted;
  let cumModelAdds = 0, cumForecastAdds = 0;
  let daysFull = 0, daysPartial = 0, daysBlocked = 0;
  let daysFullFwd = 0, daysPartialFwd = 0, daysBlockedFwd = 0;   // separate counters for forward-of-today (forecast) window
  const daily = [];
  let k = 0;
  while (cur <= stopDate && k < maxH && (remGrain > 0 || remSilage > 0)) {
    const dkey = ymd(cur);
    const wrk = miDailyWorkability(cur, fcPrecip, soilSurplusPct, { rainBlockIn: rainBlock, soakIn: soak });
    const wG = miPlantingWillingness(cur, "grain");
    const wS = miPlantingWillingness(cur, "silage");
    const todayG = Math.min(remGrain, peakGrain * wG * wrk);
    const todayS = Math.min(remSilage, peakSilage * wS * wrk);
    remGrain -= todayG;
    remSilage -= todayS;
    const addAc = todayG + todayS;
    cumPlanted += addAc;
    const isForecast = cur > asOfDate || isForecastDay[dkey];
    if (isForecast) cumForecastAdds += addAc; else cumModelAdds += addAc;
    if (cur <= asOfDate) {
      if (wrk >= 0.85) daysFull++; else if (wrk > 0.1) daysPartial++; else daysBlocked++;
    } else {
      if (wrk >= 0.85) daysFullFwd++; else if (wrk > 0.1) daysPartialFwd++; else daysBlockedFwd++;
    }
    daily.push({
      date: new Date(cur),
      dateKey: dkey,
      addAc,
      cumPlantedAc: cumPlanted,
      cumPct: intendedAc > 0 ? (cumPlanted / intendedAc) * 100 : null,
      workability: wrk,
      willingnessGrain: wG,
      willingnessSilage: wS,
      precipIn: fcPrecip[dkey] != null ? fcPrecip[dkey] : null,
      isForecast,
    });
    cur.setDate(cur.getDate() + 1);
    k++;
  }

  // Pick the row for "today" (or the latest <= today). When the most
  // recent survey lands on today (e.g. Kevin opens the page Monday after
  // farmers submit), the projection loop starts the day after, so no
  // daily row exists for today — modeled today is the observation itself.
  const todayKey = ymd(asOfDate);
  let todayRow = null;
  const lastObsKey = ymd(lastDate);
  if (lastObsKey === todayKey) {
    // Same-day case — anchor IS today
    todayRow = {
      date: new Date(asOfDate),
      dateKey: todayKey,
      addAc: 0,
      cumPlantedAc: lastPlanted,
      cumPct: lastObs.pct,
      workability: 1,
      willingnessGrain: 1,
      willingnessSilage: 1,
      precipIn: fcPrecip[todayKey] != null ? fcPrecip[todayKey] : null,
      isForecast: false,
    };
  } else {
    for (const row of daily) {
      if (row.dateKey === todayKey) { todayRow = row; break; }
      if (row.date <= asOfDate) todayRow = row;
    }
  }
  // Sum forecast tail (after today) to next Monday
  const nextMonday = new Date(asOfDate);
  const dayOfWeek = nextMonday.getDay();
  const daysToNextMonday = ((1 - dayOfWeek + 7) % 7) || 7;
  nextMonday.setDate(nextMonday.getDate() + daysToNextMonday);
  let nextMondayPct = null, nextMondayAddAc = 0;
  for (const row of daily) {
    if (row.date > asOfDate && row.date <= nextMonday) nextMondayAddAc += row.addAc;
    if (ymd(row.date) === ymd(nextMonday)) nextMondayPct = row.cumPct;
  }

  return {
    lastObsDate: lastDate,
    lastObsPct: lastObs.pct,
    lastObsPlantedAc: lastPlanted,
    intendedAc,
    asOfDate,
    daysSinceAnchor: todayRow ? daily.indexOf(todayRow) + 1 : 0,
    daysFull, daysPartial, daysBlocked,
    daysFullFwd, daysPartialFwd, daysBlockedFwd,
    todayModeledPlantedAc: todayRow ? todayRow.cumPlantedAc : null,
    todayModeledPct: todayRow ? todayRow.cumPct : null,
    modeledAddedSinceAnchorAc: todayRow ? todayRow.cumPlantedAc - lastPlanted : 0,
    modeledAddedSinceAnchorPctPts: todayRow ? todayRow.cumPct - lastObs.pct : 0,
    nextMondayProjectedPct: nextMondayPct,
    nextMondayAddedAc: nextMondayAddAc,
    soilSurplusUsed: soilSurplusPct,
    daily,
  };
}

// ──────────────────────────────────────────────────────────────────────────
// Stale-reporting adjustment for corn book total
// ──────────────────────────────────────────────────────────────────────────
// The naive "sum of each farmer's latest submission" undercounts reality
// whenever a subset of farmers hasn't reported recently. If Farmer A
// reported 100 ac last Monday and Farmer B reported 50 ac today, the
// total = 150 — but A has likely planted more in the meantime.
//
// This function fixes that: for each ACTIVE farmer, take their latest
// submission. If submitted within `freshDaysThreshold` days = use as-is.
// If older = project forward day-by-day using the same workability +
// willingness model that drives the headline nowcast, then add the
// projected progress to their last reported number. Sum across farmers
// for the book-wide "today's best estimate."
function computeCornStaleAdjustedAnchor({ sFarmers, sIntents, sProg, wxRows, soilSurplusPct, asOfDate, freshDaysThreshold = 2 }) {
  if (!sFarmers || !sFarmers.length || !sIntents || !sProg) return null;
  if (!wxRows || !wxRows.length) return null;
  const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  // WNY-avg daily precip lookup (same shape the main nowcast uses)
  const acc = {};
  for (const w of wxRows) {
    if (w.precip_in == null) continue;
    const a = acc[w.obs_date] || (acc[w.obs_date] = { s: 0, n: 0 });
    a.s += Number(w.precip_in); a.n++;
  }
  const fcPrecip = {};
  Object.keys(acc).forEach((d) => { fcPrecip[d] = acc[d].s / acc[d].n; });
  // Per-farmer projection helper — applies the SAME workability +
  // willingness loop the headline nowcast uses, scaled to this farmer's
  // own unplanted acres. Captures real weather-driven dynamics: if it
  // rained all week, the projected add is small.
  const PC = MI_CORN_PLANTING;
  const peakDays = PC.projectionDays || 18;
  const SILAGE_SHARE = (PC.silageShare != null) ? PC.silageShare : 0.35;
  const rainBlock = PC.rainBlockIn != null ? PC.rainBlockIn : 0.3;
  const soak = PC.soakIn != null ? PC.soakIn : 0.5;
  function projectFarmerForward(submitDate, unplanted) {
    if (unplanted <= 0) return 0;
    const days = Math.floor((asOfDate - submitDate) / 86400000);
    if (days <= 0) return 0;
    let remGrain = unplanted * (1 - SILAGE_SHARE);
    let remSilage = unplanted * SILAGE_SHARE;
    const peakGrain = remGrain / peakDays;
    const peakSilage = remSilage / peakDays;
    let cumAdd = 0;
    const cur = new Date(submitDate); cur.setHours(0,0,0,0); cur.setDate(cur.getDate() + 1);
    for (let k = 0; k < days && (remGrain > 0 || remSilage > 0); k++) {
      const wrk = miDailyWorkability(cur, fcPrecip, soilSurplusPct, { rainBlockIn: rainBlock, soakIn: soak });
      const wG = miPlantingWillingness(cur, "grain");
      const wS = miPlantingWillingness(cur, "silage");
      const todayG = Math.min(remGrain, peakGrain * wG * wrk);
      const todayS = Math.min(remSilage, peakSilage * wS * wrk);
      remGrain -= todayG; remSilage -= todayS;
      cumAdd += todayG + todayS;
      cur.setDate(cur.getDate() + 1);
    }
    return cumAdd;
  }
  // Walk each active farmer who has corn intentions
  const activeIds = new Set((sFarmers || []).filter((f) => f.is_active).map((f) => f.id));
  let totalIntended = 0, totalAnchored = 0, totalAdjusted = 0;
  let freshN = 0, staleN = 0, neverN = 0;
  const staleDetail = [];
  for (const fid of activeIds) {
    const farmer = sFarmers.find((f) => f.id === fid);
    const intended = (sIntents || []).filter((i) => i.crop === "Corn" && i.survey_farmer_id === fid)
      .reduce((s, i) => s + (parseFloat(i.intended_acres) || 0), 0);
    if (intended <= 0) continue;
    totalIntended += intended;
    const subs = (sProg || []).filter((p) => p.crop === "Corn" && p.survey_farmer_id === fid)
      .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));
    if (subs.length === 0) {
      neverN++;
      continue;     // never reported — no anchor, can't estimate cleanly
    }
    const latest = subs[0];
    const latestPlanted = parseFloat(latest.acres_planted) || 0;
    totalAnchored += latestPlanted;
    const submitDate = new Date(latest.submitted_at);
    const daysStale = Math.floor((asOfDate - submitDate) / 86400000);
    if (daysStale <= freshDaysThreshold) {
      freshN++;
      totalAdjusted += latestPlanted;
    } else {
      staleN++;
      const unplanted = Math.max(0, intended - latestPlanted);
      const projAdd = projectFarmerForward(submitDate, unplanted);
      totalAdjusted += latestPlanted + projAdd;
      staleDetail.push({
        name: farmer && farmer.display_name ? farmer.display_name : String(fid).slice(0, 8),
        daysStale, lastPlanted: latestPlanted, projAdd, intended,
      });
    }
  }
  staleDetail.sort((a, b) => b.daysStale - a.daysStale);
  return {
    intendedAc: totalIntended,
    anchoredPlantedAc: totalAnchored,
    anchoredPct: totalIntended > 0 ? (totalAnchored / totalIntended) * 100 : 0,
    adjustedPlantedAc: totalAdjusted,
    adjustedPct: totalIntended > 0 ? (totalAdjusted / totalIntended) * 100 : 0,
    deltaAc: totalAdjusted - totalAnchored,
    deltaPctPts: totalIntended > 0 ? ((totalAdjusted - totalAnchored) / totalIntended) * 100 : 0,
    freshN, staleN, neverN,
    staleDetail,
    freshDaysThreshold,
    asOfDate,
  };
}

// Build the corn time series (per submission day cumulative planted acres
// + % of intended) directly from the raw survey rows. Same shape as
// PlantingSurveyScreen's timeSeriesPoints — extracted here so MIYieldView
// can build the same series without dragging that screen's render code in.
// Inputs:
//   sFarmers — planting_survey_farmers rows
//   sIntents — planting_intentions rows
//   sProg    — planting_progress rows
//   year     — crop year
function buildCornSurveyTimeSeries(sFarmers, sIntents, sProg, year) {
  const activeIds = new Set((sFarmers || []).filter((f) => f.is_active).map((f) => f.id));
  let totalIntended = 0;
  (sIntents || []).forEach((i) => {
    if (i.crop === "Corn" && activeIds.has(i.survey_farmer_id)) totalIntended += parseFloat(i.intended_acres) || 0;
  });
  if (totalIntended <= 0) return { timeSeriesPoints: [], totalIntended: 0 };
  const cornProg = (sProg || []).filter((p) => p.crop === "Corn" && activeIds.has(p.survey_farmer_id));
  if (cornProg.length === 0) return { timeSeriesPoints: [], totalIntended };
  // For each unique submission day, build the cumulative book-planted total
  // using the latest submission ≤ that day for each farmer.
  const dayKeys = [...new Set(cornProg.map((p) => new Date(p.submitted_at).toLocaleDateString("en-US", { month: "numeric", day: "numeric" })))]
    .sort((a, b) => new Date(`${year}/${a}`) - new Date(`${year}/${b}`));
  const timeSeriesPoints = dayKeys.map((dateStr) => {
    const dateObj = new Date(`${year}/${dateStr}`);
    dateObj.setHours(23, 59, 59, 999);
    let totalPlanted = 0;
    for (const fid of activeIds) {
      const farmerSubs = cornProg
        .filter((p) => p.survey_farmer_id === fid && new Date(p.submitted_at) <= dateObj)
        .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));
      if (farmerSubs.length > 0) totalPlanted += parseFloat(farmerSubs[0].acres_planted) || 0;
    }
    const pct = totalIntended > 0 ? Math.min(100, Math.round((totalPlanted / totalIntended) * 100)) : 0;
    return { date: dateStr, pct, totalPlanted };
  });
  return { timeSeriesPoints, totalIntended };
}

// ──────────────────────────────────────────────────────────────────────────
// Corn planting nowcast chart — pure SVG string builder
// ──────────────────────────────────────────────────────────────────────────
// One source of truth for the planting-pace chart used by the MI page (in
// React render) AND the MI PDF export (as injected HTML). Pure function
// returning a self-contained <svg> string — no React, no d3 dependency.
// Renders:
//   • survey observations (solid orange line + dots)
//   • weather-driven projection (dotted green line)
//   • shaded uncertainty band (±MAE × sqrt(days_ahead/7))
//   • today vertical reference line
function buildCornNowcastChartSvg({ surveyPoints, nowcast, backtest, year, w = 720, h = 220 }) {
  if (!nowcast || !nowcast.daily || nowcast.daily.length === 0) return null;
  const padL = 38, padR = 16, padT = 22, padB = 32;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  // X domain: from earliest survey (or anchor) → projection end
  const surveyDates = (surveyPoints || []).map((p) => new Date(`${year}/${p.date}`));
  const allDates = [...surveyDates, ...nowcast.daily.map((d) => d.date)];
  let startDate = allDates[0];
  for (const d of allDates) if (d < startDate) startDate = d;
  let endDate = nowcast.daily[nowcast.daily.length - 1].date;
  const span = Math.max(86400000, endDate - startDate);
  const xAt = (d) => padL + Math.max(0, Math.min(1, (d - startDate) / span)) * innerW;
  const yAt = (pct) => padT + innerH - (Math.max(0, Math.min(100, pct)) / 100) * innerH;
  const baseMAE = (backtest && backtest.n >= 3) ? backtest.maePctPts : 5;
  // Build projection line + band paths
  const linePts = [[xAt(nowcast.lastObsDate), yAt(nowcast.lastObsPct)]];
  const upperPts = [[xAt(nowcast.lastObsDate), yAt(nowcast.lastObsPct)]];
  const lowerPts = [[xAt(nowcast.lastObsDate), yAt(nowcast.lastObsPct)]];
  nowcast.daily.forEach((d, idx) => {
    const x = xAt(d.date), y = yAt(d.cumPct);
    const sigma = baseMAE * Math.sqrt((idx + 1) / 7);
    linePts.push([x, y]);
    upperPts.push([x, yAt(Math.min(100, d.cumPct + sigma))]);
    lowerPts.push([x, yAt(Math.max(0, d.cumPct - sigma))]);
  });
  const bandPath = [
    ...upperPts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`),
    ...lowerPts.slice().reverse().map(([x, y]) => `L${x.toFixed(1)},${y.toFixed(1)}`),
    'Z',
  ].join(' ');
  const projPath = linePts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  // Survey observation line
  const surveyPts = (surveyPoints || []).map((p) => ({ x: xAt(new Date(`${year}/${p.date}`)), y: yAt(p.pct), pct: p.pct, label: p.date }));
  const surveyPath = surveyPts.length >= 2
    ? surveyPts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
    : null;
  // Today vertical line
  const xToday = xAt(nowcast.asOfDate);
  // X-axis weekly tick labels
  const ticks = [];
  const t = new Date(startDate); t.setHours(0,0,0,0);
  // Snap to a Monday
  const dow = t.getDay(); if (dow !== 1) t.setDate(t.getDate() + ((1 - dow + 7) % 7));
  while (t <= endDate) { ticks.push(new Date(t)); t.setDate(t.getDate() + 7); }
  const gridLines = [0, 25, 50, 75, 100].map((p) =>
    `<line x1="${padL}" x2="${padL + innerW}" y1="${yAt(p).toFixed(1)}" y2="${yAt(p).toFixed(1)}" stroke="#d6e8d6" stroke-width="1"/>` +
    `<text x="${padL - 5}" y="${(yAt(p) + 3).toFixed(1)}" font-size="9.5" fill="#5a7a5a" text-anchor="end" font-family="'Plus Jakarta Sans',sans-serif">${p}%</text>`
  ).join('');
  const xLabels = ticks.map((tk) =>
    `<text x="${xAt(tk).toFixed(1)}" y="${(h - padB + 14).toFixed(1)}" font-size="9.5" fill="#5a7a5a" text-anchor="middle" font-family="'Plus Jakarta Sans',sans-serif">${tk.getMonth()+1}/${tk.getDate()}</text>`
  ).join('');
  const surveyDots = surveyPts.map((p) =>
    `<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="3.5" fill="#f59e0b" stroke="#fff" stroke-width="1.5"><title>${p.label}: ${p.pct}%</title></circle>`
  ).join('');
  const todayPctRounded = nowcast.todayModeledPct != null ? Math.round(nowcast.todayModeledPct) : null;
  return `<svg viewBox="0 0 ${w} ${h}" width="100%" preserveAspectRatio="xMidYMid meet" style="display:block;max-width:100%;background:#fff;border-radius:6px;font-family:'Plus Jakarta Sans',sans-serif;">
    ${gridLines}
    ${xLabels}
    <line x1="${xToday.toFixed(1)}" x2="${xToday.toFixed(1)}" y1="${padT}" y2="${(padT + innerH).toFixed(1)}" stroke="#9ca3af" stroke-width="1" stroke-dasharray="2,3"/>
    <text x="${xToday.toFixed(1)}" y="${(padT - 6).toFixed(1)}" font-size="9.5" fill="#6b7c6b" text-anchor="middle">today</text>
    <path d="${bandPath}" fill="#10b981" fill-opacity="0.14" stroke="none"/>
    <path d="${projPath}" fill="none" stroke="#10b981" stroke-width="2.2" stroke-dasharray="4,3" stroke-linecap="round"/>
    ${surveyPath ? `<path d="${surveyPath}" fill="none" stroke="#f59e0b" stroke-width="2.5" stroke-linecap="round"/>` : ''}
    ${surveyDots}
    <circle cx="${xAt(nowcast.lastObsDate).toFixed(1)}" cy="${yAt(nowcast.lastObsPct).toFixed(1)}" r="5" fill="#d97706" stroke="#fff" stroke-width="2"/>
    ${todayPctRounded != null ? `<circle cx="${xToday.toFixed(1)}" cy="${yAt(nowcast.todayModeledPct).toFixed(1)}" r="5" fill="#10b981" stroke="#fff" stroke-width="2"/>` : ''}
  </svg>`;
}

// ──────────────────────────────────────────────────────────────────────────
// Corn nowcast backtest against survey history
// ──────────────────────────────────────────────────────────────────────────
// For each consecutive pair of survey observations (Monday A → Monday B),
// runs the same nowcast model forward from A using the actual weather
// between A and B, and compares the modeled B against the observed B.
// Builds the model's own track record on Kevin's book. Different from
// fitting to NY-state NASS history (deferred) — this validates against
// the exact thing the survey measures.
// Returns null if there aren't at least 2 observations to compare.
function backtestCornNowcastVsSurvey({ surveyTimeSeries, intendedAc, wxRows, soilSurplusPct }) {
  if (!surveyTimeSeries || surveyTimeSeries.length < 2) return null;
  if (!intendedAc || intendedAc <= 0) return null;
  if (!wxRows || wxRows.length === 0) return null;
  const errors = [];   // { from, to, predictedPct, actualPct, errPct, daysGap, workableDays }
  for (let i = 0; i < surveyTimeSeries.length - 1; i++) {
    const A = surveyTimeSeries[i];
    const B = surveyTimeSeries[i + 1];
    const dateA = new Date(`${new Date().getFullYear()}/${A.date}`);
    const dateB = new Date(`${new Date().getFullYear()}/${B.date}`);
    const daysGap = Math.round((dateB - dateA) / 86400000);
    if (daysGap <= 0 || daysGap > 21) continue;   // skip same-day or unreasonable gaps
    const nc = computeCornMidWeekNowcast({
      surveyTimeSeries: surveyTimeSeries.slice(0, i + 1),   // simulate "as of A"
      intendedAc,
      wxRows,
      soilSurplusPct,
      asOfDate: dateB,
      horizonDays: 0,
    });
    if (!nc || nc.todayModeledPct == null) continue;
    errors.push({
      from: A.date,
      to: B.date,
      predictedPct: nc.todayModeledPct,
      actualPct: B.pct,
      errPct: nc.todayModeledPct - B.pct,    // positive = model over-predicted
      daysGap,
      workableDays: nc.daysFull + nc.daysPartial * 0.5,
    });
  }
  if (errors.length === 0) return null;
  const absErrs = errors.map((e) => Math.abs(e.errPct));
  const meanAbs = absErrs.reduce((s, v) => s + v, 0) / absErrs.length;
  const bias = errors.reduce((s, e) => s + e.errPct, 0) / errors.length;
  const within3 = errors.filter((e) => Math.abs(e.errPct) <= 3).length;
  const within5 = errors.filter((e) => Math.abs(e.errPct) <= 5).length;
  return {
    n: errors.length,
    maePctPts: meanAbs,
    biasPctPts: bias,
    within3, within5,
    errors,
  };
}

const miMean = (a) => (a.length ? a.reduce((s, v) => s + v, 0) / a.length : null);
function miOls(pts) {
  const n = pts.length; if (n < 2) return null;
  const mx = pts.reduce((s, p) => s + p.x, 0) / n, my = pts.reduce((s, p) => s + p.y, 0) / n;
  let num = 0, den = 0;
  for (const p of pts) { num += (p.x - mx) * (p.y - my); den += (p.x - mx) ** 2; }
  if (den === 0) return null;
  const slope = num / den; return { slope, intercept: my - slope * mx };
}
function miModGdd(tx, tn) {
  if (tx == null || tn == null) return null;
  const a = Math.min(tx, 86), b = Math.max(Math.min(tn, 86), 50);
  return Math.max(0, (a + b) / 2 - 50);
}
// Early-season stress factor — separate from the cohort silking model.
// Reads the weather between season-start (Apr 1) and today and counts
// "stress days" (cold + wet, the cocktail that stunts emergence + delays
// growth) net of "recovery days" (warm + dry, when the crop catches up).
// Each net stress day costs 0.3% off yield. Capped at 6% total — the model
// shouldn't go nuclear off early-season weather alone, because per Kevin:
// "if weather turns around, drag is minimal; if it persists, it compounds."
// The cap reflects the upside potential of a good summer.
//
// Returns { factor, stress_days, recovery_days, net_stress, drag_pct, asof }.
// Today: ~30+ cold/wet days through mid-May → ~3-5% drag. If June turns
// warm/dry: stress days stay constant but recovery days accumulate, net
// drops, factor recovers toward 1.0.
function miEarlyStress(wxRows, year, climoDoy) {
  if (!wxRows || !wxRows.length) return { factor: 1, stress_days: 0, recovery_days: 0, net_stress: 0, drag_pct: 0 };
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  // WNY-avg precip + GDD per day. Observed and forecast are bucketed separately:
  // observed drives realized stress, forecast extends it forward (with confidence
  // decay) so the live model sees what's COMING, not just what already happened.
  const byDate = {}, byDateFc = {};
  for (const w of wxRows) {
    if (Number(w.obs_date.slice(0, 4)) !== year) continue;
    const bucket = w.is_forecast ? byDateFc : byDate;
    const a = bucket[w.obs_date] || (bucket[w.obs_date] = { p: 0, pN: 0, g: 0, gN: 0 });
    if (w.precip_in != null) { a.p += Number(w.precip_in); a.pN++; }
    const g = miModGdd(w.tmax_f, w.tmin_f);
    if (g != null) { a.g += g; a.gN++; }
  }
  // Classify one day vs its normal → {s: stress increment, r: recovery increment}.
  // Shared by the observed and forecast passes so they score identically.
  const classify = (rec, doyK) => {
    if (!rec || !rec.pN) return null;
    const cm = climoDoy[doyK];
    if (!cm) return null;
    const dailyP = rec.p / rec.pN;
    const dailyG = rec.gN ? rec.g / rec.gN : 0;
    const wetExcess = dailyP > cm.precip * 1.5;   // 50%+ over normal precip
    const cold = dailyG < cm.gdd * 0.6;            // <60% normal GDD accumulation
    const warm = dailyG > cm.gdd * 1.1;
    const dry = dailyP < cm.precip * 0.7;
    let s = 0, r = 0;
    if (wetExcess && cold) s = 1;                  // full stress (both bad)
    else if (wetExcess || cold) s = 0.5;           // half stress (one bad)
    if (warm && dry) r = 1;                         // full recovery
    else if (warm || dry) r = 0.4;                 // partial recovery
    return { s, r };
  };
  // Observed pass: April 1 → today (realized, full weight).
  const start = new Date(year, 3, 1);  // April 1
  let stress = 0, recovery = 0;
  for (let d = new Date(start); d < t; d.setDate(d.getDate() + 1)) {
    const k = ymd(d);
    const c = classify(byDate[k], k.slice(5));
    if (c) { stress += c.s; recovery += c.r; }
  }
  // Forecast pass: today → end of the ~16-day outlook, each day discounted by
  // lead-time confidence (exp decay, τ≈11d → lead-1 weight 1.0, ~8d ≈ 0.5,
  // ~16d ≈ 0.25). A warm/dry turn adds recovery (lifts the factor); a cool/wet
  // continuation adds stress. This is what makes the forecast actually move the
  // number — far-out days just move it less.
  const FC_TAU = 11;
  const todayK = ymd(t);
  let fStress = 0, fRecovery = 0, fDays = 0;
  for (const k of Object.keys(byDateFc).filter((kk) => kk >= todayK).sort()) {
    const c = classify(byDateFc[k], k.slice(5));
    if (!c) continue;
    const lead = Math.round((new Date(k + "T00:00:00") - t) / 86400000);
    const cw = Math.exp(-lead / FC_TAU);
    fStress += c.s * cw; fRecovery += c.r * cw; fDays++;
  }
  // Observed-only drag (so the UI can show how much the forecast moved it).
  const obsNet = Math.max(0, stress - recovery * 0.5);
  const obsDrag = Math.min(0.06, obsNet * 0.003);
  // Combined drag = the live model number (observed + forecast).
  const netStress = Math.max(0, (stress + fStress) - (recovery + fRecovery) * 0.5);
  const dragPct = Math.min(0.06, netStress * 0.003);  // 0.3%/net day, 6% cap
  const fOutlook = fDays ? (fRecovery - fStress > 0.3 ? "improving" : fStress - fRecovery > 0.3 ? "worsening" : "steady") : null;
  return {
    factor: 1 - dragPct,
    stress_days: Math.round(stress * 10) / 10,        // observed (realized)
    recovery_days: Math.round(recovery * 10) / 10,    // observed (realized)
    net_stress: Math.round(netStress * 10) / 10,      // combined
    drag_pct: Math.round(dragPct * 1000) / 10,        // combined incl. forecast, % 1dp
    obs_drag_pct: Math.round(obsDrag * 1000) / 10,    // observed-only, for the forecast delta
    forecast_days: fDays,
    forecast_stress: Math.round(fStress * 10) / 10,
    forecast_recovery: Math.round(fRecovery * 10) / 10,
    forecast_outlook: fOutlook,                       // "improving" | "steady" | "worsening" | null
    asof: ymd(t),
  };
}

// Vapor Pressure Deficit (VPD) — FAO-56 daytime approximation.
// Without RH data we use the morning-saturation assumption: dewpoint ≈ Tmin.
// Then VPD = es(Tmax) − es(Tmin) in kPa (good proxy for daily peak VPD).
// Corn silking is sensitive: > 2.0 kPa = real stress, > 2.5 kPa = severe.
function miVpdKpa(tmaxF, tminF) {
  if (tmaxF == null || tminF == null) return null;
  const fToC = (f) => (f - 32) * 5 / 9;
  const es = (tC) => 0.6108 * Math.exp((17.27 * tC) / (tC + 237.3));   // saturation vapor pressure, kPa
  const tmaxC = fToC(tmaxF), tminC = fToC(tminF);
  return Math.max(0, es(tmaxC) - es(tminC));
}
// ── DISEASE PRESSURE (2026-08-08) ─────────────────────────────────────────────
// REPORTS RISK. DOES NOT TOUCH YIELD. Deliberately kept out of the yield model:
// we have no NY county-level disease-loss series to calibrate a penalty against,
// and inventing one is exactly the unvalidated tuning that left the wet arm dead.
// This is a scouting prompt — "go look at these fields" — not a forecast.
//
// A fungal foliar infection needs two things AT ONCE: a temperature the pathogen
// likes, and a wet leaf long enough to infect. Per ~4km cell we have tmax/tmin/
// precip, so leaf wetness is inferred the standard screening way — a day is
// leaf-wet if it rained measurably, OR the diurnal range collapsed (a cloudy,
// humid day, so the overnight dew never burns off).
//
// Bands follow NY/Cornell extension guidance and the Tarspotter lineage:
//   TAR SPOT    mean 59-73°F + leaf wet — cool and humid. The live NY threat:
//               first CY2026 confirmation was Livingston County.
//   GLS / NCLB  tmax 75-90°F + leaf wet — the classic warm dew-period blights.
//   WHITE MOLD  tmax ≤ 85°F + measurable rain — soybean bloom (R1-R3) risk.
//
// The headline score is NOT a weighted blend of invented coefficients. It is the
// PERCENT OF THE LAST `win` OBSERVED DAYS that were favorable for any corn foliar
// disease. "21 of the last 30 days were disease-favorable" is a claim a grower
// can go check against his own fields. Observed days only — no climatology fill,
// no forecast — because a risk map that quietly averages in normal weather stops
// describing what is actually happening out there.
const MI_DISEASE = {
  win: 30,            // look-back, days
  minDays: 7,         // fewer observed days than this → report nothing, not a guess
  wetRainIn: 0.01,    // measurable rain wets the canopy
  wetDiurnalF: 14,    // …or a collapsed diurnal range (cloud/humidity → dew persists)
  tarLoF: 59, tarHiF: 73,     // tar spot, mean temp
  glsLoF: 75, glsHiF: 90,     // gray leaf spot / northern corn leaf blight, tmax
  moldMaxF: 85,               // white mold (soy), tmax
};
function miDiseasePressure(daily, asOfKey, year, cfg) {
  const C = cfg || MI_DISEASE;
  const end = `${year}-${asOfKey}`;
  const rows = [];
  for (const dk of Object.keys(daily || {})) {
    if (dk > end) continue;
    const rec = daily[dk];
    if (!rec || rec.is_forecast) continue;
    if (rec.tmax_f == null || rec.tmin_f == null) continue;
    rows.push({ dk, tmax: Number(rec.tmax_f), tmin: Number(rec.tmin_f), pr: rec.precip_in == null ? 0 : Number(rec.precip_in) });
  }
  rows.sort((a, b) => (a.dk < b.dk ? -1 : 1));
  const w = rows.slice(-C.win);
  if (w.length < C.minDays) return null;
  let tar = 0, gls = 0, mold = 0, wetDays = 0, run = 0, streak = 0, fav = 0, rain = 0;
  for (const d of w) {
    const meanT = (d.tmax + d.tmin) / 2;
    const leafWet = d.pr >= C.wetRainIn || (d.tmax - d.tmin) <= C.wetDiurnalF;
    rain += d.pr;
    if (leafWet) { wetDays++; run++; if (run > streak) streak = run; } else { run = 0; continue; }
    const isTar = meanT >= C.tarLoF && meanT <= C.tarHiF;
    const isGls = d.tmax >= C.glsLoF && d.tmax <= C.glsHiF;
    if (isTar) tar++;
    if (isGls) gls++;
    if (isTar || isGls) fav++;
    if (d.tmax <= C.moldMaxF && d.pr >= C.wetRainIn) mold++;
  }
  return {
    score: Math.round((fav / w.length) * 100),
    days: w.length, favorable_days: fav, wet_days: wetDays, wet_streak: streak,
    tar_spot: tar, gls_nclb: gls, white_mold: mold,
    rain_in: Math.round(rain * 100) / 100,
  };
}
// ── Soil-water BUCKET (FAO-56). The single moisture signal — see MI_COEF BKT_*.
// Hargreaves reference ET (in/day) from Tmax/Tmin + latitude/day-of-year. Temp-only
// (matches the offline calibration). Ra returned as mm/day water-equivalent.
function miRaMm(lat, doy) {
  const phi = lat * Math.PI / 180, J = doy + 1;
  const dr = 1 + 0.033 * Math.cos(2 * Math.PI / 365 * J);
  const dec = 0.409 * Math.sin(2 * Math.PI / 365 * J - 1.39);
  const ws = Math.acos(miClamp(-Math.tan(phi) * Math.tan(dec), -1, 1));
  return (24 * 60 / Math.PI) * 0.0820 * dr * (ws * Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.sin(ws)) / 2.45;
}
function miEt0In(lat, doy, tmaxF, tminF) {
  if (tmaxF == null || tminF == null) return null;
  const a = (tmaxF - 32) * 5 / 9, b = (tminF - 32) * 5 / 9, m = (a + b) / 2;
  return Math.max(0, 0.0023 * miRaMm(lat, doy) * (m + 17.8) * Math.sqrt(Math.max(0, a - b))) / 25.4;
}
// FAO-56 corn crop coefficient / root-depth fraction / yield-sensitivity weight,
// all by days-after-planting. Silking (~55-78 DAP) is the most water-sensitive.
function miKc(dap) { if (dap < 10) return 0.30; if (dap < 40) return 0.30 + 0.85 * (dap - 10) / 30; if (dap < 95) return 1.15; if (dap < 130) return 1.15 - 0.65 * (dap - 95) / 35; return 0.50; }
function miRootFrac(dap) { return miClamp(0.15 + 0.85 * dap / 60, 0.15, 1); }
function miBktStageW(dap) { if (dap < 35) return 0.5; if (dap < 55) return 0.9; if (dap < 78) return 1.6; if (dap < 108) return 1.1; return 0.4; }
// Run the daily water balance for one cohort on one cell. `byDoy` maps day-of-year
// (0-based) → {tmaxF,tminF,precipIn}; the caller pre-merges observed-to-date with
// climatology-fill for future days so this is evidence-gated automatically. Returns
// {dry, wet} stage-weighted stress indices (re-centering happens in the caller).
function miRunBucket(byDoy, awcCm, plantDoy, lat, coef) {
  const awcIn = (awcCm != null ? awcCm : (coef.AWC_REF || 17.5)) / 2.54;   // SSURGO AWS cm → in plant-available
  const DRAIN = coef.BKT_DRAIN != null ? coef.BKT_DRAIN : 0.5;
  const SAT_THRESH = coef.BKT_SAT_THRESH != null ? coef.BKT_SAT_THRESH : 0.10;
  let prevCap = awcIn * miRootFrac(0);
  let sw = prevCap * coef.BKT_START_FRAC;
  let satExc = 0;                                                    // water above field capacity awaiting drainage (in)
  let dry = 0, wet = 0;
  for (let dap = 0; dap <= 140; dap++) {
    const doy = plantDoy + dap;
    const day = byDoy[doy];
    if (!day) continue;
    const cap = awcIn * miRootFrac(dap);
    if (cap > prevCap) sw += (cap - prevCap) * coef.BKT_DEEP_FRAC;   // newly-rooted deeper soil enters near field capacity
    prevCap = cap;
    const RAW = coef.BKT_PFRAC * cap;
    sw += day.precipIn || 0;
    if (sw > cap) { satExc += sw - cap; sw = cap; }                  // over-capacity is RETAINED, not discarded
    satExc -= Math.min(satExc, DRAIN);                               // …and drains by gravity over following days
    const et0 = miEt0In(lat, doy, day.tmaxF, day.tminF) || 0;
    const demand = et0 * miKc(dap);
    const depl = cap - sw;
    const Ks = depl <= RAW ? 1 : miClamp((cap - depl) / (cap - RAW), 0, 1);  // FAO-56 water-stress coefficient
    sw -= Math.min(demand * Ks, sw);
    // While excess water remains, it tops the profile back up — so the root zone
    // stays at capacity (Ks=1, no drought stress) until the saturation clears.
    // ET is charged once, against sw, so this does not double-count evaporation.
    if (satExc > 0 && sw < cap) { const back = Math.min(satExc, cap - sw); sw += back; satExc -= back; }
    const w = miBktStageW(dap);
    dry += (1 - Ks) * w;
    if (satExc > SAT_THRESH) wet += w;   // waterlogged = anaerobic DAYS, no ET gate (see MI_COEF BKT_K_WET)
  }
  return { dry, wet };
}
// THE baseline. One implementation, used by the live model AND the backtest
// so the validation measures exactly what's shipped. ys = [{x:year,y:yield}]
// (sorted asc, y>0). Slope-capped, recent-anchored, clamped to realized max.
function miBaseline(ys, targetYear, scap) {
  const SC = scap != null ? scap : 2.5;
  if (ys.length >= 4) {
    const fit = miOls(ys);
    const rawSlope = fit.slope;
    const slope = miClamp(rawSlope, 0, SC);
    const rec = ys.slice(-5);
    const recMx = miMean(rec.map((p) => p.x)), recMy = miMean(rec.map((p) => p.y));
    // Recent-anchored trend projection. No upper cap to the historical max:
    // genetic gain plus an exceptional weather year can legitimately set a new
    // record, and the model should be free to say so (Kevin 2026-05-29). The
    // exceptional-season UPSIDE rides on top of this via the weather factor
    // (≤ WEATHER_CEIL). The slope is still clamped to [0, SC] so a short/noisy
    // series can't fit an absurd trend — that's overfit protection, not a yield
    // ceiling.
    // The slope is DAMPED by MI_COEF.TREND_DAMP (0.5): the undamped recent-anchored
    // slope over-extrapolates (+5.3 bu bias on the full NY county backtest, 2026-06-18).
    // Half-weight keeps real genetic gain without overshooting down-years. See MI_COEF.
    const damp = (typeof MI_COEF !== "undefined" && MI_COEF.TREND_DAMP != null) ? MI_COEF.TREND_DAMP : 1;
    const baseline = recMy + damp * slope * (targetYear - recMx);
    return { baseline, slope, method: `OLS trend (${ys.length} yrs)${rawSlope !== slope ? ", slope-capped" : ""}` };
  }
  if (ys.length >= 1) return { baseline: miMean(ys.map((p) => p.y)), slope: null, method: `mean (${ys.length} yrs, trend not fittable)` };
  return { baseline: null, slope: null, method: null };
}
// Cow-driven silage displacement: NY dairies must feed a near-fixed silage
// tonnage to their herd, so in a low-yield year they chop MORE acres — pulled
// from grain. Calibrated on NY history: tons-silage-per-cow-yr is implied
// from state silage acres × silage yield ÷ milk-cow inventory (back out the
// constant from real data; runs ~13 tons/cow across 2015-2025).
// Heifers eat roughly 40-50% of a milking cow's silage intake (smaller body,
// lower energy demand — but they're on the farm 2+ years before first calving).
// 0.45 chosen as midpoint of industry references. Used to convert raw heifer
// counts into "cow-equivalents" for silage demand modeling.
const HEIFER_SILAGE_RATIO = 0.45;

// Replacement-heifer INVENTORY per cow — University of New Hampshire / University of
// Maryland dairy-extension formula (the same equation; UNH writes "2 × […×(AFC/24)]"
// which equals "…×(AFC/12)"):
//
//   heifers on farm per cow = (cull_rate + herd_growth) × (1 + heifer_mortality) × (AFC_months / 12)
//
// Defaults are the USDA/extension national figures cited on those pages: cull rate
// ~33%, heifer non-completion (death/cull before first calving) ~8%, age at first
// calving ~24 mo. At steady state this gives ~0.71 heifers/cow (their 234-cow example
// → 168 heifers = 0.72 ✓). The key term a flat heifer:cow ratio misses is herd_growth:
// when the herd is EXPANDING, dairies raise EXTRA replacements, so on-farm heifer
// inventory (and its silage demand) rises faster than the milking-cow count. Sources:
//   extension.unh.edu/resource/calculating-heifer-numbers
//   extension.umd.edu/resource/managing-heifer-inventory-dairy
const HEIFER_CULL_RATE = 0.33;          // USDA national avg dairy cull rate
const HEIFER_MORTALITY = 0.08;          // heifer non-completion (UNH ~8.7%)
const HEIFER_AGE_FIRST_CALVING_MO = 24; // modern NY target (national avg ~25-26)
const miHeiferPerCow = (growth = 0) =>
  (HEIFER_CULL_RATE + Math.max(0, growth)) * (1 + HEIFER_MORTALITY) * (HEIFER_AGE_FIRST_CALVING_MO / 12);
// How much an expanding herd lifts cow-equivalent (cows + heifers×consumption) silage
// demand versus a steady-state herd — a clean multiplier independent of the absolute
// count. >1 when growing. This is what flows the expansion signal into silage acres.
const miHeiferExpansionFactor = (growth = 0) => {
  const base = 1 + miHeiferPerCow(0) * HEIFER_SILAGE_RATIO;
  const now = 1 + miHeiferPerCow(growth) * HEIFER_SILAGE_RATIO;
  return now / base;
};

// `regionNames` is the set of county NAMES the herd is summed over, and `regionWF`
// is that same region's weather factor. Both used to be implicit and both used to
// be WNY: the herd came off a hardcoded 16-county list and the weather factor was
// the mean across EVERY modelled county, so a growing 39-county book was pricing a
// 16-county herd against 39 counties' weather. Passed in now so one region's
// counties decide both, and so "All New York" can have its own answer instead of
// borrowing Western NY's. Defaults keep old callers on the WNY set.
function miCowSilage(silAcRows, silYldRows, cowRows, year, regionWF, regionNames = MI_WNY_NAMES) {
  if (!silAcRows || !silAcRows.length || !silYldRows || !silYldRows.length || !cowRows || !cowRows.length) return null;
  // cowRows mixes state-level + county-level rows for BOTH milking cows
  // (MILK_COW_INVENTORY / MILK_COW_INVENTORY_COUNTY) and replacement heifers
  // (MILK_HEIFERS_INVENTORY / MILK_HEIFERS_INVENTORY_COUNTY). Heifers eat
  // ~45% of cow silage intake — including them in the cow-equivalent count
  // corrects per-county silage demand in heifer-heavy operations like Livingston.
  // State-level back-calibration (total silage ÷ cow-equivalents) keeps
  // per-county totals tied to NASS-published state silage acres.
  const REGION = regionNames && regionNames.size ? regionNames : MI_WNY_NAMES;
  // Only WNY has a measured long-run share of the state herd to fall back on if
  // its county rows are missing. See the `fallbackShare == null` branch below.
  const fallbackShare = REGION === MI_WNY_NAMES ? 0.46 : null;
  const ac = {}, yl = {}, cwState = {}, cwWny = {}, hfState = {}, hfWny = {};
  // State-only rows (county null) — county-level silAcRows are now also in
  // this dataset (for the per-county historical anchor) but must NOT pollute
  // the state-level calibration.
  silAcRows.forEach((r) => { if (r.county) return; const y = Number(r.year), v = Number(r.value); if (v > 0) ac[y] = v; });
  silYldRows.forEach((r) => { const y = Number(r.year), v = Number(r.value); if (v > 0) yl[y] = v; });
  cowRows.forEach((r) => {
    const y = Number(r.year), v = Number(r.value);
    if (!(v > 0)) return;
    const isHeifer = r.stat_type === "MILK_HEIFERS_INVENTORY" || r.stat_type === "MILK_HEIFERS_INVENTORY_COUNTY";
    const isStateLevel = r.county == null || r.county === "";
    if (isStateLevel) {
      const bucket = isHeifer ? hfState : cwState;
      (bucket[y] = bucket[y] || []).push(v);
    } else if (REGION.has(String(r.county).toUpperCase().trim())) {
      const bucket = isHeifer ? hfWny : cwWny;
      (bucket[y] = bucket[y] || []).push(v);
    }
  });
  const cwStateY = {}, cwWnyY = {}, hfStateY = {}, hfWnyY = {};
  Object.keys(cwState).forEach((y) => { cwStateY[y] = cwState[y].reduce((a, b) => a + b, 0) / cwState[y].length; });
  Object.keys(cwWny).forEach((y) => { cwWnyY[y] = cwWny[y].reduce((a, b) => a + b, 0); });
  Object.keys(hfState).forEach((y) => { hfStateY[y] = hfState[y].reduce((a, b) => a + b, 0) / hfState[y].length; });
  Object.keys(hfWny).forEach((y) => { hfWnyY[y] = hfWny[y].reduce((a, b) => a + b, 0); });
  // State heifer:cow ratio across all years for which we have both — used as
  // fallback when a specific year's heifer state count is missing.
  const ratioYrs = Object.keys(cwStateY).map(Number).filter((y) => hfStateY[y] != null && cwStateY[y] > 0);
  const stateHeiferRatio = ratioYrs.length
    ? ratioYrs.map((y) => hfStateY[y] / cwStateY[y]).reduce((a, b) => a + b, 0) / ratioYrs.length
    : 0.45;   // fallback if no heifer data — matches typical US dairy steady-state replacement rate
  // Cow-equivalent count for state year y: cows + heifers × HEIFER_SILAGE_RATIO
  const cowEquivStateY = (y) => {
    const cows = cwStateY[y];
    if (cows == null) return null;
    const heifers = hfStateY[y] != null ? hfStateY[y] : cows * stateHeiferRatio;
    return cows + heifers * HEIFER_SILAGE_RATIO;
  };
  // Calibrate tons-per-cow-equivalent on STATE data. Including heifer
  // consumption in the denominator gives a smaller per-head figure that
  // applies uniformly across counties without baking in a state-average
  // heifer:cow ratio.
  const calibYrs = Object.keys(ac).map(Number).filter((y) => y < year && yl[y] != null && cwStateY[y] != null).sort((a, b) => a - b).slice(-5);
  if (calibYrs.length < 3) return null;
  const implied = calibYrs.map((y) => (ac[y] * yl[y]) / cowEquivStateY(y));
  let tonsPerCowEquiv = implied.reduce((a, b) => a + b, 0) / implied.length;
  // Industry-knowledge floor for milking-cow-attributed consumption (includes
  // heifer share of silage). NASS state-level back-calibration produces ~13.3
  // for NY, but Kevin's read of WNY dairy operations + heavy replacement-heifer
  // ratios puts the real per-cow-incl-heifers figure closer to 15. Apply as
  // a floor so the model can't UNDER-estimate silage demand even if state-level
  // NASS silage/cow ratios drift down (e.g. cow-count growth outpaces silage
  // acres). Counties with active heifer-raising likely consume even more —
  // but absent county-level heifer data we use this uniform floor.
  const TONS_PER_COW_FLOOR = 15;
  let tonsPerCow = tonsPerCowEquiv * (1 + stateHeiferRatio * HEIFER_SILAGE_RATIO);
  // What the NASS back-calibration actually produced, kept before the floor can
  // overwrite it. The floor is a JUDGEMENT and it moves the headline a long way —
  // on the CY2026 statewide run it is 15.0 against a calibrated 13.1, which is
  // 65,378 of the 110,194 "extra acres pulled from grain". A surface that prints
  // the gap without printing the assumption behind it reads as this season's
  // weather stress when most of it is a constant.
  const tonsPerCowCalibrated = tonsPerCow;
  let tonsPerCowFloorApplied = false;
  if (tonsPerCow < TONS_PER_COW_FLOOR) {
    tonsPerCow = TONS_PER_COW_FLOOR;
    // Reverse the relationship: keep cowEquiv × tonsPerCowEquiv = cows × tonsPerCow
    tonsPerCowEquiv = TONS_PER_COW_FLOOR / (1 + stateHeiferRatio * HEIFER_SILAGE_RATIO);
    tonsPerCowFloorApplied = true;
  }
  const calibrationDetail = calibYrs.map((y, i) => ({ year: y, silageAcres: Math.round(ac[y]), silageYield: Math.round(yl[y] * 10) / 10, cows: Math.round(cwStateY[y]), tonsPerCow: Math.round(implied[i] * 10) / 10 }));
  const normalStateAcres = calibYrs.reduce((s, y) => s + ac[y], 0) / calibYrs.length;
  const normalYield = calibYrs.reduce((s, y) => s + yl[y], 0) / calibYrs.length;
  const normalStateCows = calibYrs.reduce((s, y) => s + cwStateY[y], 0) / calibYrs.length;
  // Current cow estimate: prefer the WNY county sum for the latest year.
  // Falls back to scaling the latest state-level by historical WNY share if
  // the county data isn't loaded yet (sync hasn't run).
  const wnyYrs = Object.keys(cwWnyY).map(Number).sort((a, b) => a - b);
  const stateYrs = Object.keys(cwStateY).map(Number).sort((a, b) => a - b);
  const latestStateY = stateYrs[stateYrs.length - 1];
  const latestWnyY = wnyYrs[wnyYrs.length - 1];
  // Herd-growth rate (smoothed 2-yr CAGR off the stable STATE milk-cow series) — drives
  // the replacement-heifer expansion factor (extra heifers raised when the herd grows).
  // State series is used (not noisy per-county) so the dynamics signal is stable.
  let herdGrowth = 0;
  if (stateYrs.length >= 3) {
    const c0 = cwStateY[stateYrs[stateYrs.length - 3]], c1 = cwStateY[latestStateY];
    if (c0 > 0 && c1 > 0) herdGrowth = Math.pow(c1 / c0, 1 / 2) - 1;
  } else if (stateYrs.length >= 2) {
    const c0 = cwStateY[stateYrs[stateYrs.length - 2]], c1 = cwStateY[latestStateY];
    if (c0 > 0 && c1 > 0) herdGrowth = c1 / c0 - 1;
  }
  // Clamp to a sane band so a data blip can't blow up silage demand.
  herdGrowth = Math.max(-0.05, Math.min(0.05, herdGrowth));
  const heiferExpansionFactor = miHeiferExpansionFactor(herdGrowth);
  // Helper to get cow-equivalent count for any (cow_total, heifer_total) pair.
  // When heifers aren't directly observed, we estimate from state heifer:cow ratio.
  const cowEquiv = (cows, heifers) => {
    const h = heifers != null ? heifers : cows * stateHeiferRatio;
    return cows + h * HEIFER_SILAGE_RATIO;
  };
  let currentCows, currentHeifers, currentCowEquiv, regionShareOfState, currentSource;
  if (latestWnyY != null && cwWnyY[latestWnyY] > 0) {
    currentCows = cwWnyY[latestWnyY];
    currentHeifers = hfWnyY[latestWnyY] != null && hfWnyY[latestWnyY] > 0 ? hfWnyY[latestWnyY] : null;
    currentCowEquiv = cowEquiv(currentCows, currentHeifers);
    const sharesByYr = wnyYrs.filter((y) => cwStateY[y] > 0).map((y) => cwWnyY[y] / cwStateY[y]);
    regionShareOfState = sharesByYr.length ? sharesByYr.reduce((a, b) => a + b, 0) / sharesByYr.length : fallbackShare;
    currentSource = `${REGION.size}-county sum (${latestWnyY})${currentHeifers != null ? " + heifers" : " (heifers est)"}`;
  } else {
    // No county-level cow rows for this region. The share of the state herd is
    // then genuinely UNKNOWN, and it multiplies every number below — normal
    // acres, normal cows, the whole silage demand. Only the WNY set carries a
    // measured fallback (0.46, its long-run share); any other region returns no
    // dairy figure rather than borrowing WNY's share under its own name.
    if (fallbackShare == null) return null;
    regionShareOfState = fallbackShare;
    currentCows = cwStateY[latestStateY] * regionShareOfState;
    currentHeifers = hfStateY[latestStateY] != null ? hfStateY[latestStateY] * regionShareOfState : null;
    currentCowEquiv = cowEquiv(currentCows, currentHeifers);
    currentSource = `STATE ${latestStateY} × ${Math.round(regionShareOfState * 100)}% region-share fallback`;
  }
  const normalAcres = normalStateAcres * regionShareOfState;
  const normalCows = normalStateCows * regionShareOfState;
  const stressedYield = normalYield * (regionWF != null ? regionWF : 1);
  // Silage tonnage = the REGION's cow-equivalents × per-equiv calibration. Heifers
  // contribute via cow-equivalent at HEIFER_SILAGE_RATIO weight.
  const requiredTonnage = currentCowEquiv * tonsPerCowEquiv;
  const requiredAcres = stressedYield > 0 ? requiredTonnage / stressedYield : null;
  const extraAcres = requiredAcres != null ? Math.max(0, requiredAcres - normalAcres) : 0;
  return {
    tonsPerCow: Math.round(tonsPerCow * 10) / 10,
    // The rate the NASS 5-yr back-calibration produced, before TONS_PER_COW_FLOOR.
    // Equal to tonsPerCow when the floor did not bite.
    tonsPerCowCalibrated: Math.round(tonsPerCowCalibrated * 10) / 10,
    tonsPerCowEquiv: Math.round(tonsPerCowEquiv * 10) / 10,
    tonsPerCowFloorApplied,
    stateHeiferRatio: Math.round(stateHeiferRatio * 1000) / 1000,
    herd_growth_pct: Math.round(herdGrowth * 1000) / 10,
    heifer_per_cow: Math.round(miHeiferPerCow(herdGrowth) * 1000) / 1000,
    heiferExpansionFactor: Math.round(heiferExpansionFactor * 10000) / 10000,
    normalAcres: Math.round(normalAcres),
    normalCows: Math.round(normalCows),
    currentCows: Math.round(currentCows),
    currentHeifers: currentHeifers != null ? Math.round(currentHeifers) : null,
    currentCowEquiv: Math.round(currentCowEquiv),
    regionShareOfState: Math.round(regionShareOfState * 1000) / 10,
    regionCounties: REGION.size,
    cowSource: currentSource,
    latestCowYear: latestWnyY || latestStateY,
    normalSilageYield: Math.round(normalYield * 10) / 10,
    stressedSilageYield: Math.round(stressedYield * 10) / 10,
    requiredAcres: requiredAcres != null ? Math.round(requiredAcres) : null,
    extraAcres: Math.round(extraAcres),
    yrsCalibrated: calibYrs.length,
    calibrationDetail,
  };
}

// Walk-forward backtest: for each county-year with ≥4 prior years, predict it
// from ONLY the years before it (no peeking) and compare to actual NASS. This
// validates the trend baseline — the dominant component. Weather/pace are
// agronomic overlays that can't be backtested (no per-year survey history).
function miBacktest(yieldRows, scap) {
  const by = {};
  for (const r of yieldRows || []) {
    const nm = String(r.county || "").toUpperCase().trim();
    const yv = Number(r.value);
    if (!(yv > 0)) continue;
    (by[nm] = by[nm] || []).push({ x: Number(r.year), y: yv });
  }
  const errs = []; let minY = Infinity, maxY = -Infinity;
  Object.values(by).forEach((arr) => {
    arr.sort((a, b) => a.x - b.x);
    for (let i = 4; i < arr.length; i++) {
      const pred = miBaseline(arr.slice(0, i), arr[i].x, scap).baseline;
      if (pred == null) continue;
      errs.push(pred - arr[i].y);
      minY = Math.min(minY, arr[i].x); maxY = Math.max(maxY, arr[i].x);
    }
  });
  if (!errs.length) return null;
  const n = errs.length;
  const mae = errs.reduce((s, e) => s + Math.abs(e), 0) / n;
  const bias = errs.reduce((s, e) => s + e, 0) / n;
  return {
    n, fromY: minY, toY: maxY,
    mae: miR1(mae), bias: miR1(bias),
    w20: Math.round(errs.filter((e) => Math.abs(e) <= 20).length / n * 100),
    w10: Math.round(errs.filter((e) => Math.abs(e) <= 10).length / n * 100),
  };
}
// asOfMmdd caps the window at season-start → that MM-DD (inclusive). Used so
// the current year and every prior "normal" year are summed over the SAME
// to-date window — otherwise a mid-season run blends ~30 real days with ~130
// climatology-filled (= normal) days and the in-season anomaly washes to ≈0.
// ── Climatology: per-county MM-DD → mean (precip, GDD) across prior years.
// Used as the fallback when a cohort's silking/grain-fill window extends
// beyond what's observed + the 14-day forecast. Without it, August anomalies
// computed in May would be undefined — with it, they read as "normal" until
// real data shows otherwise. byDateRecs is the per-county date→{tmax,tmin,
// precip,is_forecast} map already built in the caller.
function miClimoDoy(byDateRecs, year) {
  const acc = {};
  for (const k of Object.keys(byDateRecs)) {
    const rec = byDateRecs[k];
    if (!rec || rec.is_forecast) continue;
    const yr = Number(k.slice(0, 4)); if (yr >= year || yr < year - 11) continue;
    const doy = k.slice(5);
    const a = acc[doy] || (acc[doy] = { p: 0, pN: 0, g: 0, gN: 0 });
    if (rec.precip_in != null) { a.p += Number(rec.precip_in); a.pN++; }
    const g = miModGdd(rec.tmax_f, rec.tmin_f);
    if (g != null) { a.g += g; a.gN++; }
  }
  const out = {};
  for (const k of Object.keys(acc)) out[k] = { precip: acc[k].pN ? acc[k].p / acc[k].pN : 0, gdd: acc[k].gN ? acc[k].g / acc[k].gN : 0 };
  return out;
}
// ── Cohort weather factor — Kevin's model. Each cohort = (plant_date, acres).
// Silking ≈ plant + 60–75d (V8→R1, water-/heat-critical), grain fill ≈ +75–105d
// (R2→R5, dry-stress sensitive). For each cohort × window, sum precip + GDD
// using observed > forecast > climatology (in that order). Compare to the same
// calendar-window historical mean. Apply the same per-anomaly formula as
// before, but PER COHORT — so late-planted acres get August weather, early-
// planted acres get July weather, and they don't muddle each other.
//
// "Evidence weight" = fraction of the window with real (observed/forecast)
// data; pure-climo windows return factor ~1 (no signal). As the season runs,
// evidence grows → the forecast sharpens.
function miCohortWeather(cohorts, byDateRecs, climoDoy, coef, opts) {
  if (!cohorts || !cohorts.length) return { factor: 1, breakdown: [] };
  // Soil-water sensitivity multiplier on the precip effect (1 = neutral). Passed
  // per-cell by miGridShadowWeather from SSURGO AWC: low-AWC (gravelly) ground >1.
  const soilMult = (opts && opts.soilMult != null) ? opts.soilMult : 1;
  const SILK_OFFSET_START = 60, SILK_OFFSET_END = 75;     // ~V8 to R1
  const FILL_OFFSET_START = 75, FILL_OFFSET_END = 105;    // ~R2 to R5
  const SILK_W = 0.60, FILL_W = 0.40;                     // weights: silking matters more
  const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  const aggregate = (start, end) => {
    let p = 0, g = 0, pN = 0, gN = 0;
    let obs = 0, fc = 0, climo = 0, tot = 0;
    let pNorm = 0, gNorm = 0;
    // VPD tracking — peak daily VPD during the window. Stress = days with
    // VPD > 2.0 kPa. Computed only on days where we have real observed or
    // forecasted tmax+tmin (NOT climatology — climo means + RH ≈ daily ave
    // gives misleading values, and we already have a separate signal weight).
    let vpdStressDays = 0, vpdMaxKpa = 0, vpdObs = 0;
    // #5 heat-stress days (Tmax > threshold) and #8 killing-freeze days
    // (Tmin <= threshold), counted on real (observed/forecast) days only so a
    // pure-climatology future window adds no penalty until data arrives.
    let heatStressDays = 0, freezeDays = 0;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      tot++;
      const k = ymd(d), doyK = k.slice(5);
      const cm = climoDoy[doyK];
      if (cm) { pNorm += cm.precip; gNorm += cm.gdd; }
      const rec = byDateRecs[k];
      let usedReal = false;
      if (rec) {
        if (rec.precip_in != null) { p += Number(rec.precip_in); pN++; usedReal = true; }
        const gg = miModGdd(rec.tmax_f, rec.tmin_f);
        if (gg != null) { g += gg; gN++; usedReal = true; }
        if (usedReal) {
          if (rec.is_forecast) fc++; else obs++;
          const vpd = miVpdKpa(rec.tmax_f, rec.tmin_f);
          if (vpd != null) {
            vpdObs++;
            if (vpd > vpdMaxKpa) vpdMaxKpa = vpd;
            if (vpd >= 2.0) vpdStressDays++;
          }
          if (rec.tmax_f != null && Number(rec.tmax_f) > coef.HEAT_STRESS_TMAX_F) heatStressDays++;
          if (rec.tmin_f != null && Number(rec.tmin_f) <= coef.FREEZE_TMIN_F) freezeDays++;
        }
        else if (cm) { p += cm.precip; g += cm.gdd; climo++; }
      } else if (cm) {
        p += cm.precip; g += cm.gdd; climo++;
      }
    }
    return { p, g, pNorm, gNorm, evidence: tot > 0 ? (obs + fc) / tot : 0, obs, fc, climo, tot, vpd_stress_days: vpdStressDays, vpd_max_kpa: Math.round(vpdMaxKpa * 100) / 100, vpd_obs_days: vpdObs, heat_stress_days: heatStressDays, freeze_days: freezeDays };
  };
  let wF = 0, totalAc = 0;
  const breakdown = [];
  let plantedAc = 0, projectedAc = 0;
  for (const c of cohorts) {
    if (!c.ac || c.ac <= 0) continue;
    const silkS = new Date(c.date); silkS.setDate(silkS.getDate() + SILK_OFFSET_START);
    const silkE = new Date(c.date); silkE.setDate(silkE.getDate() + SILK_OFFSET_END);
    const fillS = new Date(c.date); fillS.setDate(fillS.getDate() + FILL_OFFSET_START);
    const fillE = new Date(c.date); fillE.setDate(fillE.getDate() + FILL_OFFSET_END);
    const silk = aggregate(silkS, silkE);
    const fill = aggregate(fillS, fillE);
    const effect = (anomP, anomG, evidence) => {
      const gE = miClamp(coef.GDD_SENS * anomG, -coef.GDD_CLAMP, coef.GDD_CLAMP);
      let pE = 0;
      // Inverted-U moisture response (symmetric, not downside-only): deficit hurts,
      // adequate-to-moderate surplus in the water-hungry silk/fill windows HELPS,
      // excess waterlogs. See MI_COEF beneficial-rain note + project_weather_factor_backtest.
      const WET_OPT = coef.WET_OPT != null ? coef.WET_OPT : 0.15;
      const WET_GAIN = coef.WET_GAIN != null ? coef.WET_GAIN : 0;   // 0 → legacy downside-only behavior
      const DRY_CAP = coef.DRY_CAP != null ? coef.DRY_CAP : Infinity;
      if (anomP < 0) {
        // #6 heat×drought interaction: a precip deficit bites harder when the
        // same window also runs hot (evaporative demand + water stress compound).
        // Amplify the drought penalty up to (1 + HEAT_DROUGHT_INTER)× at a
        // strongly positive heat anomaly; no amplification in a cool window.
        const heatAmp = 1 + coef.HEAT_DROUGHT_INTER * miClamp(anomG, 0, 1);
        pE = Math.max(coef.DRY_SENS * anomP * heatAmp, -DRY_CAP);   // deficit penalty, capped
      } else if (anomP <= WET_OPT) {
        pE = WET_GAIN * (anomP / WET_OPT);                          // beneficial rain ramps up to peak
      } else if (anomP <= coef.WET_THRESH) {
        pE = WET_GAIN * (1 - (anomP - WET_OPT) / (coef.WET_THRESH - WET_OPT)); // benefit diminishes
      } else {
        pE = -coef.WET_SENS * (anomP - coef.WET_THRESH);           // excess-moisture penalty
      }
      // soilMult scales the precip effect only (drought + beneficial rain) by the
      // cell's water-holding capacity; heat (gE) is soil-independent.
      // PRECIP_OFF: the soil-water bucket now owns ALL moisture stress, so the
      // cohort factor is reduced to its temperature terms (GDD here; VPD/heat/freeze
      // added below) to avoid double-counting drought. See MI_COEF BKT_*.
      if (coef.PRECIP_OFF) return gE * evidence;
      return (gE + pE * soilMult) * evidence;
    };
    const silkPA = silk.pNorm > 0 ? (silk.p - silk.pNorm) / silk.pNorm : 0;
    const silkGA = silk.gNorm > 0 ? (silk.g - silk.gNorm) / silk.gNorm : 0;
    const fillPA = fill.pNorm > 0 ? (fill.p - fill.pNorm) / fill.pNorm : 0;
    const fillGA = fill.gNorm > 0 ? (fill.g - fill.gNorm) / fill.gNorm : 0;
    const silkEff = effect(silkPA, silkGA, silk.evidence);
    const fillEff = effect(fillPA, fillGA, fill.evidence);
    // VPD silking penalty — high-VPD days during silking shrivel the pollen/silk
    // pairing and reduce kernel set, separate from precip/GDD signals. Linear:
    // each VPD-stress day (>2.0 kPa) in the silking window = 0.5% yield haircut,
    // capped at 8% so the model doesn't go nuclear. Only counts days where we
    // had real (observed or forecast) tmax/tmin — climo-only silking windows get
    // no VPD penalty (we don't know yet).
    const VPD_PER_DAY_PENALTY = 0.005;
    const VPD_MAX_PENALTY = 0.08;
    const vpdSilkEff = -Math.min(VPD_MAX_PENALTY, silk.vpd_stress_days * VPD_PER_DAY_PENALTY);
    // #5 grain-fill heat-stress penalty — hot fill days (Tmax > HEAT_STRESS_TMAX_F)
    // accelerate senescence and burn assimilate to respiration, shrinking kernels.
    const heatFillEff = -Math.min(coef.HEAT_STRESS_MAX, fill.heat_stress_days * coef.HEAT_STRESS_PER_DAY);
    // #8 killing-freeze penalty — a hard freeze (Tmin <= FREEZE_TMIN_F) in the
    // grain-fill window arrives before black layer and truncates fill. Hits the
    // late-planted / silage-redirected cohorts hardest (their fill runs into fall).
    const freezeFillEff = -Math.min(coef.FREEZE_MAX, fill.freeze_days * coef.FREEZE_PER_DAY);
    const factor = miClamp(1 + silkEff * SILK_W + fillEff * FILL_W + vpdSilkEff + heatFillEff + freezeFillEff, coef.WEATHER_FLOOR, coef.WEATHER_CEIL);
    wF += factor * c.ac; totalAc += c.ac;
    if (c.projected) projectedAc += c.ac; else plantedAc += c.ac;
    breakdown.push({
      plant_date: ymd(c.date), acres: Math.round(c.ac), projected: !!c.projected,
      silk_window: `${ymd(silkS)}–${ymd(silkE)}`, fill_window: `${ymd(fillS)}–${ymd(fillE)}`,
      silk_precip_anom: miR3(silkPA), silk_gdd_anom: miR3(silkGA), silk_evidence: miR3(silk.evidence),
      fill_precip_anom: miR3(fillPA), fill_gdd_anom: miR3(fillGA), fill_evidence: miR3(fill.evidence),
      silk_vpd_stress_days: silk.vpd_stress_days, silk_vpd_max_kpa: silk.vpd_max_kpa, silk_vpd_obs_days: silk.vpd_obs_days,
      fill_heat_days: fill.heat_stress_days, fill_freeze_days: fill.freeze_days,
      factor: miR3(factor),
    });
  }
  return {
    factor: totalAc > 0 ? miR3(wF / totalAc) : 1,
    breakdown, totalAc: Math.round(totalAc),
    planted_ac: Math.round(plantedAc), projected_ac: Math.round(projectedAc),
  };
}

// day-of-year (1..365; leap Feb 29 folded into 365 by the climatology builder)
// → "MM-DD", using a fixed NON-leap reference year (2021) so the mapping matches
// the wny_grid_climatology rows. These mmdd keys are what miCohortWeather reads
// off the climoDoy object (it does k.slice(5) on each YYYY-MM-DD day).
function miDoyToMmdd(doy) {
  const d = new Date(Date.UTC(2021, 0, 1) + (Number(doy) - 1) * 86400000);
  return `${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

// ── Phase 2 SHADOW weather factor — per-cell gridMET, aggregated to county ──
// Runs the EXACT same cohort-weather model (miCohortWeather) the live forecast
// uses, but sourced from the per-cell gridMET grid + its 1995-2025 per-cell
// climatology instead of the 16 county-centroid POWER points. Each cell gets
// its own factor (its current weather vs ITS OWN normal); factors are then
// acre-weighted back to the county (v1: uniform weight within a county). This
// is decoration only — the result is displayed beside the incumbent factor and
// does NOT feed `forecast`. Promotion waits on the historical backtest gate
// (needs a gridMET history backfill that doesn't exist yet). See wny-weather-stack memory.
//   gridCells: [{id, county_fips}]
//   gridDaily: [{cell_id, obs_date, tmax_c, tmin_c, precip_mm, is_forecast}]  (current year)
//   gridClimo: [{cell_id, day_of_year, gdd_f, precip_mm}]                      (1995-2025 normal)
// WNY-average weather panels (pulse + cumulative chart) from the high-def grid
// (gridMET obs + Open-Meteo forecast) and the 1995-2025 grid climatology — so the
// Weather Pulse cards, the cumulative GDD/rain charts, and the situation-summary
// weather facts all share the SAME feed as the yield model instead of the legacy
// county-POWER series. Simple spatial mean across cells (WNY-wide conditions);
// the normal comes from per-DOY climatology (Jensen-safe gdd_f), day-matched to
// the observed window for the anomaly. Field names mirror the county wxPulse/
// wxChart so every downstream consumer (cards, PDF, summary brief) is unchanged.
function miGridWxPanels(gridDaily, gridClimo, year) {
  if (!gridDaily || !gridDaily.length || !gridClimo || !gridClimo.length) return null;
  const C2F = (c) => (c == null ? null : Number(c) * 9 / 5 + 32);
  const MM2IN = (mm) => (mm == null ? null : Number(mm) / 25.4);
  const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  // WNY-avg current-year daily (obs + forecast)
  const byDate = {};
  for (const w of gridDaily) {
    if (Number(w.obs_date.slice(0, 4)) !== year) continue;
    const a = byDate[w.obs_date] || (byDate[w.obs_date] = { g: 0, gN: 0, p: 0, pN: 0, tx: 0, txN: 0, tn: 0, tnN: 0, fc: false });
    const g = miModGdd(C2F(w.tmax_c), C2F(w.tmin_c));
    if (g != null) { a.g += g; a.gN++; }
    const pin = MM2IN(w.precip_mm);
    if (pin != null) { a.p += pin; a.pN++; }
    const txF = C2F(w.tmax_c); if (txF != null) { a.tx += txF; a.txN++; }
    const tnF = C2F(w.tmin_c); if (tnF != null) { a.tn += tnF; a.tnN++; }
    if (w.is_forecast) a.fc = true;
  }
  // WNY-avg per-mmdd climatology normal
  const norm = {};
  for (const e of gridClimo) {
    const mmdd = miDoyToMmdd(e.day_of_year);
    const a = norm[mmdd] || (norm[mmdd] = { g: 0, gN: 0, p: 0, pN: 0 });
    if (e.gdd_f != null) { a.g += Number(e.gdd_f); a.gN++; }
    const pin = MM2IN(e.precip_mm);
    if (pin != null) { a.p += pin; a.pN++; }
  }
  const dG = (k) => (byDate[k] && byDate[k].gN ? byDate[k].g / byDate[k].gN : null);
  const dP = (k) => (byDate[k] && byDate[k].pN ? byDate[k].p / byDate[k].pN : null);
  const dTx = (k) => (byDate[k] && byDate[k].txN ? byDate[k].tx / byDate[k].txN : null);
  const dTn = (k) => (byDate[k] && byDate[k].tnN ? byDate[k].tn / byDate[k].tnN : null);
  const nG = (mmdd) => (norm[mmdd] && norm[mmdd].gN ? norm[mmdd].g / norm[mmdd].gN : null);
  const nP = (mmdd) => (norm[mmdd] && norm[mmdd].pN ? norm[mmdd].p / norm[mmdd].pN : null);
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const PC = MI_CORN_PLANTING;
  const rainBlock = PC.rainBlockIn != null ? PC.rainBlockIn : 0.3;
  const soak = PC.soakIn != null ? PC.soakIn : 0.5;
  const dry = PC.drydownDays != null ? PC.drydownDays : 2;
  // last-14d observed
  let p14 = 0, g14 = 0;
  for (let k = 1; k <= 14; k++) { const d = new Date(t); d.setDate(d.getDate() - k); const kk = ymd(d); const p = dP(kk), g = dG(kk); if (p != null) p14 += p; if (g != null) g14 += g; }
  // RECENT CONDITIONS — the "what's it doing right now" signals the situation summary
  // needs so it isn't tone-deaf to the last day or two (e.g. warning about drying the
  // day after a soaking rain). last-3d precip + a per-day recent log (precip + temps),
  // and a 7-day temperature anomaly so "cool/warm right now" is grounded in data.
  let p3 = 0; for (let k = 1; k <= 3; k++) { const d = new Date(t); d.setDate(d.getDate() - k); const p = dP(ymd(d)); if (p != null) p3 += p; }
  let g7 = 0, ng7 = 0; for (let k = 1; k <= 7; k++) { const d = new Date(t); d.setDate(d.getDate() - k); const kk = ymd(d); const g = dG(kk); if (g != null) { g7 += g; const x = nG(kk.slice(5)); if (x != null) ng7 += x; } }
  const recent_daily = [];
  for (let k = 6; k >= 1; k--) {
    const d = new Date(t); d.setDate(d.getDate() - k); const kk = ymd(d);
    const p = dP(kk), tx = dTx(kk), tn = dTn(kk);
    if (p == null && tx == null) continue;   // no obs that day
    recent_daily.push({ date: kk, precip_in: p == null ? null : Math.round(p * 100) / 100, tmax_f: tx == null ? null : Math.round(tx), tmin_f: tn == null ? null : Math.round(tn) });
  }
  // next-14d forecast + workability (rain-block + drydown after a soaker)
  const fcDays = []; let pNext = 0, pNextN = 0, workable = 0, blocked = 0;
  for (let k = 0; k < 14; k++) {
    const d = new Date(t); d.setDate(d.getDate() + k);
    const kk = ymd(d); const p = dP(kk);
    // Forecast high/low (°F, WNY-avg) alongside the rain — the Forecast & Outlook
    // strip shows both, since heat drives the crop as much as the rain does.
    const fTx = dTx(kk), fTn = dTn(kk);
    fcDays.push({ d: kk, p: p == null ? null : Math.round(p * 100) / 100, tx: fTx == null ? null : Math.round(fTx), tn: fTn == null ? null : Math.round(fTn) });
    if (p != null) { pNext += p; pNextN++; }
    let bl = false;
    if (p != null && p >= rainBlock) bl = true;
    else { for (let j = 1; j <= dry; j++) { const pd = new Date(d); pd.setDate(pd.getDate() - j); const pp = dP(ymd(pd)); if (pp != null && pp >= soak) { bl = true; break; } } }
    if (bl) blocked++; else workable++;
  }
  // season-to-date observed vs day-matched climatology normal
  const [sm, sd] = MI_SEASON.start.split("-").map(Number);
  let sP = 0, snP = 0, sG = 0, snG = 0;
  for (let d = new Date(year, sm - 1, sd); d <= t; d.setDate(d.getDate() + 1)) {
    const kk = ymd(d), mmdd = kk.slice(5);
    const p = dP(kk), g = dG(kk);
    if (p != null) { sP += p; const x = nP(mmdd); if (x != null) snP += x; }
    if (g != null) { sG += g; const x = nG(mmdd); if (x != null) snG += x; }
  }
  // SEASON TRAJECTORY by month (Apr 1 → today) — the SHAPE of "where we've been",
  // each month's precip + GDD vs its own normal, so the brief can narrate the arc
  // (wet spring stalled planting → dry window opened it → wet June) instead of a
  // single season total that hides the timing.
  const MO_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const moAgg = {};
  for (let d = new Date(year, 3, 1); d <= t; d.setDate(d.getDate() + 1)) {   // Apr 1 = full spring
    const kk = ymd(d), mmdd = kk.slice(5), mo = d.getMonth();
    const p = dP(kk), g = dG(kk);
    const a = moAgg[mo] || (moAgg[mo] = { p: 0, np: 0, g: 0, ng: 0 });
    if (p != null) { a.p += p; const x = nP(mmdd); if (x != null) a.np += x; }
    if (g != null) { a.g += g; const x = nG(mmdd); if (x != null) a.ng += x; }
  }
  const season_by_month = Object.keys(moAgg).map(Number).sort((a, b) => a - b).map((mo) => ({
    month: MO_ABBR[mo],
    precip_in: miR1(moAgg[mo].p), precip_normal_in: moAgg[mo].np > 0 ? miR1(moAgg[mo].np) : null,
    gdd: Math.round(moAgg[mo].g), gdd_normal: moAgg[mo].ng > 0 ? Math.round(moAgg[mo].ng) : null,
  }));
  // ── Rain the CROP has actually seen, per candidate plant week. For each week
  // start from the season open to today: precip observed from that week forward,
  // and the day-matched normal for the SAME window. miComputeYield acre-weights
  // this across the planting cohorts to get "rain since plant date".
  //
  // Why this exists: season_precip_anom runs from MI_SEASON.start (04-20), weeks
  // before most WNY corn is in the ground. In a wet spring that rain fell on bare
  // ground — it delayed planting (and is already charged as a pace penalty) — yet
  // it reads out as a moisture SURPLUS, so the same wet spring got counted once as
  // a cost and again as a credit. Rain that fell before an acre was planted is not
  // that acre's water supply. Weight each cohort by the rain it has actually caught.
  const precip_since_by_week = [];
  {
    for (let ws = new Date(year, sm - 1, sd); ws <= t; ws.setDate(ws.getDate() + 7)) {
      let o = 0, n = 0, nd = 0;
      for (let d = new Date(ws); d <= t; d.setDate(d.getDate() + 1)) {
        const kk = ymd(d), p = dP(kk);
        if (p == null) continue;
        o += p;
        const x = nP(kk.slice(5));
        if (x != null) { n += x; nd++; }
      }
      if (nd > 0 && n > 0) precip_since_by_week.push({ week_start: ymd(ws), obs_in: miR1(o), normal_in: miR1(n), pct_of_normal: Math.round((o / n) * 100) });
    }
  }
  const wx_pulse = {
    season_precip_in: miR1(sP), season_precip_normal_in: snP > 0 ? miR1(snP) : null, season_precip_anom: snP > 0 ? miR3((sP - snP) / snP) : null,
    precip_since_by_week,
    season_gdd: Math.round(sG), season_gdd_normal: snG > 0 ? Math.round(snG) : null, season_gdd_anom: snG > 0 ? miR3((sG - snG) / snG) : null,
    season_by_month,
    last14_precip_in: miR1(p14), last14_gdd: Math.round(g14),
    last3_precip_in: miR1(p3),
    last7_gdd: Math.round(g7), last7_gdd_normal: ng7 > 0 ? Math.round(ng7) : null, last7_gdd_anom: ng7 > 0 ? miR3((g7 - ng7) / ng7) : null,
    recent_daily,
    next14_precip_in: miR1(pNext), next14_days_forecast: pNextN,
    next14_workable: workable, next14_blocked: blocked,
    next14_daily: fcDays,
    asof: ymd(t),
  };
  // cumulative weekly chart: cur = obs+forecast to today+14d; normal = climatology
  const WEEKS = 15;
  const startUTC = Date.UTC(year, sm - 1, sd);
  const asOfUTC = Date.UTC(t.getFullYear(), t.getMonth(), t.getDate());
  const lastCurUTC = asOfUTC + 14 * 864e5;
  const u2 = (u) => { const d = new Date(u); return { ymd: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`, mmdd: `${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`, lbl: `${d.getUTCMonth() + 1}/${d.getUTCDate()}` }; };
  let cg = 0, cp = 0; const cur = [];
  for (let wk = 0; wk <= WEEKS; wk++) {
    const ws = startUTC + wk * 7 * 864e5;
    if (ws > lastCurUTC) break;
    let any = false;
    for (let j = 0; j < 7; j++) { const u = ws + j * 864e5; if (u > lastCurUTC) break; const kk = u2(u).ymd; const g = dG(kk), p = dP(kk); if (g != null) { cg += g; any = true; } if (p != null) { cp += p; any = true; } }
    if (any) cur.push({ label: u2(ws).lbl, gdd: Math.round(cg), pr: Math.round(cp * 10) / 10, fc: ws > asOfUTC });
  }
  let mg = 0, mp = 0; const normal = [];
  for (let wk = 0; wk <= WEEKS; wk++) {
    const ws = startUTC + wk * 7 * 864e5;
    for (let j = 0; j < 7; j++) { const mmdd = u2(ws + j * 864e5).mmdd; const g = nG(mmdd), p = nP(mmdd); if (g != null) mg += g; if (p != null) mp += p; }
    normal.push({ label: u2(ws).lbl, gdd: Math.round(mg), pr: Math.round(mp * 10) / 10 });
  }
  return { wx_pulse, wx_chart: cur.length ? { cur, normal } : null };
}

// ─────────────────────────────────────────────────────────────────────────
// MECHANISTIC CORN MODEL (CERES / Hybrid-Maize lineage) — ported from the
// validated tools/yield-model harness (frozen config 2026-06-24). Process-based
// daily simulation per cell: GDD phenology, LAI canopy, Priestley-Taylor PET with
// VPD coupling, FAO-56 soil-water bucket (per-cell SSURGO AWC), RUE biomass, and
// stage-aware kernel-set + grain-fill. Used as a damped weather INDEX on the
// county recent-mean baseline (final = baseline × (1+DAMP·(idx/idx_normal−1))).
// Book-wide leave-one-year-out MAE 6.3 bu vs 13.9 climatology; calls the abnormal
// years (2025 139 vs 141, 2021 178 vs 175). See tools/yield-model/HANDOFF.md.
// ─────────────────────────────────────────────────────────────────────────
// ── WHICH MODEL PRODUCED A NUMBER IS PART OF THE NUMBER ──────────────────────
//
// Kevin, on the daily prediction chart: "model is really choppy, is that
// expected?" Measured, and the answer was no — and it was not the weather.
// Replaying the model day-by-day over 14 days with SETTLED data moves the
// headline 0.12 bu/day (max 0.30). The logged season moves 2.28 (max 10.0).
// Ruled out along the way, each by measurement rather than argument:
//   the 16-day forecast window   worth 0.10 bu to the headline
//   MRMS revising recent precip  differs from gridMET on 6% of rows by 0.25 mm
//                                mean, and moves the headline not at all
//   county acre re-weighting     implied acres move ~1% while yield moves ~4%
// What is left is that the MODEL CHANGED underneath the series: the weather
// ceiling moved twice in August (1.25 -> 1.12 -> 1.08), each worth several bu on
// the counties it bound, plus a wet taper added and removed inside one day. In
// the chart those are indistinguishable from a weather call.
//
// So the version is stamped on every daily row. BUMP IT whenever a change moves
// the number — a step in the chart then has a name instead of looking like a
// forecast that lost its nerve. Date-stamped rather than numbered so it reads
// against the commit log directly.
const MI_MODEL_VERSION = "2026-08-16-info-weighted-damp";

const MI_CORN = {
  GDD_EMERGE: 120, GDD_SILK: 1350, GDD_MATURE: 2350,            // °F·day from planting
  LAI_MAX: 4.3, K_BEER: 0.60, SENESCE_FRAC: 0.55,
  RUE: 3.45, PAR_FRAC: 0.48,
  AWC_TO_MM: 13.0, TAW_MIN: 60, TAW_MAX: 340, P_DEPLETE: 0.45,
  ROOT_GDD_FULL: 900, ROOT_START_FRAC: 0.35, KC_MAX: 1.18,
  VPD_DEMAND_K: 0.10, VPD_REF: 0.9, START_FRAC: 0.85,
  HEAT_PHOTO_T: 33, HEAT_PHOTO_SLOPE: 0.035, COLD_PHOTO_T: 12, COLD_PHOTO_SLOPE: 0.03,
  SILK_GDD_WIN: 170, KSET_GROWTH_REF: 12, KSET_GROWTH_W: 0.55,
  KSET_HEAT_T: 35, KSET_HEAT_PER_DAY: 0.020, KSET_VPD_T: 2.6, KSET_VPD_PER_DAY: 0.015,
  KSET_WATER_W: 0.45, KSET_MIN: 0.32, FROST_C: -2.0,
  PLANT_DOY: 130,                                                // ~May 10
  DAMP: 0.22,                                                    // weather-index damping (validated; re-confirmed 2026-08-08, see below)
  // ── CEILING TIGHTENED 1.25 -> 1.12 (2026-08-08) ──────────────────────────────
  // The raw index ratio (act/climatology) is heavily OVERDISPERSED: across 2021-25
  // WNY county-years it spans 0.264 to 2.706, mean 1.493 in 2021 and 0.399 in 2025.
  // DAMP=0.22 is really a variance shrink on that, and the CEILING is what stops the
  // tail. At 1.25 the model was allowed to predict a county 25% over its expected
  // yield as a routine outcome — but the OBSERVED NASS/RMA-expected ratio over 72
  // county-years has p95 = 1.196 and an all-time max of 1.247. 1.25 was the record,
  // offered every year. That is what put 2023 Niagara at 208 (actual 168, index ratio
  // 2.71) and 2023 Orleans at 222 (actual 186, ratio 2.65).
  // ⚠ THE FIRST VERSION OF THIS COMMENT OVERSTATED THE CASE. Corrected 2026-08-09
  // after backfilling gridMET 2015-2020 (see below) took the sample from 5 weather
  // years to 11. What actually holds up:
  //   full 11 yrs, 138 county-years, olympic-7 anchor:
  //     CEIL 1.25 -> MAE 14.10   CEIL 1.12 -> MAE 13.78   CEIL 1.08 -> 13.90
  //   so 1.12 is the best of the three, but by ~0.3 bu, NOT the ~1.0 first claimed.
  // The original 12.04 -> 11.07 was measured on 2021-2025 only AND the ceiling was
  // chosen by sweeping those same years — selection on the test set. On the 66
  // county-years of 2016-2020, which were genuinely unseen when 1.12 was picked,
  // the OLD 1.25 was BETTER (16.79 vs 17.33): that window under-predicts badly
  // (bias -14), and tightening a ceiling only helps when you are running hot.
  // Keep 1.12 — it wins on the fullest record and is never worst — but treat it as
  // a marginal call inside noise, not a validated fix.
  // Harness: c:/tmp/mech11*.mjs, gridmet_backfill.mjs.
  //
  // THREE THINGS WERE TESTED AND REJECTED — do not redo them:
  //  1. An explicit WATERLOGGING arm (saturation store + anoxia, stage-weighted
  //     early per Ren et al. — V3/V6 lose 20-32% vs 8-18% at tasseling). It FIRES
  //     (7.6 waterlogged county-days/yr) but changes MAE 10.91 -> 10.94. Nil.
  //     Why: unlike the soil-water bucket, THIS model reads solar radiation, and a
  //     wet year is a cloudy year — the srad channel already carries the penalty.
  //     The bucket needed a wet arm precisely because it has no radiation term.
  //     RE-TESTED 2026-08-09 on 11 years, now INCLUDING 2019 — NY's big wet /
  //     prevent-plant year, the excess-moisture case the 5-year sample lacked
  //     entirely. Still nil: WET_K 0 / .15 / .30 / .50 gives MAE 13.93 / 13.94 /
  //     13.95 / 13.97. It is not a thin-evidence problem. Stop revisiting this.
  //  2. RMA `expected_yield` as the anchor — this one was WRONG to dismiss. On 5
  //     years it looked like noise (~0.2 bu). On 11 years it is the single best
  //     supported change in the whole model, and it wins on every honest view:
  //       anchor alone (no weather): 16.53 vs 18.08 for olympic-7
  //       leave-one-YEAR-out, params selected out-of-fold: 14.08 vs 14.62
  //       with an out-of-fold basis scale: 12.69 vs 14.36
  //     Reason it wins: the olympic-7 anchor is a FLAT trailing mean with no trend,
  //     so it lags genetic gain and inherits whatever crash sits in its 7-yr window
  //     (2016 was 125 bu; it dragged the anchor down for years). Measured bias by
  //     year: 2017 -25, 2018 -17, 2020 -12. RMA's expected yield is a published,
  //     smooth, actuarially-set trend and carries none of that. It also structurally
  //     fixes thin-history counties — Orleans has 6 NASS years and the olympic trim
  //     of 3 values returns a single number. NOT YET WIRED: it needs expected_yield
  //     plumbed into miComputeYield. This is the next change to make.
  //     (Note the basis difference: RMA expected is insured-acre, NASS is all-acre.)
  //  3. A per-county debias (ratio or offset, leave-one-year-out): 14.01 / 14.35 vs
  //     12.04. With 4-5 observations per county the correction is pure noise.
  // ── CEILING 1.12 -> 1.08 (2026-08-13) ────────────────────────────────────────
  // Kevin flagged Orleans 2026 at 206.6 — a record call in a county running +96%
  // silk/fill rain. Chasing it through the WET arm failed (fitted and rejected a
  // third time, see the note above). The actual defect was in how the ceiling was
  // being judged: every prior sweep optimised POOLED MAE, and pooling hides this.
  //
  // Split the 138 county-years by regime, at the shipped 0.22/1.12:
  //        PEGGED at the ceiling  n=13   bias +6.86 (vs NASS)  +7.35 (vs RMA final)
  //        not pegged             n=125  bias -6.71            -8.28
  // The model runs ~7 bu HOT exactly where it clips and ~7 bu COLD everywhere
  // else. Pooled MAE is dominated by the 125 cold rows, so the optimiser raises
  // the ceiling to offset THEM and averages the hot tail away — which is why 1.12
  // looked defensible and why "tighten the clip" kept reading as noise.
  //
  // At 1.08 the pegged bias goes +6.86 -> -1.74 (NASS) and +7.35 -> -3.16 (RMA
  // final), while overall MAE holds (13.43 -> 13.41) or improves on the basis that
  // actually matches the anchor (16.25 -> 15.98). Honest leave-one-year-out picks
  // 1.08 in 8 of 10 folds against NASS and 8 of 9 against RMA final — it is the
  // out-of-fold choice, not a full-sample argmin.
  //
  // 1.10 is the near-tie (pegged bias +3.74/+2.16, best pegged MAE 7.12). 1.08 was
  // taken over it because LOYO prefers it and because a slightly COLD tail is the
  // right error direction here: the model has no penalty for excess water, so in a
  // wet year the tail is where it is least trustworthy, and these numbers feed a
  // pre-harvest sell ceiling.
  //
  // Live effect — only counties that were clipping move (12 of 16 unchanged):
  //   Orleans 206.5 -> 199.1, Niagara 189.3 -> 182.5, Monroe 188.7 -> 185.5.
  // Harness: c:/tmp/mech11e.mjs (wet arm), mech11f.mjs (basis), ceil108.mjs.
  FACTOR_FLOOR: 0.70, FACTOR_CEIL: 1.08,
  // ── THE LINE + CLAMP BECAME A CURVE (2026-08-15) ─────────────────────────────
  // Kevin: "I dont want this capped thing. I just want a good model." He is right,
  // and the ceiling sweeps above were treating a symptom. The clamp was never the
  // defect — the STRAIGHT LINE under it was.
  //
  // Asked the data what shape it wants: for each of the 138 county-years, the
  // factor the model would have NEEDED is target / anchor. Binned against the raw
  // index (NASS target, shipped 0.22/1.08 alongside):
  //     index 0.40  n=36   needed 0.873   shipped 0.868   +0.005
  //     index 0.70  n=25   needed 1.022   shipped 0.934   +0.089
  //     index 0.99  n=13   needed 1.118   shipped 0.998   +0.120
  //     index 1.36  n=25   needed 1.094   shipped 1.070   +0.024
  //     index 1.69  n=13   needed 1.075   shipped 1.080   -0.005
  //     index 2.68  n= 2   needed 1.029   shipped 1.080   -0.051
  // The response is essentially FLAT at ~1.07-1.12 from index 0.9 all the way to
  // 2.68, and falls off below. So 1.08 was the RIGHT plateau — reached far too
  // late. `1 + 0.22*(idx-1)` does not get there until index 1.364, leaving the
  // 0.6-1.2 band (where most county-years live) 0.09-0.12 light. That gap IS the
  // -6 to -8 bu systematic cold bias the ceiling sweeps kept measuring and could
  // not fix, because a clamp can only ever change the tail.
  //
  //   factor = M + S * tanh( K * ln(index / R0) )
  //
  // saturates at M±S with no discontinuity, so nothing is ever "capped" and the
  // plateau is reached where the data says it should be.
  //
  // SELECTED OUT OF FOLD. The 1.12 ceiling was once chosen by sweeping the same
  // years it was scored on; this was not. Leave-one-YEAR-out, params refitted on
  // 9 years and scored on the held-out one:
  //     NASS       clamp MAE 13.41 bias -6.37  ->  curve 12.69 bias +1.17 (wins 7/10 folds)
  //     RMA final  clamp MAE 15.98 bias -7.70  ->  curve 14.93 bias +0.05 (wins 5/9 folds)
  //
  // Two candidate parameter sets were fitted, one per target, and each scored
  // against BOTH bases so the choice was not made on the basis it was fitted to.
  // These (fitted on RMA final, the basis the anchor is on) beat the clamp on
  // every metric on every basis — including the TAIL, which is the part the
  // ceiling existed to protect:
  //     on NASS       MAE 13.41->11.81  bias -6.37->+0.36  tail bias -1.74->-1.21
  //     on RMA final  MAE 15.98->13.89  bias -7.70->-0.44  tail bias -3.16->-2.62
  // The NASS-fitted set scored marginally better on NASS MAE (11.57) but runs a
  // HOT tail (+2.57). Rejected on the principle already recorded above for
  // choosing 1.08 over 1.10: this model has no excess-water penalty, so the wet
  // tail is where it is least trustworthy, and these numbers feed a pre-harvest
  // sell ceiling. A slightly cold tail is the right error direction.
  //
  // Live CY2026: the counties that were clipping barely move (Orleans 199.1 ->
  // 200.0, Niagara 182.5 -> 183.4, Genesee 176.0 -> 176.8) — the plateau was
  // right. The MID-RANGE counties are what come up, which is the bias being
  // removed: Livingston 190.6 -> 196.9, Wyoming 132.6 -> 140.4.
  //
  // DAMP / FACTOR_FLOOR / FACTOR_CEIL are no longer read on the corn path. They
  // stay because MI_SOY mirrors this shape and the Field Pulse path branches on
  // crop, and because deleting the numbers would delete the record of what the
  // clamp was when the notes above were written.
  // Harness: c:/tmp/resp_shape.mjs (shape), resp_fit.mjs (LOYO), resp_pick.mjs.
  RESP_M: 0.939, RESP_S: 0.148, RESP_K: 1.875, RESP_R0: 0.483,
  // ── HOW MUCH THE INDEX KNOWS YET (2026-08-16) ────────────────────────────────
  // DAMP was applied at full strength from the first day of the season. It should
  // not be: the mid-season index is observed-to-date plus climatology fill, and
  // early on that is mostly climatology. Measured on 10 years x 6 as-of dates by
  // recomputing the index with the season truncated (c:/tmp/asof_rows.mjs, which
  // truncates the daily map fed to the SAME cellIndex the page runs), correlation
  // between the as-of index and the finished-season index:
  //
  //     Jun 15  Jul 1  Jul 15  Aug 1  Aug 15  Sep 1
  //      0.085  0.393   0.717  0.894   0.944  0.987
  //
  // In mid-June the number the model acts on is very nearly uncorrelated with how
  // the season ends, and the model was weighting it exactly as hard as it weights
  // the September index. Sweeping DAMP per as-of date agrees and does not care
  // which target you use: the optimum is 0.00 on Jun 15 on BOTH NASS and RMA
  // final, reaching ~0.20 by August.
  //
  // The weight is the CORRELATION ITSELF, not a curve fitted to yields — it is
  // measured on weather alone, so using it is not selection on the thing being
  // predicted. DAMP_eff(doy) = DAMP x INFO_W(doy).
  //
  // Leave-one-YEAR-out, in-season dates, weights recomputed per fold:
  //     NASS       flat 0.22  MAE 15.53 bias -5.39  ->  15.34 / -4.32
  //     RMA final  flat 0.22  MAE 17.30 bias -7.27  ->  16.83 / -6.27
  // Better on both metrics on both bases, and the gain sits where the theory says
  // it should: Jun 15 MAE 19.51 -> 18.07 (RMA), while Aug 15 barely moves
  // (15.57 -> 15.45). So this does NOT disturb the late-season number.
  //
  // It is a partial answer on choppiness, and only partial: mean absolute move
  // between consecutive as-of dates goes 3.44 -> 3.21 bu. Most in-season movement
  // is the index genuinely changing, not the weighting.
  INFO_W: [[166, 0.085], [182, 0.393], [196, 0.717], [213, 0.894], [227, 0.944], [244, 0.987], [258, 1.0]],
};
// Linear interpolation over INFO_W; flat outside the measured range (never
// extrapolate a correlation). Before the first knot it holds 0.085 rather than
// dropping to 0 — the measurement starts at Jun 15 and says nothing about May.
function miInfoWeight(doy, C) {
  const K = (C && C.INFO_W) || MI_CORN.INFO_W;
  if (doy == null || !Number.isFinite(doy)) return 1;
  if (doy <= K[0][0]) return K[0][1];
  if (doy >= K[K.length - 1][0]) return K[K.length - 1][1];
  for (let i = 1; i < K.length; i++) {
    if (doy <= K[i][0]) {
      const [d0, w0] = K[i - 1], [d1, w1] = K[i];
      return w0 + (w1 - w0) * ((doy - d0) / (d1 - d0));
    }
  }
  return 1;
}
// The saturating weather response. Returns null for a null/non-positive index so
// a missing ratio stays missing rather than becoming 1.0 — a not-applicable
// sentinel must never be reported as a measurement.
function miCornResponse(ratio, C) {
  if (ratio == null || !(ratio > 0)) return null;
  const K = C || MI_CORN;
  return K.RESP_M + K.RESP_S * Math.tanh(K.RESP_K * Math.log(ratio / K.RESP_R0));
}
const miCornC2F = (c) => (c * 9) / 5 + 32;
const miCornClamp = (x, lo, hi) => Math.max(lo, Math.min(hi, x));
function miCornGdd(tmaxC, tminC) {
  if (tmaxC == null || tminC == null) return 0;
  const tx = Math.min(miCornC2F(tmaxC), 86);
  const tn = miCornClamp(miCornC2F(tminC), 50, 86);
  return Math.max(0, (tx + tn) / 2 - 50);
}
function miCornPet(sradWm2, tmeanC) {
  if (sradWm2 == null) return 2.5;
  const Rs = sradWm2 * 0.0864, Rn = 0.6 * Rs, lambda = 2.45;
  const T = tmeanC == null ? 18 : tmeanC;
  const slope = (4098 * (0.6108 * Math.exp((17.27 * T) / (T + 237.3)))) / Math.pow(T + 237.3, 2);
  const gamma = 0.066, alpha = 1.26;
  return Math.max(0, alpha * (slope / (slope + gamma)) * (Rn / lambda));
}
// daily: parallel arrays keyed by index { doys, tmax_c, tmin_c, precip_mm, srad_wm2, vpd_kpa }
function miCornSimulate(daily, awc, plantDoy, C) {
  const n = daily.doys.length;
  const TAW = miCornClamp((awc || 12) * C.AWC_TO_MM, C.TAW_MIN, C.TAW_MAX);
  let sw = C.START_FRAC * TAW;
  let gdd = 0, gddEmerge = null, gddSilk = null, started = false;
  let earlyStressInteg = 0, earlyStressDays = 0;
  let silkHeatDays = 0, silkVpdDays = 0, silkKsSum = 0, silkKsN = 0, silkGrowthSum = 0, silkGrowthN = 0;
  let fillKsSum = 0, fillKsN = 0, matured = false, frostKill = false;
  for (let i = 0; i < n; i++) {
    if (daily.doys[i] < plantDoy) continue;
    started = true;
    const tmax = daily.tmax_c[i], tmin = daily.tmin_c[i];
    const tmean = tmax != null && tmin != null ? (tmax + tmin) / 2 : null;
    gdd += miCornGdd(tmax, tmin);
    if (gddEmerge == null && gdd >= C.GDD_EMERGE) gddEmerge = gdd;
    const emerged = gddEmerge != null;
    const gddSinceEmerge = emerged ? gdd - gddEmerge : 0;
    const rootFrac = emerged ? miCornClamp(C.ROOT_START_FRAC + (1 - C.ROOT_START_FRAC) * (gddSinceEmerge / C.ROOT_GDD_FULL), C.ROOT_START_FRAC, 1) : C.ROOT_START_FRAC;
    const TAWc = TAW * rootFrac, RAW = C.P_DEPLETE * TAWc;
    let lai = 0;
    if (emerged) {
      if (gddSilk == null) lai = C.LAI_MAX * miCornClamp(gddSinceEmerge / (C.GDD_SILK - C.GDD_EMERGE), 0, 1);
      else { const fillFrac = miCornClamp((gdd - gddSilk) / (C.GDD_MATURE - C.GDD_SILK), 0, 1); lai = C.LAI_MAX * (1 - (1 - C.SENESCE_FRAC) * fillFrac); }
      lai *= miCornClamp(1 - 0.25 * earlyStressInteg / Math.max(1, earlyStressDays), 0.6, 1);
    }
    const fInt = 1 - Math.exp(-C.K_BEER * lai);
    const pet = miCornPet(daily.srad_wm2[i], tmean);
    const vpd = daily.vpd_kpa[i];
    const vpdMult = vpd != null ? 1 + C.VPD_DEMAND_K * Math.max(0, vpd - C.VPD_REF) : 1;
    const kc = 0.3 + (C.KC_MAX - 0.3) * fInt;
    const etcPot = pet * kc * vpdMult;
    const depletion = TAWc - sw;
    const Ks = depletion <= RAW ? 1 : miCornClamp((TAWc - depletion) / (TAWc - RAW), 0, 1);
    sw += (daily.precip_mm[i] || 0) - etcPot * Ks;
    if (sw > TAWc) sw = TAWc; if (sw < 0) sw = 0;
    const ipar = C.PAR_FRAC * (daily.srad_wm2[i] != null ? daily.srad_wm2[i] * 0.0864 : 0) * fInt;
    const heatPhoto = tmax != null && tmax > C.HEAT_PHOTO_T ? miCornClamp(1 - C.HEAT_PHOTO_SLOPE * (tmax - C.HEAT_PHOTO_T), 0.5, 1) : 1;
    const coldPhoto = tmean != null && tmean < C.COLD_PHOTO_T ? miCornClamp(1 - C.COLD_PHOTO_SLOPE * (C.COLD_PHOTO_T - tmean), 0.4, 1) : 1;
    const dBio = emerged && !matured && !frostKill ? C.RUE * ipar * Ks * heatPhoto * coldPhoto : 0;
    if (emerged && gddSilk == null) { earlyStressInteg += 1 - Ks; earlyStressDays += 1; }
    if (gddSilk == null && gdd >= C.GDD_SILK) gddSilk = gdd;
    if (gddSilk != null && Math.abs(gdd - C.GDD_SILK) <= C.SILK_GDD_WIN) {
      if (tmax != null && tmax > C.KSET_HEAT_T) silkHeatDays += 1;
      if (vpd != null && vpd > C.KSET_VPD_T) silkVpdDays += 1;
      silkKsSum += Ks; silkKsN += 1; silkGrowthSum += dBio; silkGrowthN += 1;
    }
    if (gddSilk != null && !matured) { fillKsSum += Ks; fillKsN += 1; }
    if (gdd >= C.GDD_MATURE) matured = true;
    if (gddSilk != null && !matured && tmin != null && tmin <= C.FROST_C) frostKill = true;
  }
  if (!started || gddSilk == null) return null;
  const growthRate = silkGrowthN ? silkGrowthSum / silkGrowthN : 0;
  const supplyTerm = miCornClamp(growthRate / C.KSET_GROWTH_REF, 0, 1);
  const silkKs = silkKsN ? silkKsSum / silkKsN : 1;
  const kSet = miCornClamp((1 - C.KSET_GROWTH_W) + C.KSET_GROWTH_W * supplyTerm - C.KSET_HEAT_PER_DAY * silkHeatDays - C.KSET_VPD_PER_DAY * silkVpdDays - C.KSET_WATER_W * (1 - silkKs), C.KSET_MIN, 1);
  const fillKs = fillKsN ? fillKsSum / fillKsN : 1;
  return { kSet, fillKs, index: kSet * fillKs };
}
// Build the season (DOY 91..305) for one cell: observed → forecast → climatology
// fill; run the model on the actual-merged series AND on pure climatology (the
// "normal"), returning the weather index ratio. dailyEntry keyed by YYYY-MM-DD;
// climoDoyEntry keyed by fixed-2021 doy0 (carries native c/mm/srad/vpd).
function miCornCellIndex(dailyEntry, climoDoyEntry, awc, year, C) {
  if (!climoDoyEntry) return null;
  const md2doy0 = (mm, dd) => Math.floor((Date.UTC(2021, mm - 1, dd) - Date.UTC(2021, 0, 1)) / 86400000);
  const pad = (n) => String(n).padStart(2, "0");
  const act = { doys: [], tmax_c: [], tmin_c: [], precip_mm: [], srad_wm2: [], vpd_kpa: [] };
  const nrm = { doys: [], tmax_c: [], tmin_c: [], precip_mm: [], srad_wm2: [], vpd_kpa: [] };
  for (let doy = 91; doy <= 305; doy++) {
    const dt = new Date(Date.UTC(year, 0, 1) + (doy - 1) * 86400000);
    const mm = dt.getUTCMonth() + 1, dd = dt.getUTCDate();
    const cl = climoDoyEntry[md2doy0(mm, dd)];
    if (!cl) continue;
    // normal series = climatology every day
    nrm.doys.push(doy); nrm.tmax_c.push(cl.tmax_c); nrm.tmin_c.push(cl.tmin_c); nrm.precip_mm.push(cl.precip_mm); nrm.srad_wm2.push(cl.srad_wm2); nrm.vpd_kpa.push(cl.vpd_kpa);
    // actual series = observed (full) → forecast (climo srad/vpd) → climatology
    const obs = dailyEntry && dailyEntry[`${year}-${pad(mm)}-${pad(dd)}`];
    let tx, tn, pr, sr, vp;
    if (obs && obs.tmax_c != null && !obs.is_forecast) { tx = obs.tmax_c; tn = obs.tmin_c; pr = obs.precip_mm; sr = obs.srad_wm2 != null ? obs.srad_wm2 : cl.srad_wm2; vp = obs.vpd_kpa != null ? obs.vpd_kpa : cl.vpd_kpa; }
    else if (obs && obs.tmax_c != null && obs.is_forecast) { tx = obs.tmax_c; tn = obs.tmin_c; pr = obs.precip_mm; sr = cl.srad_wm2; vp = cl.vpd_kpa; }
    else { tx = cl.tmax_c; tn = cl.tmin_c; pr = cl.precip_mm; sr = cl.srad_wm2; vp = cl.vpd_kpa; }
    act.doys.push(doy); act.tmax_c.push(tx); act.tmin_c.push(tn); act.precip_mm.push(pr); act.srad_wm2.push(sr); act.vpd_kpa.push(vp);
  }
  const a = miCornSimulate(act, awc, C.PLANT_DOY, C);
  const nn = miCornSimulate(nrm, awc, C.PLANT_DOY, C);
  if (!a || !nn || !nn.index) return null;
  return { idx: a.index, norm: nn.index };
}

// ── FIELD PULSE: per-field, current-state read for the Field Mapper "Pulse" ──
// Runs the SAME validated corn engine (miCornSimulate physics) on one field's
// nearest cell, but also captures the state AS OF the last observed day — current
// growth stage, soil-water status, and dated stress events — for a live, factual
// "where is this field right now" view. The end-of-season index (vs the cell's own
// climatology) drives the directional "tracking vs APH" gauge.
const miPulseDoy = (iso) => { const d = new Date(iso + "T00:00:00Z"); return Math.floor((d - Date.UTC(d.getUTCFullYear(), 0, 1)) / 86400000) + 1; };
// Stage sequence per crop as a TABLE. These are the SAME thresholds and the SAME
// label strings the stage functions have always used — expressed once so the text
// label and the <StageRail> can never drift apart. (The grain ladder learned this
// the hard way: duplicated math meant the same board gave two different answers.)
// `to` = the GDD at which the NEXT stage begins; the last stage is open-ended.
// MI_CORN / MI_SOY are read lazily inside the body, so definition order is free.
const MI_STAGE_TABLE = (crop) => {
  if (String(crop || "").toUpperCase().startsWith("SOY")) {
    const C = MI_SOY;
    return { crop:"SOYBEANS", mature:C.GDD_MATURE, stages:[
      { label:"pre-emergence",      short:"Pre",   to:C.GDD_EMERGE },
      { label:"vegetative",         short:"Veg",   to:C.GDD_R1 },
      { label:"flowering (R1–R2)",  short:"R1–R2", to:C.GDD_R3 },
      { label:"pod set (R3–R4)",    short:"R3–R4", to:C.GDD_R5 },
      { label:"seed fill (R5)",     short:"R5",    to:C.GDD_R6 },
      { label:"seed fill (R6)",     short:"R6",    to:C.GDD_MATURE },
      { label:"mature (R7+)",       short:"R7+",   to:Infinity },
    ]};
  }
  const C = MI_CORN;
  return { crop:"CORN", mature:C.GDD_MATURE, stages:[
    { label:"pre-emergence",         short:"Pre",        to:C.GDD_EMERGE },
    { label:"vegetative (early)",    short:"Early V",    to:900 },
    { label:"vegetative (late)",     short:"Late V",     to:1180 },
    { label:"silking / pollination", short:"Silk",       to:1520 },
    { label:"grain fill",            short:"Grain fill", to:2100 },
    { label:"dent / maturing",       short:"Dent",       to:C.GDD_MATURE },
    { label:"mature (black layer)",  short:"Mature",     to:Infinity },
  ]};
};
// Only corn and soybeans have a calibrated GDD phenology here. Wheat's model uses a
// different clock — do NOT fall through to the corn table for it.
const MI_STAGE_HAS_TABLE = (crop) => {
  const c = String(crop || "").toUpperCase();
  return c.startsWith("SOY") || c.startsWith("CORN");
};
const MI_STAGE_AT = (gdd, crop) => {
  const table = MI_STAGE_TABLE(crop);
  const i = table.stages.findIndex(s => gdd < s.to);
  return { table, idx: i < 0 ? table.stages.length - 1 : i };
};
const MI_STAGE_LABEL = (gdd, planted) => {
  if (!planted) return "pre-emergence";
  const { table, idx } = MI_STAGE_AT(gdd, "CORN");
  return table.stages[idx].label;
};
// Extended sim: identical computation to miCornSimulate, plus current-state capture
// (at lastObsDoy) and stress-event tally. Returns null if it never reaches silking.
function miCornPulseSim(daily, awc, plantDoy, lastObsDoy, C) {
  const n = daily.doys.length;
  const TAW = miCornClamp((awc || 12) * C.AWC_TO_MM, C.TAW_MIN, C.TAW_MAX);
  let sw = C.START_FRAC * TAW;
  let gdd = 0, gddEmerge = null, gddSilk = null, started = false;
  let earlyStressInteg = 0, earlyStressDays = 0;
  let silkHeatDays = 0, silkVpdDays = 0, silkKsSum = 0, silkKsN = 0, silkGrowthSum = 0, silkGrowthN = 0;
  let fillKsSum = 0, fillKsN = 0, matured = false, frostKill = false;
  let cur = null, dryRun = 0, dryMax = 0, dryEndDoy = null, heatPollDays = 0, frostDoy = null;
  for (let i = 0; i < n; i++) {
    if (daily.doys[i] < plantDoy) continue;
    started = true;
    const doy = daily.doys[i];
    const tmax = daily.tmax_c[i], tmin = daily.tmin_c[i];
    const tmean = tmax != null && tmin != null ? (tmax + tmin) / 2 : null;
    gdd += miCornGdd(tmax, tmin);
    if (gddEmerge == null && gdd >= C.GDD_EMERGE) gddEmerge = gdd;
    const emerged = gddEmerge != null;
    const gddSinceEmerge = emerged ? gdd - gddEmerge : 0;
    const rootFrac = emerged ? miCornClamp(C.ROOT_START_FRAC + (1 - C.ROOT_START_FRAC) * (gddSinceEmerge / C.ROOT_GDD_FULL), C.ROOT_START_FRAC, 1) : C.ROOT_START_FRAC;
    const TAWc = TAW * rootFrac, RAW = C.P_DEPLETE * TAWc;
    let lai = 0;
    if (emerged) {
      if (gddSilk == null) lai = C.LAI_MAX * miCornClamp(gddSinceEmerge / (C.GDD_SILK - C.GDD_EMERGE), 0, 1);
      else { const fillFrac = miCornClamp((gdd - gddSilk) / (C.GDD_MATURE - C.GDD_SILK), 0, 1); lai = C.LAI_MAX * (1 - (1 - C.SENESCE_FRAC) * fillFrac); }
      lai *= miCornClamp(1 - 0.25 * earlyStressInteg / Math.max(1, earlyStressDays), 0.6, 1);
    }
    const fInt = 1 - Math.exp(-C.K_BEER * lai);
    const pet = miCornPet(daily.srad_wm2[i], tmean);
    const vpd = daily.vpd_kpa[i];
    const vpdMult = vpd != null ? 1 + C.VPD_DEMAND_K * Math.max(0, vpd - C.VPD_REF) : 1;
    const kc = 0.3 + (C.KC_MAX - 0.3) * fInt;
    const etcPot = pet * kc * vpdMult;
    const depletion = TAWc - sw;
    const Ks = depletion <= RAW ? 1 : miCornClamp((TAWc - depletion) / (TAWc - RAW), 0, 1);
    sw += (daily.precip_mm[i] || 0) - etcPot * Ks;
    if (sw > TAWc) sw = TAWc; if (sw < 0) sw = 0;
    const ipar = C.PAR_FRAC * (daily.srad_wm2[i] != null ? daily.srad_wm2[i] * 0.0864 : 0) * fInt;
    const heatPhoto = tmax != null && tmax > C.HEAT_PHOTO_T ? miCornClamp(1 - C.HEAT_PHOTO_SLOPE * (tmax - C.HEAT_PHOTO_T), 0.5, 1) : 1;
    const coldPhoto = tmean != null && tmean < C.COLD_PHOTO_T ? miCornClamp(1 - C.COLD_PHOTO_SLOPE * (C.COLD_PHOTO_T - tmean), 0.4, 1) : 1;
    const dBio = emerged && !matured && !frostKill ? C.RUE * ipar * Ks * heatPhoto * coldPhoto : 0;
    if (emerged && gddSilk == null) { earlyStressInteg += 1 - Ks; earlyStressDays += 1; }
    if (gddSilk == null && gdd >= C.GDD_SILK) gddSilk = gdd;
    const inSilkWin = gddSilk != null && Math.abs(gdd - C.GDD_SILK) <= C.SILK_GDD_WIN;
    if (inSilkWin) {
      if (tmax != null && tmax > C.KSET_HEAT_T) silkHeatDays += 1;
      if (vpd != null && vpd > C.KSET_VPD_T) silkVpdDays += 1;
      silkKsSum += Ks; silkKsN += 1; silkGrowthSum += dBio; silkGrowthN += 1;
    }
    if (gddSilk != null && !matured) { fillKsSum += Ks; fillKsN += 1; }
    if (gdd >= C.GDD_MATURE) matured = true;
    if (gddSilk != null && !matured && tmin != null && tmin <= C.FROST_C) frostKill = true;
    // ── observed-window-only tallies for the display (skip forecast/climo-fill) ──
    if (lastObsDoy != null && doy <= lastObsDoy) {
      if (miCornC2F(tmax) > 90 && (inSilkWin || (gddSilk == null && gdd > C.GDD_SILK - 250))) heatPollDays += 1;
      if (emerged && depletion > RAW) { dryRun += 1; if (dryRun > dryMax) { dryMax = dryRun; dryEndDoy = doy; } } else dryRun = 0;
      // Spring frost only damages corn once the growing point is above ground
      // (~V6, gddSinceEmerge > 300). A light frost at/near emergence — growing point
      // still below the soil — doesn't hurt it, so don't flag it. Also require a real
      // freeze (≤30°F), not just ≤32°F.
      if (emerged && tmin != null && tmin <= -1.1 && gddSinceEmerge > 300 && doy < 175) frostDoy = doy;
      cur = { gdd, sw, TAWc, Ks, swPct: TAWc > 0 ? sw / TAWc : 1, doy, depletion, RAW };
    }
  }
  if (!started) return null;
  const growthRate = silkGrowthN ? silkGrowthSum / silkGrowthN : 0;
  const supplyTerm = miCornClamp(growthRate / C.KSET_GROWTH_REF, 0, 1);
  const silkKs = silkKsN ? silkKsSum / silkKsN : 1;
  const kSet = gddSilk != null ? miCornClamp((1 - C.KSET_GROWTH_W) + C.KSET_GROWTH_W * supplyTerm - C.KSET_HEAT_PER_DAY * silkHeatDays - C.KSET_VPD_PER_DAY * silkVpdDays - C.KSET_WATER_W * (1 - silkKs), C.KSET_MIN, 1) : null;
  const fillKs = fillKsN ? fillKsSum / fillKsN : 1;
  return { kSet, fillKs, index: kSet != null ? kSet * fillKs : null, cur,
    silkHeatDays, frostKill, dryMax, dryEndDoy, heatPollDays, frostDoy };
}
// Assemble one field's season (observed → forecast → climatology fill) from raw DB
// rows and run the pulse sim on actual + on the cell's own climatology (the normal).
// dailyRows: wny_grid_weather_daily for the cell; climoRows: wny_grid_climatology.
// fieldPrecip (optional): { [obs_date]: precip_mm } from field_precip_daily — the field's
// OWN native ~1 km MRMS rainfall. When supplied it overrides the cell precip on observed
// days, so two fields in the same model cell diverge by their actual rain (the whole point
// of Field Pulse). Temperature/radiation/VPD stay per-cell (1 km temp is ~uniform inside a
// cell; MRMS only differentiates precip). Forecast + pre-2025 days fall back to the cell.
function buildFieldPulse(dailyRows, climoRows, awc, plantDate, crop, fieldPrecip) {
  const isSoy = /SOY/.test(String(crop || "").toUpperCase());
  const C = isSoy ? MI_SOY : MI_CORN;
  const climo = {};
  for (const r of (climoRows || [])) climo[r.day_of_year] = r;
  if (!Object.keys(climo).length) return null;
  const byDate = {};
  let lastObsDoy = null, year = null;
  for (const r of (dailyRows || [])) {
    byDate[r.obs_date] = r;
    const y = Number(r.obs_date.slice(0, 4)); if (year == null) year = y;
    if (!r.is_forecast && r.tmax_c != null) { const d = miPulseDoy(r.obs_date); if (lastObsDoy == null || d > lastObsDoy) lastObsDoy = d; }
  }
  if (year == null) year = new Date().getUTCFullYear();
  // No real planting date → don't fabricate one. Without it we can't place the
  // phenology, and NAU corn with acres but no planting date is frequently
  // prevent-plant (never grown). Flag it so the UI says so honestly.
  if (!(plantDate && /^\d{4}-\d{2}-\d{2}/.test(plantDate))) return { noPlantDate: true };
  const plantDoy = miPulseDoy(plantDate.slice(0, 10));
  const pad = (x) => String(x).padStart(2, "0");
  const act = { doys: [], tmax_c: [], tmin_c: [], precip_mm: [], srad_wm2: [], vpd_kpa: [] };
  const nrm = { doys: [], tmax_c: [], tmin_c: [], precip_mm: [], srad_wm2: [], vpd_kpa: [] };
  let fpDays = 0;   // count of observed days driven by the field's OWN 1 km rainfall
  for (let doy = 91; doy <= 305; doy++) {
    const dt = new Date(Date.UTC(year, 0, 1) + (doy - 1) * 86400000);
    const cl = climo[doy]; if (!cl) continue;
    nrm.doys.push(doy); nrm.tmax_c.push(cl.tmax_c); nrm.tmin_c.push(cl.tmin_c); nrm.precip_mm.push(cl.precip_mm); nrm.srad_wm2.push(cl.srad_wm2); nrm.vpd_kpa.push(cl.vpd_kpa);
    const iso = `${year}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
    const o = byDate[iso];
    let tx, tn, pr, sr, vp;
    if (o && o.tmax_c != null) { tx = o.tmax_c; tn = o.tmin_c; if (fieldPrecip && fieldPrecip[iso] != null) { pr = fieldPrecip[iso]; if (!o.is_forecast) fpDays++; } else { pr = o.precip_mm; } sr = o.srad_wm2 != null ? o.srad_wm2 : cl.srad_wm2; vp = o.vpd_kpa != null ? o.vpd_kpa : cl.vpd_kpa; }
    else { tx = cl.tmax_c; tn = cl.tmin_c; pr = cl.precip_mm; sr = cl.srad_wm2; vp = cl.vpd_kpa; }
    act.doys.push(doy); act.tmax_c.push(tx); act.tmin_c.push(tn); act.precip_mm.push(pr); act.srad_wm2.push(sr); act.vpd_kpa.push(vp);
  }
  const lat = 42.8; // NY book latitude (photoperiod gate for soy; negligible variation across the book)
  const a  = isSoy ? miSoySim(act, awc, plantDoy, lastObsDoy, C, lat) : miCornPulseSim(act, awc, plantDoy, lastObsDoy, C);
  const nn = isSoy ? miSoySim(nrm, awc, plantDoy, null, C, lat)       : miCornSimulate(nrm, awc, plantDoy, C);
  if (!a) return null;
  const ratio = (a.index != null && nn && nn.index) ? a.index / nn.index : null;
  // CORN ONLY gets the saturating response. This path is shared with SOYBEANS,
  // and MI_SOY carries its own separately-validated DAMP/FLOOR/CEIL (0.22 /
  // 0.72 / 1.22, fitted on a different crop against 8.2 bu MAE on a ~49 bu crop).
  // Handing MI_SOY to miCornResponse would read RESP_* off a constants object
  // that has none and return NaN for every soybean field — a silent one, since
  // nothing downstream type-checks a factor.
  const mechFactor = ratio != null ? miCornClamp(1 + C.DAMP * (ratio - 1), C.FACTOR_FLOOR, C.FACTOR_CEIL) : null;
  // season-to-date actual vs normal (plant → last observed)
  let rainStd = 0, rainNrm = 0, gddStd = 0, gddNrm = 0;
  for (let k = 0; k < act.doys.length; k++) {
    if (act.doys[k] < plantDoy || (lastObsDoy != null && act.doys[k] > lastObsDoy)) continue;
    rainStd += act.precip_mm[k] || 0; gddStd += miCornGdd(act.tmax_c[k], act.tmin_c[k]);
    rainNrm += nrm.precip_mm[k] || 0; gddNrm += miCornGdd(nrm.tmax_c[k], nrm.tmin_c[k]);
  }
  const gddNow = a.cur ? a.cur.gdd : gddStd;
  // Critical period = the yield-determining window each crop is racing toward:
  // corn → silking; soy → seed fill (R5). gddToSilk/silked are kept as the field
  // names the views read, but critLabel tells them which word to show.
  const critGdd = isSoy ? C.GDD_R5 : C.GDD_SILK;
  return {
    plantDoy, lastObsDoy, year, crop: isSoy ? "SOYBEANS" : "CORN",
    stage: isSoy ? MI_SOY_STAGE_LABEL(gddNow, C) : MI_STAGE_LABEL(gddNow, true, C),
    gddNow: Math.round(gddNow), gddToMature: Math.max(0, Math.round(C.GDD_MATURE - gddNow)),
    gddToSilk: gddNow >= critGdd ? 0 : Math.round(critGdd - gddNow), silked: gddNow >= critGdd,
    critLabel: isSoy ? "seed fill" : "silking",
    pctToMature: miCornClamp(gddNow / C.GDD_MATURE, 0, 1),
    rainStdIn: rainStd / 25.4, rainNrmIn: rainNrm / 25.4,
    gddStd: Math.round(gddStd), gddNrm: Math.round(gddNrm),
    soilWaterPct: a.cur ? a.cur.swPct : null,
    // soilKs = the FAO-56 water-stress coefficient (1 = no stress; <1 = transpiration
    // cut). "Stressed" only when Ks drops meaningfully — crossing the readily-available
    // line (depletion > RAW, Ks just under 1) is the crop drawing reserves, NOT stress.
    soilKs: a.cur ? a.cur.Ks : null,
    waterStressed: a.cur ? (a.cur.Ks < 0.70) : false,
    silkHeatDays: isSoy ? a.silkHeatDays : a.heatPollDays, dryMax: a.dryMax, dryEndDoy: a.dryEndDoy, frostDoy: a.frostDoy,
    indexRatio: ratio, mechFactor, fieldPrecipDays: fpDays,
  };
}

// ── SOYBEAN pulse engine — validated mechanistic soy model (tools/yield-model/
// soyModel.js): same FAO-56 water bucket + RUE engine, soy phenology (VE → R1
// bloom → R3 pod → R5/R6 SEED FILL → maturity) with a photoperiod gate on R1.
// Yield is plastic (no hard silk ceiling) → index = podSet × seed-fill water.
// Backtest: grower LOYO book MAE 8.81 → 8.21 bu (+0.60), gain drought-concentrated.
// DAMP kept conservative; farmers see DIRECTION only (8.2 bu MAE on a ~49 bu crop).
const MI_SOY = {
  GDD_EMERGE: 130, GDD_R1: 950, GDD_R3: 1300, GDD_R5: 1750, GDD_R6: 2150, GDD_MATURE: 2500,
  LAI_MAX: 5.0, K_BEER: 0.52, SENESCE_FRAC: 0.50, RUE: 1.05, PAR_FRAC: 0.48,
  AWC_TO_MM: 13.0, TAW_MIN: 60, TAW_MAX: 340, P_DEPLETE: 0.50,
  ROOT_GDD_FULL: 900, ROOT_START_FRAC: 0.35, KC_MAX: 1.15, VPD_DEMAND_K: 0.10, VPD_REF: 0.9, START_FRAC: 0.85,
  HEAT_PHOTO_T: 32, HEAT_PHOTO_SLOPE: 0.030, COLD_PHOTO_T: 12, COLD_PHOTO_SLOPE: 0.03,
  POD_HEAT_T: 33, POD_HEAT_PER_DAY: 0.010, POD_WATER_W: 0.30, POD_GROWTH_REF: 8, POD_GROWTH_W: 0.30, POD_MIN: 0.45,
  FILL_HEAT_T: 32, FROST_C: -2.5, HI_MAX: 0.42,
  PHOTO_CRIT_HR: 14.5, PLANT_DOY: 145, DAMP: 0.22, FACTOR_FLOOR: 0.72, FACTOR_CEIL: 1.22,
};
const miSoyDaylenHr = (latDeg, doy) => {
  const lat = (latDeg * Math.PI) / 180;
  const decl = 0.4093 * Math.sin((2 * Math.PI / 365) * (doy - 81));
  const cosH = -Math.tan(lat) * Math.tan(decl);
  if (cosH >= 1) return 0; if (cosH <= -1) return 24;
  return (24 / Math.PI) * Math.acos(cosH);
};
// Reads the shared MI_STAGE_TABLE (see above) — same thresholds, same strings.
const MI_SOY_STAGE_LABEL = (gdd) => {
  const { table, idx } = MI_STAGE_AT(gdd, "SOYBEANS");
  return table.stages[idx].label;
};

// Core soy sim — index = podSet × fillKs; lastObsDoy!=null also captures current state + stress.
function miSoySim(daily, awc, plantDoy, lastObsDoy, C, lat) {
  const n = daily.doys.length;
  const TAW = miCornClamp((awc || 12) * C.AWC_TO_MM, C.TAW_MIN, C.TAW_MAX);
  let sw = C.START_FRAC * TAW;
  let gdd = 0, gddEmerge = null, gddR1 = null, started = false;
  let earlyStressInteg = 0, earlyStressDays = 0;
  let podHeatDays = 0, podKsSum = 0, podKsN = 0, podGrowthSum = 0, podGrowthN = 0;
  let fillKsSum = 0, fillKsN = 0, matured = false, frostKill = false;
  let cur = null, dryRun = 0, dryMax = 0, dryEndDoy = null, heatDays = 0, frostDoy = null;
  for (let i = 0; i < n; i++) {
    if (daily.doys[i] < plantDoy) continue;
    started = true;
    const doy = daily.doys[i];
    const tmax = daily.tmax_c[i], tmin = daily.tmin_c[i];
    const tmean = tmax != null && tmin != null ? (tmax + tmin) / 2 : null;
    gdd += miCornGdd(tmax, tmin);
    if (gddEmerge == null && gdd >= C.GDD_EMERGE) gddEmerge = gdd;
    const emerged = gddEmerge != null;
    if (gddR1 == null && emerged && gdd >= C.GDD_R1 && miSoyDaylenHr(lat, doy) <= C.PHOTO_CRIT_HR) gddR1 = gdd;
    const gddSinceEmerge = emerged ? gdd - gddEmerge : 0;
    const rootFrac = emerged ? miCornClamp(C.ROOT_START_FRAC + (1 - C.ROOT_START_FRAC) * (gddSinceEmerge / C.ROOT_GDD_FULL), C.ROOT_START_FRAC, 1) : C.ROOT_START_FRAC;
    const TAWc = TAW * rootFrac, RAW = C.P_DEPLETE * TAWc;
    let lai = 0;
    if (emerged) {
      if (gdd < C.GDD_R5) lai = C.LAI_MAX * miCornClamp((gdd - C.GDD_EMERGE) / (C.GDD_R5 - C.GDD_EMERGE), 0, 1);
      else { const f = miCornClamp((gdd - C.GDD_R5) / (C.GDD_MATURE - C.GDD_R5), 0, 1); lai = C.LAI_MAX * (1 - (1 - C.SENESCE_FRAC) * f); }
      lai *= miCornClamp(1 - 0.25 * earlyStressInteg / Math.max(1, earlyStressDays), 0.6, 1);
    }
    const fInt = 1 - Math.exp(-C.K_BEER * lai);
    const pet = miCornPet(daily.srad_wm2[i], tmean);
    const vpd = daily.vpd_kpa[i];
    const vpdMult = vpd != null ? 1 + C.VPD_DEMAND_K * Math.max(0, vpd - C.VPD_REF) : 1;
    const kc = 0.3 + (C.KC_MAX - 0.3) * fInt;
    const etcPot = pet * kc * vpdMult;
    const depletion = TAWc - sw;
    const Ks = depletion <= RAW ? 1 : miCornClamp((TAWc - depletion) / (TAWc - RAW), 0, 1);
    sw += (daily.precip_mm[i] || 0) - etcPot * Ks;
    if (sw > TAWc) sw = TAWc; if (sw < 0) sw = 0;
    const ipar = C.PAR_FRAC * (daily.srad_wm2[i] != null ? daily.srad_wm2[i] * 0.0864 : 0) * fInt;
    const heatPhoto = tmax != null && tmax > C.HEAT_PHOTO_T ? miCornClamp(1 - C.HEAT_PHOTO_SLOPE * (tmax - C.HEAT_PHOTO_T), 0.5, 1) : 1;
    const coldPhoto = tmean != null && tmean < C.COLD_PHOTO_T ? miCornClamp(1 - C.COLD_PHOTO_SLOPE * (C.COLD_PHOTO_T - tmean), 0.4, 1) : 1;
    const dBio = emerged && !matured && !frostKill ? C.RUE * ipar * Ks * heatPhoto * coldPhoto : 0;
    if (emerged && gddR1 == null) { earlyStressInteg += 1 - Ks; earlyStressDays += 1; }
    if (gddR1 != null && gdd < C.GDD_R5) { if (tmax != null && tmax > C.POD_HEAT_T) podHeatDays += 1; podKsSum += Ks; podKsN += 1; podGrowthSum += dBio; podGrowthN += 1; }
    if (gdd >= C.GDD_R5 && !matured) { fillKsSum += Ks; fillKsN += 1; }
    if (gdd >= C.GDD_MATURE) matured = true;
    if (gdd >= C.GDD_R5 && !matured && tmin != null && tmin <= C.FROST_C) frostKill = true;
    if (lastObsDoy != null && doy <= lastObsDoy) {
      if (gdd >= C.GDD_R1 && gdd < C.GDD_MATURE && miCornC2F(tmax) > 90) heatDays += 1;
      if (emerged && depletion > RAW) { dryRun += 1; if (dryRun > dryMax) { dryMax = dryRun; dryEndDoy = doy; } } else dryRun = 0;
      if (gdd >= C.GDD_R5 && !matured && tmin != null && tmin <= -1.1) frostDoy = doy;
      cur = { gdd, sw, TAWc, Ks, swPct: TAWc > 0 ? sw / TAWc : 1, doy, depletion, RAW };
    }
  }
  if (!started) return null;
  const podGrowth = podGrowthN ? podGrowthSum / podGrowthN : 0;
  const podSupply = miCornClamp(podGrowth / C.POD_GROWTH_REF, 0, 1);
  const podKs = podKsN ? podKsSum / podKsN : 1;
  const podSet = gddR1 != null ? miCornClamp((1 - C.POD_GROWTH_W) + C.POD_GROWTH_W * podSupply - C.POD_HEAT_PER_DAY * podHeatDays - C.POD_WATER_W * (1 - podKs), C.POD_MIN, 1) : null;
  const fillKs = fillKsN ? fillKsSum / fillKsN : 1;
  return { kSet: podSet, fillKs, index: podSet != null ? podSet * fillKs : null, cur, silkHeatDays: heatDays, frostKill, dryMax, dryEndDoy, frostDoy };
}

// ── Season Weather Score (0–100, ABSOLUTE) — current year only ───────────
// "How close has the growing weather been to IDEAL for corn, from Apr 20 through
// today + the forecast." 100 = every day perfect; it bleeds down as real stress
// accumulates. Built from the model's DAILY agronomic comfort (FAO-56 soil-water
// deficit, heat, cold, frost, waterlog), stage-weighted so a silking day counts
// ~3× a May day, per cell → acre-weighted to book. NO climatology — measured
// against agronomic ideal, not other years. Validated in tools/yield-model/
// comfort.js: the raw quality correlates 0.85 with actual book yield anomaly
// (2021 best, 2016/2025 worst). The display map below stretches the raw "fraction
// of perfect" so a perfect season pegs 100 (clip), the best observed year ≈ 90,
// and a disaster ≈ 38 — monotonic, so it doesn't change the ranking.
// stage config: ideal daytime-high band [lo,hi] °C, cold/heat penalty slope per °C
// outside it, stage weight. (°F refs: 18=64 20=68 22=72 25=77 26=79 30=86 31=88 35=95)
function miCornStage(gdd, planted, C) {
  if (!planted || gdd < C.GDD_EMERGE) return { lo: 20, hi: 27, cold: 0.022, heat: 0.010, w: 0.5, estab: true };
  if (gdd < 900)   return { lo: 22, hi: 30, cold: 0.040, heat: 0.018, w: 0.7 };
  if (gdd < 1180)  return { lo: 25, hi: 31, cold: 0.024, heat: 0.028, w: 1.1 };
  if (gdd < 1520)  return { lo: 25, hi: 30, cold: 0.026, heat: 0.075, w: 3.0, silk: true };
  if (gdd < 2100)  return { lo: 26, hi: 31, cold: 0.020, heat: 0.045, w: 1.6, fill: true };
  if (gdd < C.GDD_MATURE) return { lo: 23, hi: 31, cold: 0.010, heat: 0.020, w: 0.7 };
  return { lo: 18, hi: 30, cold: 0.005, heat: 0.005, w: 0.15 };
}
function miCornTempComfort(tmax, st) {
  if (tmax == null) return 1;
  let q = 1;
  if (tmax < st.lo) q = 1 - (st.lo - tmax) * st.cold;
  else if (tmax > st.hi) q = 1 - (tmax - st.hi) * st.heat;
  return miCornClamp(q, 0.12, 1);
}
// dailyObj: { [YYYY-MM-DD]: {tmax_c,tmin_c,precip_mm,srad_wm2,is_forecast} } (observed
// + forecast; NO climatology fill). Returns { q, ww } — raw quality 0–1 over Apr 20 →
// last available day, and the weighted-days elapsed (for the projection), or null.
function miCornComfort(dailyObj, awc, C) {
  const days = Object.keys(dailyObj).sort().map((k) => {
    const d = new Date(k + "T00:00:00Z");
    return { doy: Math.floor((d - Date.UTC(d.getUTCFullYear(), 0, 1)) / 86400000) + 1, r: dailyObj[k] };
  });
  if (!days.length) return null;
  const TAW = miCornClamp((awc || 12) * C.AWC_TO_MM, C.TAW_MIN, C.TAW_MAX);
  let sw = C.START_FRAC * TAW, gdd = 0, gddEmerge = null, gddSilk = null, wq = 0, ww = 0, wetRun = 0;
  for (const { doy, r } of days) {
    if (doy < 110) continue;                         // Apr 20 onward
    const tx = r.tmax_c, tn = r.tmin_c, tmean = tx != null && tn != null ? (tx + tn) / 2 : null;
    const planted = doy >= C.PLANT_DOY;
    if (planted) gdd += miCornGdd(tx, tn);
    if (gddEmerge == null && gdd >= C.GDD_EMERGE) gddEmerge = gdd;
    const emerged = gddEmerge != null;
    const gse = emerged ? gdd - gddEmerge : 0;
    const rootFrac = emerged ? miCornClamp(C.ROOT_START_FRAC + (1 - C.ROOT_START_FRAC) * (gse / C.ROOT_GDD_FULL), C.ROOT_START_FRAC, 1) : C.ROOT_START_FRAC;
    const TAWc = TAW * rootFrac, RAW = C.P_DEPLETE * TAWc;
    const fInt = emerged ? miCornClamp(gse / 600, 0.1, 1) : 0.1;
    const pet = miCornPet(r.srad_wm2, tmean) * (0.3 + (C.KC_MAX - 0.3) * fInt);
    const dep = TAWc - sw, Ks = dep <= RAW ? 1 : miCornClamp((TAWc - dep) / (TAWc - RAW), 0, 1);
    if (gddSilk == null && gdd >= C.GDD_SILK) gddSilk = gdd;
    const st = miCornStage(gdd, planted, C);
    // moisture: deficit (drought, harsher at silk) AND sustained excess (waterlog/
    // slow soggy start — counts pre-emergence too, but only when it actually lingers)
    const waterDef = st.silk && Ks < 0.7 ? Ks * Ks : Ks;
    const satFrac = miCornClamp((sw / TAWc - 0.88) / 0.12, 0, 1);
    if (satFrac > 0.5 && (r.precip_mm || 0) > 4) wetRun++; else wetRun = Math.max(0, wetRun - 1);
    const wetSustain = miCornClamp((wetRun - 2) / 4, 0, 1);
    const excessC = miCornClamp(1 - (st.estab ? 0.55 : 0.45) * satFrac * wetSustain, 0.45, 1);
    const waterC = miCornClamp(Math.min(emerged ? waterDef : 1, excessC), 0.1, 1);
    const nightC = (st.fill && tn != null && tn > 21) ? miCornClamp(1 - (tn - 21) * 0.05, 0.7, 1) : 1;
    const frostC = (gddSilk != null && tn != null && tn <= -1) ? 0.35 : 1;
    const comfort = miCornClamp(miCornTempComfort(tx, st) * waterC * nightC * frostC, 0.08, 1);
    sw += (r.precip_mm || 0) - pet * Ks; if (sw > TAWc) sw = TAWc; if (sw < 0) sw = 0;
    wq += st.w * comfort; ww += st.w;
  }
  return ww > 0 ? { q: wq / ww, ww } : null;
}
// raw quality Q (0–1) → final 0–100 (disaster ≈38, best year ≈90, perfect→100).
function miSeasonScoreRaw(Q) {
  const s = Q <= 0.81 ? 38 + 136.8 * (Q - 0.43) : 90 + 52.6 * (Q - 0.81);
  return Math.max(0, Math.min(100, Math.round(s)));
}
// PROJECTED display score: blends weather-so-far with a neutral/average rest-of-
// season, anchored at NEUTRAL and converging to the real score as the (weight-heavy)
// season fills in. WW_FULL = weighted-days of a complete season. Keeps adapting:
// climbs on a good summer, falls on a bad silking.
function miSeasonScoreFromQ(Q, ww) {
  if (Q == null || !Number.isFinite(Q)) return null;
  const WW_FULL = 168, NEUTRAL = 80;
  const raw = miSeasonScoreRaw(Q);
  const prog = ww != null ? Math.max(0, Math.min(1, ww / WW_FULL)) : 1;
  const score = Math.round(NEUTRAL + prog * (raw - NEUTRAL));
  const label = score >= 86 ? "Excellent" : score >= 73 ? "Very good" : score >= 60 ? "Good" : score >= 48 ? "Fair" : score >= 36 ? "Below avg" : "Tough";
  return { score, label, quality: Math.round(Q * 1000) / 1000, raw_score: raw, season_progress_pct: Math.round(prog * 100) };
}

// Returns null when the grid daily set is empty (e.g. a past year has no grid history).
function miGridShadowWeather(cohorts, gridCells, gridDaily, gridClimo, coef, year) {
  if (!cohorts || !cohorts.length) return null;
  if (!gridCells || !gridCells.length || !gridDaily || !gridDaily.length || !gridClimo || !gridClimo.length) return null;
  const C2F = (c) => (c == null ? null : Number(c) * 9 / 5 + 32);
  const MM2IN = (mm) => (mm == null ? null : Number(mm) / 25.4);
  const cellCounty = {}, cellCorn = {}, cellNccpi = {}, cellAwc = {}, cellLat = {}, cellLon = {};
  // Per-county CDL coverage: corn-weighting is only trustworthy when most of a
  // county's cells have loaded corn data. Partial counties (e.g. a CDL backfill
  // still in progress) fall back to uniform so a few loaded cells can't skew the
  // county factor. coverage = loaded cells / total cells.
  const ctyTotal = {}, ctyLoaded = {};
  for (const c of gridCells) {
    cellCounty[c.id] = c.county_fips;
    cellCorn[c.id] = c.corn_acres != null ? Number(c.corn_acres) : 0;
    cellNccpi[c.id] = c.nccpi_corn != null ? Number(c.nccpi_corn) : null;
    cellAwc[c.id] = c.awc != null ? Number(c.awc) : null;
    cellLat[c.id] = c.lat != null ? Number(c.lat) : 42.9;   // WNY mid-lat fallback for ET
    cellLon[c.id] = c.lon != null ? Number(c.lon) : null;   // for the per-cell water map join (lat|lon key)
    const f = c.county_fips;
    if (f) { ctyTotal[f] = (ctyTotal[f] || 0) + 1; if (c.corn_acres != null) ctyLoaded[f] = (ctyLoaded[f] || 0) + 1; }
  }
  const CORN_COVERAGE_MIN = 0.7;   // need ≥70% of a county's cells loaded to corn-weight it
  // NCCPI fallback for the rare null cell so it still contributes by corn acres
  const NCCPI_FALLBACK = 0.4;
  // cell_id → { [obs_date]: {tmax_f,tmin_f,precip_in,is_forecast} } (unit-converted)
  const dailyByCell = {};
  for (const w of gridDaily) {
    (dailyByCell[w.cell_id] = dailyByCell[w.cell_id] || {})[w.obs_date] = {
      obs_date: w.obs_date,
      tmax_f: C2F(w.tmax_c), tmin_f: C2F(w.tmin_c), precip_in: MM2IN(w.precip_mm),
      // native units for the mechanistic corn model (miCornSimulate)
      tmax_c: w.tmax_c != null ? Number(w.tmax_c) : null, tmin_c: w.tmin_c != null ? Number(w.tmin_c) : null,
      precip_mm: w.precip_mm != null ? Number(w.precip_mm) : 0,
      srad_wm2: w.srad_wm2 != null ? Number(w.srad_wm2) : null, vpd_kpa: w.vpd_kpa != null ? Number(w.vpd_kpa) : null,
      is_forecast: !!w.is_forecast,
    };
  }
  // cell_id → { [mmdd]: {precip(in), gdd(F)} } — gdd_f is already Jensen-safe mean(daily GDD)
  const climoByCell = {};
  // cell_id → { [doy0]: {tmaxF,tminF,precipIn} } — for the soil-water bucket's ET +
  // climatology-fill of unobserved future days + the per-cell re-centering baseline.
  // doy0 uses a FIXED non-leap reference (2021) so observed dates and climatology
  // align day-for-day with no leap drift. Needs tmax_c/tmin_c in the climo fetch.
  const climoDoyCell = {};
  const miMdToDoy0 = (mm, dd) => Math.floor((Date.UTC(2021, mm - 1, dd) - Date.UTC(2021, 0, 1)) / 86400000);
  for (const e of gridClimo) {
    const mmdd = miDoyToMmdd(e.day_of_year);
    (climoByCell[e.cell_id] = climoByCell[e.cell_id] || {})[mmdd] = {
      precip: MM2IN(e.precip_mm) || 0,
      gdd: e.gdd_f != null ? Number(e.gdd_f) : 0,
    };
    const doy0 = miMdToDoy0(Number(mmdd.slice(0, 2)), Number(mmdd.slice(3, 5)));
    (climoDoyCell[e.cell_id] = climoDoyCell[e.cell_id] || {})[doy0] = {
      tmaxF: C2F(e.tmax_c), tminF: C2F(e.tmin_c), precipIn: MM2IN(e.precip_mm) || 0,
      // native units for miCornSimulate climatology fill + normal run
      tmax_c: e.tmax_c != null ? Number(e.tmax_c) : null, tmin_c: e.tmin_c != null ? Number(e.tmin_c) : null,
      precip_mm: e.precip_mm != null ? Number(e.precip_mm) : 0,
      srad_wm2: e.srad_wm2 != null ? Number(e.srad_wm2) : null, vpd_kpa: e.vpd_kpa != null ? Number(e.vpd_kpa) : null,
    };
  }
  // Representative plant date for the bucket = acre-weighted mean cohort date
  // (the per-cohort silking TIMING still lives in the temperature factor; the
  // moisture bucket uses one date per cell, matching the offline calibration).
  let pdNum = 0, pdDen = 0;
  for (const co of cohorts) {
    if (!co.ac || co.ac <= 0 || !co.date) continue;
    pdNum += miMdToDoy0(co.date.getMonth() + 1, co.date.getDate()) * co.ac; pdDen += co.ac;
  }
  const bktPlantDoy = pdDen > 0 ? Math.round(pdNum / pdDen) : miMdToDoy0(5, 12);
  // season-to-date window (matches MI_SEASON; live diagnostic for current year)
  const t = new Date();
  const isCur = year === t.getFullYear();
  const asOf = isCur
    ? `${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`
    : MI_SEASON.end;
  const seasonStart = MI_SEASON.start;
  const mean = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);
  const byCounty = {};   // fips → accumulators
  const cellWaterByLL = {};   // "lat|lon" → soil-water bucket factor, for the per-cell map layer
  const cellDiseaseByLL = {}; // "lat|lon" → disease-pressure readout (reporting only, never yield)
  let cellsUsed = 0;
  for (const cellId of Object.keys(dailyByCell)) {
    const fips = cellCounty[cellId];
    if (!fips) continue;
    const climo = climoByCell[cellId];
    if (!climo) continue;
    const daily = dailyByCell[cellId];
    // Per-cell soil-water sensitivity from SSURGO AWC: low-AWC (gravelly) ground
    // gets a steeper drought penalty AND a bigger benefit from timely rain.
    const awc = cellAwc[cellId];
    const soilMult = awc != null
      ? miClamp(1 + coef.AWC_SENS * (coef.AWC_REF - awc) / coef.AWC_REF, coef.AWC_S_MIN, coef.AWC_S_MAX)
      : 1;
    // Cohort factor is now TEMPERATURE-ONLY (GDD/VPD/heat/freeze) — the soil-water
    // bucket below owns all moisture stress, so PRECIP_OFF prevents double-counting.
    const cw = miCohortWeather(cohorts, daily, climo, { ...coef, PRECIP_OFF: true }, { soilMult });
    // ── Soil-water bucket (the single moisture factor). Merge observed-to-date with
    // this cell's climatology for unobserved days (evidence-gating), run the daily
    // balance, and re-center against the cell's OWN climatology so a normal season
    // reads ~1.0. See MI_COEF BKT_* + miRunBucket.
    let waterFactor = 1;
    const climoDoy = climoDoyCell[cellId];
    if (climoDoy) {
      const lat = cellLat[cellId];
      const byDoyObs = {};
      for (const dk of Object.keys(daily)) {
        const rec = daily[dk];
        const doy0 = miMdToDoy0(Number(dk.slice(5, 7)), Number(dk.slice(8, 10)));
        byDoyObs[doy0] = { tmaxF: rec.tmax_f, tminF: rec.tmin_f, precipIn: rec.precip_in };
      }
      const merged = {};
      for (let dap = 0; dap <= 140; dap++) { const doy = bktPlantDoy + dap; merged[doy] = byDoyObs[doy] || climoDoy[doy] || null; }
      const act = miRunBucket(merged, awc, bktPlantDoy, lat, coef);
      const nrm = miRunBucket(climoDoy, awc, bktPlantDoy, lat, coef);
      waterFactor = miClamp(1 - coef.BKT_K_DRY * (act.dry - nrm.dry) - coef.BKT_K_WET * (act.wet - nrm.wet), coef.BKT_FLOOR, coef.BKT_CEIL);
    }
    // Early-season stress (Apr→today) re-derived from THIS cell's grid weather
    // vs its own normal — the live-NOW signal the cohort factor can't see yet
    // (silking is months out). This is what actually moves the shadow yield
    // today; without it the grid's cool-wet spring never reaches the number.
    const es = miEarlyStress(Object.values(daily), year, climo);
    // Season-to-date observed vs normal — normal paired to the SAME observed
    // days. gridMET lags ~2d (data ends before "today"), so summing the normal
    // through today overstates the cold (a real bug caught 2026-05-29: Allegany
    // read −19% when the day-matched anomaly is −10%). Match day-for-day.
    let sg = 0, sp = 0, ng = 0, np = 0;
    for (const dk of Object.keys(daily)) {
      const mmdd = dk.slice(5);
      if (mmdd < seasonStart || mmdd > asOf) continue;
      const rec = daily[dk];
      if (rec.is_forecast) continue;
      const cm = climo[mmdd];
      const g = miModGdd(rec.tmax_f, rec.tmin_f);
      if (g != null) { sg += g; if (cm) ng += cm.gdd; }
      if (rec.precip_in != null) { sp += Number(rec.precip_in); if (cm) np += cm.precip; }
    }
    // Mechanistic corn model — per-cell weather index vs this cell's own climatology
    // normal (the validated model; see MI_CORN). idx/norm each ~kSet×fillKs.
    const mech = miCornCellIndex(daily, climoDoyCell[cellId], awc, year, MI_CORN);
    // Weather-quality (Apr 20 → today + forecast) for the Season Weather Score
    const comfort = miCornComfort(daily, awc, MI_CORN);
    const b = byCounty[fips] || (byCounty[fips] = { cells: [] });
    b.cells.push({
      factor: cw.factor, es: es.factor, water: waterFactor,
      mechIdx: mech ? mech.idx : null, mechNorm: mech ? mech.norm : null,
      comfortQ: comfort ? comfort.q : null, comfortWW: comfort ? comfort.ww : null,
      silkEv: cw.breakdown.length ? mean(cw.breakdown.map((x) => x.silk_evidence || 0)) : 0,
      fillEv: cw.breakdown.length ? mean(cw.breakdown.map((x) => x.fill_evidence || 0)) : 0,
      sg, ng, sp, np,
      w: cellCorn[cellId] || 0,    // CDL corn acres in this cell (for reporting + coverage)
      nccpi: cellNccpi[cellId],     // SSURGO soil productivity (0-1)
      // PRODUCTION weight = corn acres × soil potential — "where the bushels come
      // from". Weighting the cell weather aggregation by this captures the
      // soil×weather covariance: if the high-corn high-soil ground (e.g. Genesee
      // Valley bottom) catches good weather, it pulls the county factor up.
      pw: (cellCorn[cellId] || 0) * (cellNccpi[cellId] != null ? cellNccpi[cellId] : NCCPI_FALLBACK),
    });
    if (cellLat[cellId] != null && cellLon[cellId] != null) {
      const llKey = `${cellLat[cellId].toFixed(2)}|${cellLon[cellId].toFixed(2)}`;
      cellWaterByLL[llKey] = waterFactor;
      // Disease pressure is REPORTING ONLY — computed here because this is where
      // the per-cell daily weather already lives, but it never enters waterFactor,
      // cw.factor, or anything downstream of them. See miDiseasePressure.
      const dz = miDiseasePressure(daily, asOf, year);
      if (dz) cellDiseaseByLL[llKey] = dz;
    }
    cellsUsed++;
  }
  const counties = {};
  for (const fips of Object.keys(byCounty)) {
    const cs = byCounty[fips].cells;
    // PRODUCTION weighting (corn × soil): each cell counts in proportion to where
    // the county's bushels actually come from, so the county weather factor
    // reflects the weather the *productive* corn ground saw — the soil×weather
    // covariance. Falls back to uniform until CDL is loaded (totPW=0) or the
    // county is under-covered. corn_acres (totW) kept separately for reporting.
    const totW = cs.reduce((a, c) => a + (c.w > 0 ? c.w : 0), 0);
    const totPW = cs.reduce((a, c) => a + (c.pw > 0 ? c.pw : 0), 0);
    const coverage = ctyTotal[fips] > 0 ? (ctyLoaded[fips] || 0) / ctyTotal[fips] : 0;
    const useCorn = totPW > 0 && coverage >= CORN_COVERAGE_MIN;   // partial counties → uniform until CDL fully loaded
    const W = useCorn ? totPW : cs.length;
    const wt = (c) => (useCorn ? (c.pw > 0 ? c.pw : 0) : 1);
    const wsum = (sel) => cs.reduce((a, c) => a + sel(c) * wt(c), 0);
    const wmean = (sel) => wsum(sel) / W;
    const nccpiVals = cs.map((c) => c.nccpi).filter((v) => v != null);
    const sgW = wmean((c) => c.sg), ngW = wmean((c) => c.ng), spW = wmean((c) => c.sp), npW = wmean((c) => c.np);
    // ── Mechanistic corn weather factor (THE live forecast multiplier) ──
    // Production-weighted index vs normal (same corn×soil weighting as the temp
    // factor), over cells the model resolved. mech_factor = 1+DAMP·(idx/norm−1).
    let mIdxW = 0, mNrmW = 0, mWden = 0, mIdxU = 0, mNrmU = 0, mU = 0;
    for (const c of cs) {
      if (c.mechIdx == null || c.mechNorm == null) continue;
      mU++; mIdxU += c.mechIdx; mNrmU += c.mechNorm;
      const w = useCorn ? (c.pw > 0 ? c.pw : 0) : 1;
      if (w > 0) { mIdxW += c.mechIdx * w; mNrmW += c.mechNorm * w; mWden += w; }
    }
    let mechFactor = null, mechRatio = null, infoW = 1;
    if (mU > 0) {
      mechRatio = (mWden > 0 && mNrmW > 0) ? mIdxW / mNrmW : (mNrmU > 0 ? mIdxU / mNrmU : null);
      // DAMP scaled by how much the as-of index actually knows (MI_CORN.INFO_W).
      // asOf is "MM-DD"; convert on a fixed non-leap reference so the weight does
      // not shift by a day in leap years.
      if (mechRatio != null) {
        const _m = Number(String(asOf).slice(0, 2)), _d = Number(String(asOf).slice(3, 5));
        const _doy = Number.isFinite(_m) && Number.isFinite(_d)
          ? Math.floor((Date.UTC(2021, _m - 1, _d) - Date.UTC(2021, 0, 1)) / 86400000) + 1
          : null;
        infoW = miInfoWeight(_doy, MI_CORN);
        mechFactor = miCornClamp(1 + MI_CORN.DAMP * infoW * (mechRatio - 1), MI_CORN.FACTOR_FLOOR, MI_CORN.FACTOR_CEIL);
      }
    }
    // Weather-quality Q + weighted-days (corn-acre weighted) for the Season Weather Score
    let cqW = 0, cwwW = 0, cqDen = 0;
    for (const c of cs) { if (c.comfortQ == null) continue; const w = useCorn ? (c.pw > 0 ? c.pw : 0) : 1; if (w > 0) { cqW += c.comfortQ * w; cwwW += (c.comfortWW || 0) * w; cqDen += w; } }
    const comfortQ = cqDen > 0 ? cqW / cqDen : null;
    const comfortWW = cqDen > 0 ? cwwW / cqDen : null;
    counties[fips] = {
      factor: miR3(wmean((c) => c.factor)), early_stress_factor: miR3(wmean((c) => c.es)),
      water_factor: miR3(wmean((c) => c.water)),
      mech_factor: mechFactor != null ? miR3(mechFactor) : null,
      mech_index_ratio: mechRatio != null ? miR3(mechRatio) : null, mech_cells: mU,
      // reported so the page can say WHY an early-season factor is muted
      info_weight: miR3(infoW),
      comfort_q: comfortQ != null ? miR3(comfortQ) : null, comfort_ww: comfortWW != null ? miR1(comfortWW) : null,
      cell_n: cs.length, corn_weighted: useCorn, corn_acres: Math.round(totW), corn_coverage: miR3(coverage),
      mean_nccpi: nccpiVals.length ? miR3(nccpiVals.reduce((a, b) => a + b, 0) / nccpiVals.length) : null,
      silk_evidence_pct: Math.round(wmean((c) => c.silkEv) * 100),
      fill_evidence_pct: Math.round(wmean((c) => c.fillEv) * 100),
      season_gdd: Math.round(sgW), normal_gdd: Math.round(ngW),
      gdd_anom: ngW > 0 ? miR3((sgW - ngW) / ngW) : null,
      season_precip_in: miR1(spW), normal_precip_in: miR1(npW),
      precip_anom: npW > 0 ? miR3((spW - npW) / npW) : null,
    };
  }
  // Per-county wet z, and the turndown it implies. Attached here (not applied)
  // so miComputeYield stays the single place a factor is actually changed.
  const wetZ = miWetZByCounty(gridCells, gridDaily, gridClimo, year);
  for (const [fips, c] of Object.entries(counties)) {
    const t = miWetTurndown(wetZ[fips]);
    c.wet_z = t.z; c.wet_scale = t.scale; c.wet_penalty = t.penalty;
  }
  const allF = Object.values(counties).map((c) => c.factor).filter((v) => v != null);
  const anyCorn = Object.values(counties).some((c) => c.corn_weighted);
  return {
    wny_factor: allF.length ? miR3(mean(allF)) : 1,
    counties, cells_used: cellsUsed, county_n: allF.length, asof: asOf, corn_weighted: anyCorn,
    cell_water: cellWaterByLL,   // per-cell soil-water factor for the map dry-down layer
    cell_disease: cellDiseaseByLL, // per-cell disease pressure — map layer only, no yield effect
    // Input coverage, reported so `model_mode` in miComputeYield can state what
    // the run actually stood on. This is the ONLY place the cell rows are in
    // scope — miComputeYield never receives gridCells — so counting here is what
    // keeps the mode indicator from asserting an input it cannot see.
    cells_total: (gridCells || []).length,
    soil_cells: (gridCells || []).filter((c) => c.awc != null).length,
    cdl_cells: (gridCells || []).filter((c) => c.corn_acres != null).length,
  };
}
function miSeasonAgg(byDate, year, climo, asOfMmdd) {
  const start = new Date(`${year}-${MI_SEASON.start}`);
  const endMmdd = asOfMmdd && asOfMmdd < MI_SEASON.end ? asOfMmdd : MI_SEASON.end;
  const end = new Date(`${year}-${endMmdd}`);
  let gdd = 0, precip = 0, filled = 0, real = 0, span = 0;
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    span++;
    const key = d.toISOString().slice(0, 10), doy = key.slice(5), rec = byDate[key];
    let g = null, p = null;
    if (rec) { g = miModGdd(rec.tmax_f, rec.tmin_f); p = rec.precip_in; }
    if (g == null && climo) { g = climo.gdd[doy]; filled++; } else if (g != null) real++;
    if (p == null && climo) p = climo.precip[doy];
    if (g != null) gdd += g;
    if (p != null) precip += Number(p);
  }
  return { gdd, precip, filled, real, span };
}
function miComputeYield(year, ovrState, yieldRows, paceRows, wxRows, surveyPace, harvRows, plantedRows, cfg, silData, signalsData, gridShadow, rmaExpRows) {
  // ⚠ THE ACRE SERIES NOW CARRY STATE ROWS, AND THIS MODEL IS COUNTY-WISE.
  // `nass_data` gained STATE-level corn ACRES_HARVESTED on 2026-08-23 (the USDA
  // benchmark card) and ACRES_PLANTED the same way. Every caller selects these
  // series WITHOUT a county filter, so a state row arrives beside the counties
  // carrying `county: null` — and `String(r.county || "")` turns it into a real
  // group under the key `""`.
  //
  // It is not inert. `nyGrainAcres` sums `Object.values()` over that map, so the
  // whole state total was being added ON TOP of the counties it is made of:
  // measured 2026-08-23, **1,244,364 acres where the county sum is 695,364**, and
  // `grain_share_pct` read **75.3%** against the 60.1% this file's own comment
  // records for matched footprints.
  //
  // Filtered ONCE here rather than at each of the loops below, because the next
  // county-wise read of these rows would have the same bug and no reason to
  // suspect it. Nothing downstream wants the state row: every consumer keys by
  // county name. Same shape as the `payload` region defect — a column read as
  // one thing and written as another.
  harvRows = (harvRows || []).filter((r) => r.county != null && String(r.county).trim() !== "");
  plantedRows = (plantedRows || []).filter((r) => r.county != null && String(r.county).trim() !== "");
  const ofac = (f) => { const v = parseFloat(ovrState[f]); return !isNaN(v) && v > 0 ? v : 1; };
  const CO = (cfg && cfg.coef) || MI_COEF;                          // weather sensitivities (tunable)
  const SCAP = cfg && cfg.slopeCap != null ? cfg.slopeCap : 2.5;    // baseline slope cap (tunable)
  // Planting-pace factor = date-windowed agronomic model from OUR survey
  // (miCornPotential curve, computed in load() → surveyPace). NASS multi-year
  // statewide pace is retained only as the "vs NY normal" context line.
  const sp = surveyPace || {};
  let statePacePct = sp.pctPlanted != null ? sp.pctPlanted : null;
  let paceWeek = null, paceNormalPct = null, paceFactor = 1, paceNote, nyStatePct = null, nyStateWeek = null;
  // Week-of-year on the Jan-4-Sunday scheme (matches NASS PCT_PLANTED weeks),
  // capped at 30 so a past-year selection can't pull the flat 100% tail.
  const jan4 = new Date(year, 0, 4);
  const todaySun = new Date(); todaySun.setHours(0, 0, 0, 0); todaySun.setDate(todaySun.getDate() - todaySun.getDay());
  const nowWeek = Math.min(30, Math.round((todaySun - jan4) / (7 * 86400000)) + 1);
  // 5-yr window (year-5..year-1) for unity with PP report + weekly text.
  // Was year-10 — produced 51.5% normal for wk 21 vs 60% in PP report.
  const prior = (paceRows || []).filter((r) => Number(r.year) >= year - 5 && Number(r.year) < year && Number(r.week) <= nowWeek);
  if (prior.length) {
    paceWeek = Math.max(...prior.map((r) => Number(r.week)));
    const atWk = prior.filter((r) => Number(r.week) === paceWeek).map((r) => Number(r.value));
    paceNormalPct = atWk.length ? miMean(atWk) : null;
  }
  // Current-year NY statewide pace (NASS PCT_PLANTED). Most recent week ≤ now.
  // This is the headline truth-test: if the book's 14% pace mirrors statewide
  // (~10%), we know we're sampling representatively. If they diverge, the
  // forecast's "pace factor" inherits the book's bias.
  const curState = (paceRows || []).filter((r) => Number(r.year) === year && Number(r.week) <= nowWeek);
  if (curState.length) {
    nyStateWeek = Math.max(...curState.map((r) => Number(r.week)));
    const sv = curState.filter((r) => Number(r.week) === nyStateWeek).map((r) => Number(r.value));
    if (sv.length) nyStatePct = sv.reduce((a, b) => a + b, 0) / sv.length;
  }
  if (sp.factor != null && (sp.plantedAc > 0 || sp.unplantedAc > 0)) {
    paceFactor = miClamp(sp.factor, MI_CORN_PLANTING.floor, 1.02);
    const lossPct = Math.round((1 - paceFactor) * 100);
    const plP = sp.plantedPot != null ? `${Math.round(sp.plantedPot * 100)}%` : "—";
    const prP = sp.projPot != null ? `${Math.round(sp.projPot * 100)}%` : "—";
    const normTxt = paceNormalPct != null ? ` · NY normal ~${paceNormalPct.toFixed(0)}% by wk ${paceWeek}` : "";
    paceNote = `Our book corn: ${Math.round(sp.pctPlanted || 0)}% planted (${Math.round(sp.plantedAc).toLocaleString()}/${Math.round(sp.intendedAc).toLocaleString()} ac, ${sp.farms} farms). Planted-window potential ${plP}; ${Math.round(sp.unplantedAc).toLocaleString()} ac unplanted projected ${prP} over a ${MI_CORN_PLANTING.projectionDays}-day catch-up → pace −${lossPct}%${normTxt}`;
  } else {
    paceNote = "No planting-survey corn data yet — pace factor 1.0";
  }
  // yields by county name
  const yByCty = {};
  for (const r of yieldRows || []) {
    const nm = String(r.county || "").toUpperCase().trim();
    (yByCty[nm] = yByCty[nm] || []).push({ x: Number(r.year), y: Number(r.value) });
  }
  // RMA's PUBLISHED expected county yield, "COUNTY|YEAR" → bu/ac. This is the
  // mechanistic forecast's anchor — see effBaseline below and MI_CORN.
  const rmaExpBy = {};
  // Best RMA FINAL yield ever recorded per county — the county's record on the
  // INSURED-ACRE basis. Needed because the anchor above is RMA expected, which is
  // insured-acre too, so the forecast is an insured-acre number: comparing it to
  // NASS (an all-acre survey) compares two different populations. Orleans is the
  // worked example — NASS's best year is 193.6, RMA's is 203.6 (2024), and NASS
  // read 176.5 that same year. Judging a 199 forecast against 193.6 calls it a
  // record; against 203.6 it isn't one.
  const rmaFinalBest = {};
  for (const r of rmaExpRows || []) {
    // New York only. RMA's table carries every state and this map is keyed by county
    // NAME, so an unfiltered read let a same-named county elsewhere win: Seneca took
    // Ohio's 189.5 and Wayne and Wyoming took Pennsylvania's 138.8 and 133 for 2026
    // (New York's are 176.4, 176.1 and 163.8). Both callers now read state=eq.NY; this
    // drops any row that still says another state (2026-11-03).
    if (r.state != null && String(r.state).toUpperCase().trim() !== "NY") continue;
    const cty = String(r.county || "").toUpperCase().trim();
    if (r.expected_yield != null) rmaExpBy[`${cty}|${Number(r.crop_year)}`] = Number(r.expected_yield);
    if (r.rma_final_yield != null) {
      const v = Number(r.rma_final_yield);
      if (isFinite(v) && v > 0 && (rmaFinalBest[cty] == null || v > rmaFinalBest[cty].y)) {
        rmaFinalBest[cty] = { y: v, year: Number(r.crop_year) };
      }
    }
  }
  // Production roll-up. Predicted GRAIN acres (Kevin 2026-05-26 reformulation):
  //   pred_grain_acres = (5-yr avg ACRES_PLANTED total) × plantedByFpdFrac
  //                       − (this year's projected silage acres)
  // Rationale: ACRES_PLANTED is total corn (grain + silage combined) going in
  // the ground in a typical year. Multiplying by plantedByFpdFrac shrinks for
  // this year's slow-planting reality. Subtracting this year's projected
  // silage acres (dairy-driven, relatively stable) leaves the grain piece.
  // Old model used ACRES_HARVESTED (grain-only) baseline and subtracted
  // "extra silage above normal" on top — layered slow-year shrinkage on a
  // baseline that already excluded silage, which made the math hard to follow.
  // hByCty kept for the normal-year reference (normalProd computation below).
  const hByCty = {};
  for (const r of harvRows || []) {
    const nm = String(r.county || "").toUpperCase().trim();
    (hByCty[nm] = hByCty[nm] || []).push({ x: Number(r.year), y: Number(r.value) });
  }
  // pByCty: 5-yr avg TOTAL planted corn (grain + silage combined) per county
  const pByCty = {};
  for (const r of plantedRows || []) {
    const nm = String(r.county || "").toUpperCase().trim();
    (pByCty[nm] = pByCty[nm] || []).push({ x: Number(r.year), y: Number(r.value) });
  }
  // Acre-shrinkage factor: fraction of intended acres that actually go in the
  // ground this year (any time of season). Late-planted acres COUNT — their
  // yield penalty is handled separately via paceFactor on per-acre yield, so
  // using plantedByFpdFrac here would double-count the late-planting penalty.
  // Only PP acres are truly excluded (never planted, never harvested).
  //   1 − PP%   = ~0.825 for current 17.5% PP projection
  //   plantedByFpdFrac = ~0.72  ← would understate by ~10pts (the late-acres bucket)
  const ppFrac = (surveyPace && surveyPace.intendedAc > 0 && surveyPace.ppAc != null)
    ? Math.max(0, 1 - (surveyPace.ppAc / surveyPace.intendedAc))
    : (surveyPace && surveyPace.plantedByFpdFrac != null ? surveyPace.plantedByFpdFrac : 1);
  let prodPredSum = 0, prodNormSum = 0, prodCty = 0;
  let anomGsum = 0, anomPsum = 0, anomN = 0;        // WNY-avg ACTUAL anomalies (slider defaults)
  const ov = cfg && cfg.ov;                          // scenario overrides {heatAnom,rainAnom,acresPct}
  // weather by fips
  const wByFips = {};
  for (const w of wxRows || []) (wByFips[w.county_fips] = wByFips[w.county_fips] || []).push(w);

  // ── EARLY-SEASON STRESS — book-level, fixes the model contradiction ─
  // The cohort silking model returns ×1.0 when silking is still climatology
  // (silk_evidence=0%), but the crop already in the ground RIGHT NOW is
  // hurting from cold+wet weather. This factor captures that drag.
  // Self-correcting: recovery days offset stress days as the season unfolds.
  // Capped at 6% — Kevin's intuition: minimal if conditions improve,
  // compounds if they don't.
  const earlyClimoDoy = (() => {
    // Build a one-shot climatology from the same window the cohort model uses
    const flat = {};
    for (const w of wxRows || []) {
      if (w.is_forecast || Number(w.obs_date.slice(0, 4)) >= year) continue;
      const k = w.obs_date.slice(5);
      const a = flat[k] || (flat[k] = { p: 0, pN: 0, g: 0, gN: 0 });
      if (w.precip_in != null) { a.p += Number(w.precip_in); a.pN++; }
      const g = miModGdd(w.tmax_f, w.tmin_f);
      if (g != null) { a.g += g; a.gN++; }
    }
    const out = {};
    for (const k of Object.keys(flat)) out[k] = { precip: flat[k].pN ? flat[k].p / flat[k].pN : 0, gdd: flat[k].gN ? flat[k].g / flat[k].gN : 0 };
    return out;
  })();
  let earlyStress = miEarlyStress(wxRows, year, earlyClimoDoy);   // mutable so we can replace with the weighted-mean-across-counties aggregate after the per-county loop runs

  // ── Cow-equivalent calibration (PRE-LOOP) ──────────────────────────────
  // Need tonsPerCowEquiv + stateHeiferRatio inside the per-county loop to
  // compute each county's silage demand. miCowSilage's full output depends on
  // wnyWF which we don't have yet (computed from per-county weather factors).
  // Run a wnyWF=null pass purely for the state-level calibration values;
  // the wnyWF-aware full pass runs after the loop.
  const _calib = silData ? miCowSilage(silData.silAcRows, silData.silYldRows, silData.cowRows, year, null) : null;
  const stateHeiferRatio_top = _calib ? _calib.stateHeiferRatio : 0.45;
  const tonsPerCowEquiv_top = _calib ? _calib.tonsPerCowEquiv : 8;
  // Replacement-heifer expansion multiplier (UNH/UMD extension formula) — lifts silage
  // demand when the herd is growing, since the milking-cow change alone misses the extra
  // replacements being raised. 1.0 = steady-state herd (no adjustment).
  const heiferExpansion_top = _calib && _calib.heiferExpansionFactor ? _calib.heiferExpansionFactor : 1;


  // ── NASS CURRENT-YEAR ACRES — use real values when published ──────
  // NASS releases planted acres at COUNTY level around Dec/Jan and state
  // level in late June. As soon as it lands, swap from "projected acres
  // (book pace × 5-yr avg harvested)" to the real number. This is the
  // single biggest "info captured" event — pace projection becomes pace
  // reality. Until then, the projection logic stands.
  const nassActualAcres = {};   // county_name (upper) → { planted, harvested }
  if (signalsData && signalsData.curYrAcresRows) {
    for (const r of signalsData.curYrAcresRows) {
      if (Number(r.year) !== year) continue;
      const cty = String(r.county || "").toUpperCase().trim();
      if (!cty) continue;
      const v = Number(r.value);
      if (!(v > 0)) continue;
      if (!nassActualAcres[cty]) nassActualAcres[cty] = {};
      if (r.stat_type === "ACRES_PLANTED") nassActualAcres[cty].planted = v;
      else if (r.stat_type === "ACRES_HARVESTED") nassActualAcres[cty].harvested = v;
    }
  }
  const nassAcresUsed = Object.keys(nassActualAcres).length;

  // ── Per-county USDM DSCI lookup ──────────────────────────────────
  // USDM publishes per-county DSCI weekly. Per-county drought signal lets
  // us flag e.g. "Allegany is at DSCI 150 (moderate drought), Wayne is 0"
  // even though both might have similar precip anomalies on aggregate.
  // Latest week's value used per county.
  // ── Per-county dairy cow inventory (NASS, annual; latest year per county) ─
  // Used as a map data layer + drives the silage-displacement model. Pulled
  // from the same cowRows that feeds miCowSilage — but here we keep
  // county-level rows AND track latest year per county.
  // Track ALL years per county so we can compute 5-yr avg (needed for the
  // cow-change factor in the historical-anchor silage model).
  const cowYearsByCounty = {};
  const heiferYearsByCounty = {};
  if (silData && silData.cowRows) {
    for (const r of silData.cowRows) {
      if (!r.county) continue;
      const cty = String(r.county).toUpperCase().trim();
      const yr = Number(r.year), v = Number(r.value);
      if (!cty || !yr || !(v > 0)) continue;
      const isHeifer = r.stat_type === "MILK_HEIFERS_INVENTORY_COUNTY" || r.stat_type === "MILK_HEIFERS_INVENTORY";
      const bucket = isHeifer ? heiferYearsByCounty : cowYearsByCounty;
      (bucket[cty] = bucket[cty] || []).push({ year: yr, head: v });
    }
  }
  const cowByCounty = {};        // upper-case county name → { head, year, avgRecent }
  for (const cty in cowYearsByCounty) {
    const sorted = cowYearsByCounty[cty].sort((a, b) => b.year - a.year);
    const latest = sorted[0];
    // 3-yr window: weights recent dairy trends (cow growth in Cayuga went
    // 41.5k → 49k 2018→2025) more than a wider window would.
    const recent = sorted.slice(0, 3);
    const avgRecent = recent.reduce((s, r) => s + r.head, 0) / recent.length;
    cowByCounty[cty] = { head: latest.head, year: latest.year, avgRecent };
  }
  const heiferByCounty = {};     // upper-case county name → { head, year }
  for (const cty in heiferYearsByCounty) {
    const sorted = heiferYearsByCounty[cty].sort((a, b) => b.year - a.year);
    heiferByCounty[cty] = { head: sorted[0].head, year: sorted[0].year };
  }
  // 3-yr avg silage acres harvested per county — the empirical baseline that
  // implicitly captures exports, heifer ratios, storage practices, etc.
  // 3-yr (not 5-yr) chosen to weight recent dairy trends: Cayuga's 2025
  // silage (38.2k ac) is closer to the actual current operation than the
  // 5-yr avg (28.9k ac) which is dragged down by a 2022 anomaly. Sidesteps
  // the "13 t/cow state-avg vs 20 t/cow Livingston-actual" mismatch.
  const silAcByCounty = {};
  if (silData && silData.silAcRows) {
    const byCty = {};
    for (const r of silData.silAcRows) {
      if (!r.county) continue;
      const cty = String(r.county).toUpperCase().trim();
      const yr = Number(r.year), v = Number(r.value);
      if (!cty || !yr || !(v > 0)) continue;
      (byCty[cty] = byCty[cty] || []).push({ year: yr, ac: v });
    }
    for (const cty in byCty) {
      const sorted = byCty[cty].sort((a, b) => b.year - a.year);
      const recent = sorted.slice(0, 3);
      silAcByCounty[cty] = recent.reduce((s, r) => s + r.ac, 0) / recent.length;
    }
  }

  // ── Silage chain constants — calibrated from STATE-level NASS history
  // so they can be applied per-county. Each county needs:
  //   silage_tons_demanded = cow_count × tons_per_cow_per_year
  //   silage_yield_per_acre = normal_silage_yield × THIS county's weather factor
  //   silage_acres_needed = demand / yield
  //
  // tons_per_cow: physical constant — how much silage one dairy cow eats per
  // year. ~13.3 tons/cow for NY (back-calculated from state acres × yield ÷
  // cows over the last 5 yrs). Same for every county.
  //
  // normal_silage_yield: state 5-yr avg silage yield (~18-21 tons/ac for NY).
  // Per-county weather factor modifies it: e.g. Allegany with weather × 0.95
  // expects ~18 tons/ac if state normal is 19.
  let tonsPerCow = 13.3;
  let normalSilageYield = 19;
  const countySilageYieldBaseline = {};   // upper-case county name → 5-yr avg silage yield (t/ac)
  if (silData && silData.silAcRows && silData.silYldRows && silData.cowRows) {
    const ac = {}, yl = {}, cwY = {};
    silData.silAcRows.forEach((r) => { const y = Number(r.year), v = Number(r.value); if (v > 0) ac[y] = v; });
    // State-level silage yield (county=null) for tons-per-cow calibration
    silData.silYldRows.forEach((r) => {
      if (r.county) return;   // state rows only for the calibration
      const y = Number(r.year), v = Number(r.value);
      if (v > 0) yl[y] = v;
    });
    silData.cowRows.forEach((r) => {
      if (r.county) return;
      const y = Number(r.year), v = Number(r.value);
      if (v > 0) cwY[y] = v;
    });
    const calibYrs = Object.keys(ac).map(Number).filter((y) => y < year && yl[y] != null && cwY[y] != null).sort((a, b) => a - b).slice(-5);
    if (calibYrs.length >= 3) {
      const implied = calibYrs.map((y) => (ac[y] * yl[y]) / cwY[y]);
      tonsPerCow = implied.reduce((a, b) => a + b, 0) / implied.length;
      normalSilageYield = calibYrs.reduce((s, y) => s + yl[y], 0) / calibYrs.length;
    }
    // Per-county silage-yield baseline (5-yr avg) — silage yields vary
    // meaningfully across counties due to hybrid selection, soil, dairy mgmt.
    // NASS publishes county YIELD_SILAGE intermittently — some counties have
    // every year, others have gaps. Use whatever's there.
    const byCounty = {};
    silData.silYldRows.forEach((r) => {
      if (!r.county) return;
      const c = String(r.county).toUpperCase().trim();
      const y = Number(r.year), v = Number(r.value);
      if (!c || !(v > 0) || y >= year) return;
      (byCounty[c] = byCounty[c] || []).push({ y, v });
    });
    for (const c of Object.keys(byCounty)) {
      byCounty[c].sort((a, b) => a.y - b.y);
      const last5 = byCounty[c].slice(-5).map((p) => p.v);
      if (last5.length >= 2) {
        countySilageYieldBaseline[c] = last5.reduce((a, b) => a + b, 0) / last5.length;
      }
    }
  }

  const usdmByFips = {};         // fips → { dsci, week }
  if (signalsData && signalsData.usdmRows && signalsData.usdmRows.length) {
    const cur = signalsData.usdmRows.filter((r) => Number(r.year) === year);
    if (cur.length) {
      // Group by fips, take latest week per county
      const byF = {};
      for (const r of cur) {
        const f = String(r.fips || "").trim();
        if (!f) continue;
        const wk = Number(r.week), v = Number(r.value);
        if (!byF[f] || wk > byF[f].week) byF[f] = { dsci: v, week: wk };
      }
      Object.assign(usdmByFips, byF);
    }
  }

  const counties = [];
  for (const meta of MI_MODEL_COUNTIES) {
    const notes = [];
    const ys = (yByCty[meta.name] || []).filter((p) => p.y > 0).sort((a, b) => a.x - b.x);
    const bl = miBaseline(ys, year, SCAP);
    if (bl.baseline == null) { counties.push({ fips: meta.fips, name: meta.name, error: "no NASS yield history", forecast_yield: null }); continue; }
    const baseline = bl.baseline, slope = bl.slope, method = bl.method;
    const conf = ys.length >= 8 ? "med" : "low";
    const recentMean = miMean(ys.slice(-5).map((p) => p.y));

    const wr = wByFips[meta.fips] || [];
    let weather;
    let countyEarlyStress = null;   // per-county early-season stress factor
    if (!wr.length) {
      weather = { status: "no_weather_data", factor: 1 };
      notes.push("No weather rows yet — run the one-time backfill (POST /api/market?fn=weather-sync&backfill=1) to enable the weather factor");
    } else {
      const byDate = {};
      for (const w of wr) byDate[w.obs_date] = w;
      const climoDoy = miClimoDoy(byDate, year);
      // Per-county early-season stress — uses THIS county's own weather and
      // its own climatology. Allegany's stress can be lower than Wayne's if
      // their precip pattern was different. Falls back to the book-level
      // factor if this county has no current-year data.
      countyEarlyStress = miEarlyStress(wr, year, climoDoy);
      const hy = [...new Set(wr.map((w) => Number(w.obs_date.slice(0, 4))).filter((y) => y < year))];
      // Apply scenario overrides (Model Lab sliders) by tweaking the climatology
      // before the cohort sums it. Scenario rainAnom = +0.30 means we tell the
      // model "from here forward, rain runs 30% above the historical mean";
      // observed days (already known) are NOT modified — overrides only affect
      // future days, matching how the model should respond to a what-if.
      let scenarioClimoDoy = climoDoy;
      if (ov && (ov.heatAnom != null || ov.rainAnom != null)) {
        scenarioClimoDoy = {};
        for (const k of Object.keys(climoDoy)) {
          scenarioClimoDoy[k] = {
            precip: climoDoy[k].precip * (ov.rainAnom != null ? (1 + ov.rainAnom) : 1),
            gdd: climoDoy[k].gdd * (ov.heatAnom != null ? (1 + ov.heatAnom) : 1),
          };
        }
      }
      const cohorts = (surveyPace && surveyPace.cohorts) || [];
      if (cohorts.length && hy.length >= 3) {
        const cw = miCohortWeather(cohorts, byDate, scenarioClimoDoy, CO);
        // Season-to-date context (kept for the drilldown chips): cumulative
        // observed-only precip + GDD vs same-window historical mean.
        const _t = new Date();
        const asOf = year === _t.getFullYear()
          ? `${String(_t.getMonth() + 1).padStart(2, "0")}-${String(_t.getDate()).padStart(2, "0")}`
          : MI_SEASON.end;
        const climoFlat = { gdd: Object.fromEntries(Object.entries(climoDoy).map(([k, v]) => [k, v.gdd])), precip: Object.fromEntries(Object.entries(climoDoy).map(([k, v]) => [k, v.precip])) };
        const sAgg = miSeasonAgg(byDate, year, climoFlat, asOf);
        const hG = [], hP = [];
        for (const y of hy) { const a = miSeasonAgg(byDate, y, null, asOf); if (a.span > 0 && a.real >= a.span * 0.6) { hG.push(a.gdd); hP.push(a.precip); } }
        const nG = miMean(hG), nP = miMean(hP);
        const gA = nG ? (sAgg.gdd - nG) / nG : 0, pA = nP ? (sAgg.precip - nP) / nP : 0;
        anomGsum += gA; anomPsum += pA; anomN++;
        // Pull avg silking + grain-fill evidence across cohorts for the chip
        const avg = (k) => {
          if (!cw.breakdown.length) return 0;
          const v = cw.breakdown.map((b) => b[k] || 0).reduce((a, b) => a + b, 0) / cw.breakdown.length;
          return Math.round(v * 1000) / 1000;
        };
        weather = {
          status: "cohort", factor: cw.factor,
          model: "silking+grain-fill cohorts",
          season_gdd: miR1(sAgg.gdd), normal_gdd: nG ? miR1(nG) : null, gdd_anom: miR3(gA),
          season_precip_in: miR1(sAgg.precip), normal_precip_in: nP ? miR1(nP) : null, precip_anom: miR3(pA),
          days_to_date: sAgg.span, asof: asOf, hist_years: hy.length,
          silk_evidence_pct: Math.round(avg("silk_evidence") * 100),
          fill_evidence_pct: Math.round(avg("fill_evidence") * 100),
          cohorts_n: cw.breakdown.length,
        };
      } else {
        weather = { status: "insufficient_data", factor: 1 };
      }
    }
    const oF = ofac(meta.fips);
    // ── LIVE weather factor source (switched over 2026-05-30) ──
    // The live forecast now uses the high-def gridMET factors — per-cell weather
    // production-weighted by corn × soil (NCCPI) — when available for this county.
    // The old county-centroid POWER factors remain the FALLBACK (and are kept as
    // wF_power/esF_power for the old-vs-new comparison). gridMET uses a proper
    // 1995-2025 normal; POWER used a shorter 2010+ baseline.
    const wFPower = weather.factor || 1;
    const esFPower = (countyEarlyStress && countyEarlyStress.factor != null) ? countyEarlyStress.factor : earlyStress.factor;
    // waterF = the measured soil-water bucket factor, or null when no bucket was
    // computed for this county. Never 1-as-a-placeholder — see the mech block below.
    let wF = wFPower, esF = esFPower, wxSource = "county_power", waterF = null, bucketActive = false, mechActive = false;
    let wetTurndown = null;   // set only when the wet-side taper actually bites
    const gs = gridShadow && gridShadow.counties && gridShadow.counties[meta.fips];
    if (gs && gs.factor != null) {
      wF = gs.factor;   // now TEMPERATURE-only (GDD/VPD/heat/freeze) — moisture moved to the bucket
      // ── Soil-water bucket = THE moisture factor (2026-06-06). Replaces the old
      // cold/wet early-stress term; calibrated + 2025-holdout validated. When it's
      // present, the USDM-DSCI drag (droughtF) is also retired below to avoid
      // double-counting drought. esF carries the bucket so the existing σ_early /
      // display plumbing flows unchanged.
      if (gs.water_factor != null) { esF = gs.water_factor; waterF = gs.water_factor; bucketActive = true; }
      else if (gs.early_stress_factor != null) esF = gs.early_stress_factor;
      wxSource = gs.corn_weighted ? "grid_cornsoil" : "grid_uniform";
      // ── MECHANISTIC corn model = THE live forecast multiplier (rolled out
      // 2026-06-24). One validated weather index (per-cell process model vs each
      // cell's climatology normal) REPLACES the temperature + water + drought
      // terms — book-wide leave-one-year-out MAE 6.3 bu vs 13.9 climatology, calls
      // the abnormal years (2025/2021). esF + droughtF are neutralized so the
      // weather signal isn't double-counted, and the baseline switches to the flat
      // recent-mean (the trend baseline measured WORSE on the backtest). The old
      // cohort/bucket factors stay computed above for the diagnostic chips.
      if (gs.mech_factor != null) {
        // NO WET TURNDOWN. A hand-set taper was built here on 2026-08-13 and taken
        // straight back out: it suppressed a number by assertion, and if a season
        // is genuinely excellent the model must be allowed to say so. `wet_z` below
        // is reported but changes NOTHING — it says where a county sits relative to
        // the range our record actually covers, and that is all we can honestly
        // claim today. See MI_WET_CLIMO for why the upper edge is still unlocated.
        wF = gs.mech_factor;
        esF = 1; bucketActive = true; mechActive = true;
        // waterF is deliberately NOT reset to 1 here. It is a REPORTED DIAGNOSTIC,
        // not a term in the factor chain — esF=1 above is what keeps the bucket from
        // being applied twice (mech_factor runs its own FAO-56 bucket internally).
        // Zeroing it made water_balance.drag_pct read exactly 0.0 for every county,
        // which the situation summary then narrated as "the soil-water bucket shows
        // no drag" while the dry-down map was showing −6 to −10% on the same cells.
        // A not-applicable sentinel must never be reported as a measurement.
        wxSource = gs.corn_weighted ? "grid_mech_cornsoil" : "grid_mech";
      }
      // Unify the DISPLAYED season GDD/precip + anomalies onto the SAME high-def
      // grid (vs 1995-2025 climatology) the factor uses, so the county table
      // agrees with the Forecast & Outlook card instead of showing the legacy
      // NASA-POWER county-centroid numbers (different source AND different normal
      // — the cause of the 280-vs-350 GDD discrepancy). Observed-to-date here;
      // the hero card adds the 14-day forecast, so a small residual is expected.
      if (gs.season_gdd != null && weather && typeof weather === "object") {
        weather.season_gdd = gs.season_gdd;
        weather.normal_gdd = gs.normal_gdd;
        weather.gdd_anom = gs.gdd_anom;
        weather.season_precip_in = gs.season_precip_in;
        weather.normal_precip_in = gs.normal_precip_in;
        weather.precip_anom = gs.precip_anom;
        weather.wx_source = wxSource;
      }
    }
    // Per-county USDM drought signal — separate from precip anomaly (which
    // is week-level), DSCI captures accumulated drought conditions including
    // groundwater + soil-moisture trends. Light penalty: 0.5%/100 DSCI,
    // capped at 5%. Only kicks in at DSCI >= 50 (Abnormally Dry).
    const countyDsci = usdmByFips[meta.fips] ? usdmByFips[meta.fips].dsci : 0;
    const dsciDrag = Math.min(0.05, Math.max(0, (countyDsci - 50) / 100 * 0.005));
    // When the soil-water bucket is active it owns drought, so the coarse weekly
    // USDM-DSCI drag is retired (kept only as the fallback when the grid is absent).
    const droughtF = bucketActive ? 1 : 1 - dsciDrag;
    // Mechanistic-forecast anchor: 7-year OLYMPIC mean (drop the high + low of the last
    // 7 seasons, average the middle 5). Backtested 2026-07-06 across 84 WNY county-years as
    // the best "normal": lower MAE + bias than the flat 5-yr mean AND than linear-trend /
    // trend-Olympic (trend measured worse — confirms the 2026-06-24 call). Outlier-robust, so
    // a single crash (2025) or banner (2024) year can't distort the normal. Falls back to a
    // plain mean when <3 seasons exist. The old (non-mechanistic) path keeps its trend baseline.
    const olyVals7 = ys.slice(-7).map((p) => p.y);
    const anchorOly7 = olyVals7.length >= 3
      ? miMean([...olyVals7].sort((a, b) => a - b).slice(1, -1))
      : miMean(olyVals7);
    // ── MECHANISTIC ANCHOR (reworked 2026-08-09) ────────────────────────────────
    // Prefer RMA's PUBLISHED expected county yield; fall back to the 7-yr Olympic
    // mean of NASS, then to the trend baseline.
    // The Olympic anchor is a FLAT trailing mean: no trend term, so it lags genetic
    // gain, and it inherits whatever crash sits in its 7-year window (2016 averaged
    // 125 bu across these counties and dragged the anchor down for years after).
    // Measured bias by year with it: 2017 -25, 2018 -17, 2020 -12 bu. It also breaks
    // on thin history — Orleans has 6 NASS years, and an Olympic trim of 3 values
    // returns a single number, which is how Orleans was forecast at 226/235/209 in
    // years it made 194/186/177.
    // RMA's figure is smooth, actuarially set, published per county-year, and covers
    // every county from 2017. Backtested over 11 weather years / 138 county-years
    // (gridMET 2015-2020 backfilled specifically to test this):
    //   anchor alone, no weather      16.53  vs  18.08 Olympic
    //   leave-one-YEAR-out, params chosen out-of-fold   14.08  vs  14.62
    // It wins on every honest view. Basis note: RMA expected is INSURED-acre while
    // NASS is all-acre, so the two are not the same quantity — the residual bias is
    // real and is why this is an anchor, not a prediction.
    const rmaExp = rmaExpBy[`${meta.name}|${year}`];
    const mechAnchor = (rmaExp != null && rmaExp > 0) ? rmaExp : anchorOly7;
    const effBaseline = (mechActive && mechAnchor != null) ? mechAnchor : baseline;
    const anchorSource = (mechActive && mechAnchor != null) ? (rmaExp != null && rmaExp > 0 ? "rma_expected" : "olympic7") : "trend";
    const forecast = effBaseline * wF * esF * droughtF * paceFactor * oF;
    const hs = (hByCty[meta.name] || []).filter((p) => p.y > 0).sort((a, b) => a.x - b.x);
    const harv5 = hs.length ? miMean(hs.slice(-5).map((p) => p.y)) : null;
    // Per-county production history (bu/yr = yield × harvested acres) for the
    // last ~10 yrs — powers the "% of normal" map with an adjustable normal window.
    const acreByYr = {}; for (const p of hs) acreByYr[p.x] = p.y;
    const prodHist = ys.filter((p) => acreByYr[p.x] != null).map((p) => ({ year: p.x, bu: Math.round(p.y * acreByYr[p.x]) })).slice(-10);
    // 5-yr avg TOTAL planted corn for this county (grain + silage combined)
    const ps = (pByCty[meta.name] || []).filter((p) => p.y > 0).sort((a, b) => a.x - b.x);
    const planted5 = ps.length ? miMean(ps.slice(-5).map((p) => p.y)) : null;
    // normalProd keeps using harvested baseline so "what does a normal year
    // produce" stays apples-to-apples with NASS-reported production history.
    const normalProd = recentMean != null && harv5 != null ? recentMean * harv5 : null;
    // This year's silage acres needed (dairy-driven, relatively stable):
    //   cow_equivalents × tons/cow-equiv/yr ÷ this year's per-county silage yield
    // Cow-equivalents = milking cows + heifers × HEIFER_SILAGE_RATIO. Captures the
    // fact that heifer-heavy operations (Livingston, Wyoming) consume more silage
    // per milking cow than counties without active replacement-raising.
    // Where county heifer data is missing (most non-Census years), we estimate
    // heifers from the state heifer:cow ratio applied to county milking cows.
    // Historical-anchor silage model (Kevin 2026-05-26):
    //   this_year_silage_acres = county_5yr_silage_acres
    //                          × (this_year_cows / county_5yr_avg_cows)
    //                          × (normal_silage_yield / stressed_silage_yield)
    // Anchoring on county-historical silage acres implicitly captures exports,
    // heifer ratios, storage, and other dynamics that state-uniform demand
    // calibrations can't match per-county. Falls back to cow-demand math
    // when a county has no silage history (typically low-dairy counties).
    let silageObj = null, countySilageAcNeeded = null;
    if (cowByCounty[meta.name]) {
      const cows = cowByCounty[meta.name].head;
      const cowsAvgRecent = cowByCounty[meta.name].avgRecent;
      const countyBaseYield = countySilageYieldBaseline[meta.name] || normalSilageYield;
      // Silage yield is far more weather-robust than grain — damp its reaction to the
      // grain weather/pace stress (see MI_COEF.SILAGE_WX_DAMP). Prevents over-diverting
      // grain acres to silage in stress (esp. wet) years where silage actually holds up.
      const grainStressFactor = (esF * droughtF * wF) * paceFactor;
      const silageWxDamp = MI_COEF.SILAGE_WX_DAMP != null ? MI_COEF.SILAGE_WX_DAMP : 0.5;
      const silageYieldFactor = 1 + silageWxDamp * (grainStressFactor - 1);
      const silageYieldEst = countyBaseYield * silageYieldFactor;
      const histSilageAc = silAcByCounty[meta.name];
      if (histSilageAc != null && histSilageAc > 0 && cowsAvgRecent > 0) {
        const cowChangeFactor = cows / cowsAvgRecent;
        const yieldStressFactor = silageYieldEst > 0 ? countyBaseYield / silageYieldEst : 1;
        // × heiferExpansion: an expanding herd raises extra replacement heifers, lifting
        // silage demand beyond the milking-cow change (UNH/UMD extension formula).
        countySilageAcNeeded = histSilageAc * cowChangeFactor * heiferExpansion_top * yieldStressFactor;
        silageObj = {
          cows,
          cows_3yr_avg: Math.round(cowsAvgRecent),
          cow_change_factor: Math.round(cowChangeFactor * 1000) / 1000,
          heifer_expansion_factor: Math.round(heiferExpansion_top * 10000) / 10000,
          historical_silage_ac_3yr_avg: Math.round(histSilageAc),
          yield_normal_t_per_ac: miR1(countyBaseYield),
          yield_est_t_per_ac: miR1(silageYieldEst),
          yield_stress_factor: Math.round(yieldStressFactor * 1000) / 1000,
          acres_needed: Math.round(countySilageAcNeeded),
          acres_normal_year: Math.round(histSilageAc),
          extra_acres_vs_normal: Math.round(countySilageAcNeeded - histSilageAc),
          stress_factor: miR3(silageYieldFactor),
          yield_baseline_source: countySilageYieldBaseline[meta.name] ? "county_5yr_avg" : "state_fallback",
          method: "county_3yr_historical_anchor",
        };
      } else {
        // Fallback for counties with no silage history — use cow-demand math
        // with state-calibrated tons/cow. Keeps low-dairy counties producing
        // a sensible (small) silage prediction rather than zero.
        const heifers = heiferByCounty[meta.name]
          ? heiferByCounty[meta.name].head
          : cows * stateHeiferRatio_top;
        const cowEquiv = cows + heifers * 0.45;
        const demandTons = cowEquiv * tonsPerCowEquiv_top;
        const acresNeeded = silageYieldEst > 0 ? demandTons / silageYieldEst : null;
        countySilageAcNeeded = acresNeeded;
        silageObj = {
          cows,
          heifers: Math.round(heifers),
          cow_equivalents: Math.round(cowEquiv),
          tons_per_cow_equiv: Math.round(tonsPerCowEquiv_top * 10) / 10,
          tons_demanded: Math.round(demandTons),
          yield_est_t_per_ac: miR1(silageYieldEst),
          yield_normal_t_per_ac: miR1(countyBaseYield),
          acres_needed: acresNeeded != null ? Math.round(acresNeeded) : null,
          stress_factor: miR3(silageYieldFactor),
          method: "cow_demand_fallback_no_silage_history",
        };
      }
    }
    // Acres: prefer NASS published current-year over projection. Order:
    //   (a) NASS HARVESTED for current year (final grain — most accurate)
    //   (b) NASS PLANTED for current year − this year's silage demand
    //   (c) PROJECTION: (5-yr avg PLANTED − this year's silage) × (1 − grainPP%)
    //       Silage is dairy-priority — it goes in regardless of a slow spring, so
    //       it's subtracted FIRST and the grain prevent-plant shrink (ppFrac =
    //       1 − grainPP%) applies only to the GRAIN residual. The old form
    //       (planted5 × ppFrac − silage) wrongly PP-penalized the silage acres
    //       too, then subtracted them in full — double-hitting grain by
    //       silage × grainPP% (≈3,300 ac too few grain acres for a dairy county
    //       like Chautauqua in a slow-planting year; Kevin caught it 2026-05-30).
    //       Late-planted grain still counts — its yield drag is in paceFactor on
    //       per-acre yield, NOT a second hit on the acre count; only true PP is cut.
    // Override slider scales the planted baseline before silage subtraction.
    const nass = nassActualAcres[meta.name];
    let predAcres, acresSource;
    const silageSub = countySilageAcNeeded || 0;
    if (ov && ov.acresPct != null && planted5 != null) {
      predAcres = Math.max(0, (planted5 * (1 + ov.acresPct)) - silageSub) * ppFrac;
      acresSource = "override";
    } else if (nass && nass.harvested != null) {
      predAcres = nass.harvested;
      acresSource = "nass_harvested";
    } else if (nass && nass.planted != null) {
      predAcres = nass.planted - silageSub;
      acresSource = "nass_planted_less_silage";
    } else if (planted5 != null) {
      predAcres = Math.max(0, planted5 - silageSub) * ppFrac;
      acresSource = "projected";
    } else if (harv5 != null) {
      // Last-resort fallback: county has harvested history but no planted history.
      // Approximate planted as harvested + 5% (typical NY harvest ratio).
      predAcres = Math.max(0, (harv5 / 0.95) - silageSub) * ppFrac;
      acresSource = "projected_harvested_fallback";
    } else {
      predAcres = null;
      acresSource = null;
    }
    if (predAcres != null && predAcres < 0) predAcres = 0;
    const predProd = predAcres != null ? forecast * predAcres : null;
    if (normalProd != null && predProd != null) { prodNormSum += normalProd; prodPredSum += predProd; prodCty++; }
    counties.push({
      // ⚠ `baseline_method` MUST be derived from `anchorSource`, never from
      // `mechActive` alone. It used to read `mechActive ? "olympic-7
      // (mechanistic)" : method`, so every county row shipped the label
      // "olympic-7 (mechanistic)" beside `anchor_source: "rma_expected"` — two
      // fields on one row contradicting each other, on all 39 counties. The
      // Olympic mean is now only the FALLBACK when RMA has no expected yield
      // for the county-year (measured 2026-08-23: that is 0 of 39 counties).
      // One decision, one label.
      fips: meta.fips, name: meta.name, baseline: miR1(effBaseline),
      baseline_method: anchorSource === "rma_expected" ? "RMA expected (mechanistic)"
        : anchorSource === "olympic7" ? "olympic-7 (mechanistic)" : method,
      trend_slope: miR3(slope), recent_5yr_mean: miR1(recentMean), weather,
      // Non-null only where the wet taper actually moved the number, so a reader
      // can always see WHY a wet county came down instead of it happening quietly.
      wet_turndown: wetTurndown && (wetTurndown.scale < 1 || wetTurndown.penalty > 0) ? wetTurndown : null,
      wet_z: (gs && gs.wet_z != null) ? gs.wet_z : null,
      wx_source: wxSource, mech_factor: (gs && gs.mech_factor != null) ? miR3(gs.mech_factor) : null,
      mech_index_ratio: (gs && gs.mech_index_ratio != null) ? gs.mech_index_ratio : null,
      comfort_q: (gs && gs.comfort_q != null) ? gs.comfort_q : null,
      comfort_ww: (gs && gs.comfort_ww != null) ? gs.comfort_ww : null,
      pace_factor: miR3(paceFactor), override_factor: oF, forecast_yield: miR1(forecast),
      anchor_source: anchorSource, rma_expected: rmaExp != null ? miR1(rmaExp) : null,
      effective_baseline: effBaseline != null ? miR1(effBaseline) : null,
      vs_recent_mean: recentMean ? miR1(forecast - recentMean) : null, confidence: conf, notes,
      hist_low: ys.length ? miR1(Math.min(...ys.map((p) => p.y))) : null,
      hist_high: ys.length ? miR1(Math.max(...ys.map((p) => p.y))) : null,
      hist_n: ys.length,
      // ── OVERREACH: null unless this county's number is standing somewhere the
      // model cannot actually see. Two independent tells, neither an error:
      //
      //   above_record  the forecast exceeds the county's own best year on record.
      //
      // `pegged` IS GONE (2026-08-15). It reported that the weather factor had
      // been CLIPPED at FACTOR_CEIL — "the printed factor is the cap, not a
      // measurement" — which was an honest thing to say while a hard clamp
      // existed. The response is now a saturating curve with no clamp (see
      // MI_CORN.RESP_*), so there is no cap to sit on and nothing to disclose.
      // Removing the badge without removing the clamp would have been hiding it;
      // the clamp went first.
      //
      // WHY THIS IS A FOOTNOTE AND NOT A WARNING. It was built as a warning, on
      // the theory that the wettest counties peg the ceiling because the model has
      // no penalty for surplus water. The first half is true; the conclusion was
      // not, and the check is worth keeping because it is easy to re-derive the
      // wrong way round. Over 154 WNY county-years (2015-2025, `c:/tmp/wetacres.mjs`):
      //
      //   precip anomaly vs county yield        r = +0.535
      //   precip anomaly vs abandoned acres     r = -0.073
      //   very dry (-27%) 135.8 bu   normal 158.3   wet (+24%) 163.8   very wet (+46%) 174.9
      //
      // Wetter is BETTER here, monotonically, and it does not cost acres either —
      // abandonment is worst in DROUGHT (6.98% vs ~5%). Western NY is water
      // limited; drought is the yield risk, surplus is not. That is also the real
      // reason a waterlogging arm has failed to fit three separate times: there is
      // no penalty in the record to fit, so rejecting it was correct.
      //
      // What legitimately remains is narrow: for two counties 2026 is past the
      // observed range entirely (Orleans +74%, Niagara +87% season precip vs a
      // wettest-on-record +71% — Niagara 2017, which yielded 176.2, a GOOD year).
      // Beyond that range nobody knows, the record included. So: name it, don't
      // alarm about it.
      overreach: (() => {
        // RESTORED with the clamp (2026-08-16). The saturating response that made this
        // unnecessary was reverted the same day — see MI_CORN.RESP_*. While a hard
        // ceiling is live, a county sitting on it is showing a CAP, not a
        // measurement, and that has to be said.
        const mf = gs && gs.mech_factor != null ? gs.mech_factor : null;
        const pegged = mf != null && mf >= MI_CORN.FACTOR_CEIL - 1e-9;
        // Compare against the record on the SAME basis the forecast is built on.
        // The anchor is RMA expected (insured acre), so RMA final is the matching
        // record; NASS is the all-acre fallback and is LABELLED as such, because
        // the two run tens of bushels apart on the same county-year.
        const rf = rmaFinalBest[String(meta.name).toUpperCase().trim()] || null;
        const nassRec = ys.length ? Math.max(...ys.map((p) => p.y)) : null;
        const rec = rf ? rf.y : nassRec;
        const recYear = rf ? rf.year : (nassRec != null ? (ys.find((p) => p.y === nassRec) || {}).x : null);
        const basis = rf ? "rma_final" : "nass";
        const above = rec != null && forecast != null && forecast > rec;
        if (!pegged && !above) return null;
        return {
          pegged, above_record: above,
          ceil: pegged ? MI_CORN.FACTOR_CEIL : null,
          index_ratio: gs && gs.mech_index_ratio != null ? miR3(gs.mech_index_ratio) : null,
          record: rec != null ? miR1(rec) : null, record_year: recYear != null ? recYear : null,
          record_basis: basis, over_by: above ? miR1(forecast - rec) : null,
          nass_record: nassRec != null ? miR1(nassRec) : null,
          years_on_record: ys.length,
        };
      })(),
      harv_acres_5yr: harv5 != null ? Math.round(harv5) : null,
      planted_acres_5yr: planted5 != null ? Math.round(planted5) : null,
      pred_acres: predAcres != null ? Math.round(predAcres) : null,
      acres_source: acresSource,
      // Silage subtracted to arrive at grain prediction (this year's dairy
      // demand ÷ this year's silage yield). Shown in drilldown so the path
      // from "total planted" to "predicted grain" is transparent.
      silage_displaced_grain_ac: countySilageAcNeeded != null ? Math.round(countySilageAcNeeded) : 0,
      // Per-county early stress factor + drag — visible in drilldown so the
      // agent can see "this county's stress is X% off, while Wayne is Y%."
      early_stress: countyEarlyStress ? {
        factor: countyEarlyStress.factor,
        drag_pct: countyEarlyStress.drag_pct,
        stress_days: countyEarlyStress.stress_days,
        recovery_days: countyEarlyStress.recovery_days,
        net_stress: countyEarlyStress.net_stress,
      } : null,
      drought: {
        dsci: countyDsci,
        week: usdmByFips[meta.fips] ? usdmByFips[meta.fips].week : null,
        category: countyDsci >= 400 ? "D4 Exceptional" : countyDsci >= 300 ? "D3 Extreme" : countyDsci >= 200 ? "D2 Severe" : countyDsci >= 100 ? "D1 Moderate" : countyDsci >= 50 ? "D0 Abnormally Dry" : "None",
        factor: droughtF,
        drag_pct: Math.round(dsciDrag * 1000) / 10,
        retired_by_bucket: bucketActive,   // when true, droughtF=1; the soil-water bucket owns drought
      },
      // Soil-water bucket — THE moisture factor (FAO-56 daily water balance, 2026-06-06).
      // Re-centered (normal season ~1.0); drags only as the soil-water bank draws
      // below the readily-available threshold and accelerates as it empties. Glides
      // (evidence-gated), never whiplashes. See MI_COEF BKT_*.
      // Emitted whenever a bucket was actually MEASURED (waterF != null) — not when
      // it's "active in the factor chain". Under the mechanistic model the bucket is
      // measured but not separately applied (mech carries its own), so `applied` says
      // which. Consumers that narrate moisture must read factor/drag_pct as the real
      // state of the soil and use `applied` only to explain double-counting, never to
      // conclude the ground is wet.
      water_balance: waterF != null ? {
        factor: waterF,
        drag_pct: Math.round((1 - waterF) * 1000) / 10,
        applied: !mechActive,
      } : null,
      // Composite weather factor = early_stress × drought × silking. The single
      // headline weather number combining all weather-related drags on this
      // county. Today: dominated by early-stress (silking still unknown).
      // Later in season: silking weather (wF) takes over as evidence accrues.
      total_wx_factor: miR3(esF * droughtF * wF),
      // Which weather engine produced this county's live factor + the old-vs-new
      // comparison. wx_source: grid_cornsoil (high-def, corn×soil weighted) /
      // grid_uniform (grid, partial CDL → uniform) / county_power (old fallback).
      wx_source: wxSource,
      wF_power: miR3(wFPower), esF_power: miR3(esFPower),
      wF_grid: gs ? gs.factor : null, esF_grid: gs ? gs.early_stress_factor : null,
      mean_nccpi: gs ? gs.mean_nccpi : null,
      // Per-county dairy cow inventory (NASS latest year). Drives silage-acre
      // demand; visible as a map data layer.
      dairy_cows: cowByCounty[meta.name] ? cowByCounty[meta.name].head : null,
      dairy_cows_year: cowByCounty[meta.name] ? cowByCounty[meta.name].year : null,
      // Per-county silage demand chain — uses cow-equivalent count to
      // properly weight heifer-heavy operations. Single source of truth: this
      // is the SAME silage acreage subtracted from total-planted to derive
      // grain acres in the pred_acres calculation above.
      silage: silageObj,
      pred_prod: predProd != null ? Math.round(predProd) : null,
      normal_prod: normalProd != null ? Math.round(normalProd) : null,
      prod_hist: prodHist,
      // Last ~10 yrs of county yield (bu/ac) for the high-def % of normal map's
      // adjustable normal window (3/5/7/10-yr).
      yld_series: ys.slice(-10).map((p) => ({ year: p.x, y: p.y })),
      yld_hist: ys.slice(-5).map((p) => ({ y: p.x, v: miR1(p.y) })),
      acre_hist: hs.slice(-5).map((p) => ({ y: p.x, v: Math.round(p.y) })),
    });
  }

  // ── Book-level early-stress: weighted average of per-county factors.
  // Replaces the WNY-aggregated book-level number for the brief + summary.
  // If a county has its own factor, that wins; otherwise the WNY-agg book
  // factor fills in. Surface as the "headline" early-stress for the summary.
  {
    const countyESs = counties.filter((c) => c.early_stress && c.early_stress.factor != null);
    if (countyESs.length) {
      // Simple mean across reporting counties (acre-weighting would be even
      // better but harv5 isn't always present and this is close enough).
      const factors = countyESs.map((c) => c.early_stress.factor);
      const drags = countyESs.map((c) => c.early_stress.drag_pct || 0);
      const obsDrags = countyESs.map((c) => c.early_stress.obs_drag_pct != null ? c.early_stress.obs_drag_pct : (c.early_stress.drag_pct || 0));
      const stresses = countyESs.map((c) => c.early_stress.stress_days || 0);
      const recoveries = countyESs.map((c) => c.early_stress.recovery_days || 0);
      const fStresses = countyESs.map((c) => c.early_stress.forecast_stress || 0);
      const fRecoveries = countyESs.map((c) => c.early_stress.forecast_recovery || 0);
      const fDayCounts = countyESs.map((c) => c.early_stress.forecast_days || 0);
      const mean = (a) => a.reduce((s, v) => s + v, 0) / a.length;
      const fStressM = mean(fStresses), fRecovM = mean(fRecoveries);
      earlyStress = {
        ...earlyStress,
        factor: mean(factors),
        drag_pct: Math.round(mean(drags) * 10) / 10,
        obs_drag_pct: Math.round(mean(obsDrags) * 10) / 10,
        stress_days: Math.round(mean(stresses) * 10) / 10,
        recovery_days: Math.round(mean(recoveries) * 10) / 10,
        forecast_stress: Math.round(fStressM * 10) / 10,
        forecast_recovery: Math.round(fRecovM * 10) / 10,
        forecast_days: Math.round(Math.max(...fDayCounts)),
        forecast_outlook: Math.max(...fDayCounts) ? (fRecovM - fStressM > 0.3 ? "improving" : fStressM - fRecovM > 0.3 ? "worsening" : "steady") : null,
        county_n: countyESs.length,
        county_factor_range: [Math.min(...factors), Math.max(...factors)].map((v) => Math.round(v * 1000) / 1000),
      };
    }
  }
  // ── Book-level cohort weather summary (for the UI chart) ──
  // Uses WNY-aggregated weather (avg across the 16 counties' wxRows) so the
  // chart shows ONE silking-window timeline that represents the book. Per-
  // county weather factors above stay per-county; this is just the rollup.
  const bookCohortSummary = (() => {
    if (!surveyPace || !surveyPace.cohorts || !surveyPace.cohorts.length) return null;
    // Build WNY-avg byDate (mean across counties for each date)
    const acc = {};
    for (const w of wxRows || []) {
      const k = w.obs_date;
      const a = acc[k] || (acc[k] = { tmaxS: 0, tmaxN: 0, tminS: 0, tminN: 0, pS: 0, pN: 0, fc: !!w.is_forecast });
      if (w.tmax_f != null) { a.tmaxS += Number(w.tmax_f); a.tmaxN++; }
      if (w.tmin_f != null) { a.tminS += Number(w.tmin_f); a.tminN++; }
      if (w.precip_in != null) { a.pS += Number(w.precip_in); a.pN++; }
      if (w.is_forecast) a.fc = true;
    }
    const wnyByDate = {};
    for (const k of Object.keys(acc)) {
      wnyByDate[k] = {
        obs_date: k,
        tmax_f: acc[k].tmaxN ? acc[k].tmaxS / acc[k].tmaxN : null,
        tmin_f: acc[k].tminN ? acc[k].tminS / acc[k].tminN : null,
        precip_in: acc[k].pN ? acc[k].pS / acc[k].pN : null,
        is_forecast: acc[k].fc,
      };
    }
    const climoDoy = miClimoDoy(wnyByDate, year);
    const cw = miCohortWeather(surveyPace.cohorts, wnyByDate, climoDoy, CO);
    // Sort breakdown by plant date for charting
    cw.breakdown.sort((a, b) => a.plant_date.localeCompare(b.plant_date));
    // Bucket into weekly cohorts for charting (raw breakdown has ~30+ rows)
    const weekly = {};
    for (const b of cw.breakdown) {
      const dt = new Date(b.plant_date + "T00:00:00");
      // Sunday-anchored week key
      const day = dt.getDay();
      const wk = new Date(dt); wk.setDate(wk.getDate() - day);
      const key = `${wk.getFullYear()}-${String(wk.getMonth() + 1).padStart(2, "0")}-${String(wk.getDate()).padStart(2, "0")}`;
      const w = weekly[key] || (weekly[key] = { week_start: key, acres: 0, factorSum: 0, projected_acres: 0, planted_acres: 0, silkA_p: 0, silkA_g: 0, fillA_p: 0, fillA_g: 0, evidence: 0, vpdStress: 0, vpdMax: 0 });
      w.acres += b.acres;
      w.factorSum += b.factor * b.acres;
      if (b.projected) w.projected_acres += b.acres; else w.planted_acres += b.acres;
      w.silkA_p += b.silk_precip_anom * b.acres;
      w.silkA_g += b.silk_gdd_anom * b.acres;
      w.fillA_p += b.fill_precip_anom * b.acres;
      w.fillA_g += b.fill_gdd_anom * b.acres;
      w.evidence += b.silk_evidence * b.acres;
      w.vpdStress += (b.silk_vpd_stress_days || 0) * b.acres;
      if ((b.silk_vpd_max_kpa || 0) > w.vpdMax) w.vpdMax = b.silk_vpd_max_kpa || 0;
    }
    const weeklyArr = Object.values(weekly).sort((a, b) => a.week_start.localeCompare(b.week_start)).map((w) => ({
      week_start: w.week_start,
      acres: Math.round(w.acres),
      planted_acres: Math.round(w.planted_acres),
      projected_acres: Math.round(w.projected_acres),
      factor: w.acres > 0 ? miR3(w.factorSum / w.acres) : 1,
      silk_precip_anom: w.acres > 0 ? miR3(w.silkA_p / w.acres) : 0,
      silk_gdd_anom: w.acres > 0 ? miR3(w.silkA_g / w.acres) : 0,
      fill_precip_anom: w.acres > 0 ? miR3(w.fillA_p / w.acres) : 0,
      fill_gdd_anom: w.acres > 0 ? miR3(w.fillA_g / w.acres) : 0,
      silk_evidence: w.acres > 0 ? miR3(w.evidence / w.acres) : 0,
      silk_vpd_stress_days: w.acres > 0 ? Math.round(w.vpdStress / w.acres) : 0,
      silk_vpd_max_kpa: miR3(w.vpdMax),
    }));
    return {
      weekly: weeklyArr,
      total_ac: cw.totalAc, planted_ac: cw.planted_ac, projected_ac: cw.projected_ac,
      book_factor: cw.factor,
      n_cohorts: cw.breakdown.length,
    };
  })();

  // ── Weather pulse — WNY-aggregated last-14d and next-14d numbers for the
  // prominent "Spring Conditions" card. Built from the same wxRows.
  const wxPulse = (() => {
    if (!wxRows || !wxRows.length) return null;
    const t = new Date(); t.setHours(0, 0, 0, 0);
    const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    // Bucket by date → WNY-avg precip & GDD (avg across whatever counties reported)
    const byDate = {};
    for (const w of wxRows) {
      const k = w.obs_date;
      const r = byDate[k] || (byDate[k] = { p: 0, pN: 0, g: 0, gN: 0, fc: !!w.is_forecast });
      if (w.precip_in != null) { r.p += Number(w.precip_in); r.pN++; }
      const g = miModGdd(w.tmax_f, w.tmin_f);
      if (g != null) { r.g += g; r.gN++; }
      if (w.is_forecast) r.fc = true;
    }
    const wnyP = (d) => { const r = byDate[ymd(d)]; return r && r.pN ? r.p / r.pN : null; };
    const wnyG = (d) => { const r = byDate[ymd(d)]; return r && r.gN ? r.g / r.gN : null; };
    // Last-14d observed
    let p14 = 0, p14n = 0, g14 = 0, g14n = 0;
    for (let k = 1; k <= 14; k++) { const d = new Date(t); d.setDate(d.getDate() - k); const p = wnyP(d), g = wnyG(d); if (p != null) { p14 += p; p14n++; } if (g != null) { g14 += g; g14n++; } }
    // Next-14d forecast + workable count
    const fcDays = []; let pNext = 0, pNextN = 0; let workable = 0; let blocked = 0;
    const rainBlock = MI_CORN_PLANTING.rainBlockIn != null ? MI_CORN_PLANTING.rainBlockIn : 0.3;
    const soak = MI_CORN_PLANTING.soakIn != null ? MI_CORN_PLANTING.soakIn : 0.5;
    const dry = MI_CORN_PLANTING.drydownDays != null ? MI_CORN_PLANTING.drydownDays : 2;
    for (let k = 0; k < 14; k++) {
      const d = new Date(t); d.setDate(d.getDate() + k);
      const p = wnyP(d);
      fcDays.push({ d: ymd(d), p: p == null ? null : Math.round(p * 100) / 100 });
      if (p != null) { pNext += p; pNextN++; }
      let blockedToday = false;
      if (p != null && p >= rainBlock) blockedToday = true;
      else {
        for (let j = 1; j <= dry; j++) {
          const pd = new Date(d); pd.setDate(pd.getDate() - j);
          const pp = wnyP(pd); if (pp != null && pp >= soak) { blockedToday = true; break; }
        }
      }
      if (blockedToday) blocked++; else workable++;
    }
    // Same windows in prior years (Apr-1 → today) for the % anomaly
    const seasonStartMM = MI_SEASON.start; // "04-20"
    const [sm, sd] = seasonStartMM.split("-").map(Number);
    const seasonStart = new Date(year, sm - 1, sd);
    let sP = 0, sPn = 0, sG = 0, sGn = 0;
    for (let d = new Date(seasonStart); d <= t; d.setDate(d.getDate() + 1)) {
      const p = wnyP(d), g = wnyG(d);
      if (p != null) { sP += p; sPn++; }
      if (g != null) { sG += g; sGn++; }
    }
    const histYrs = [...new Set(wxRows.map((w) => Number(w.obs_date.slice(0, 4))).filter((y) => y < year && y >= year - 11))];
    const hPs = [], hGs = [];
    for (const yy of histYrs) {
      const hs = new Date(yy, sm - 1, sd);
      const he = new Date(yy, t.getMonth(), t.getDate());
      let pp = 0, ppn = 0, gg = 0, ggn = 0;
      for (let d = new Date(hs); d <= he; d.setDate(d.getDate() + 1)) {
        const p = wnyP(d), g = wnyG(d);
        if (p != null) { pp += p; ppn++; }
        if (g != null) { gg += g; ggn++; }
      }
      if (ppn > 0) hPs.push(pp);
      if (ggn > 0) hGs.push(gg);
    }
    const normP = hPs.length ? hPs.reduce((a, b) => a + b, 0) / hPs.length : null;
    const normG = hGs.length ? hGs.reduce((a, b) => a + b, 0) / hGs.length : null;
    return {
      season_precip_in: miR1(sP), season_precip_normal_in: normP != null ? miR1(normP) : null, season_precip_anom: normP ? miR3((sP - normP) / normP) : null,
      season_gdd: Math.round(sG), season_gdd_normal: normG != null ? Math.round(normG) : null, season_gdd_anom: normG ? miR3((sG - normG) / normG) : null,
      last14_precip_in: miR1(p14), last14_gdd: Math.round(g14),
      next14_precip_in: miR1(pNext), next14_days_forecast: pNextN,
      next14_workable: workable, next14_blocked: blocked,
      next14_daily: fcDays,
      asof: ymd(t),
    };
  })();

  // ── Rain since the crop went in the ground ────────────────────────────────
  // Acre-weighted across the planting cohorts: each cohort is credited only with
  // the rain that fell AFTER it was planted, measured against the day-matched
  // normal for that same window. This is the honest "is the standing crop behind
  // on water?" number. season_precip_anom (from 04-20) is NOT that number — it
  // banks pre-plant rain the crop never drank, so a wet spring reads as a surplus
  // even when every planted acre is running dry.
  const precipSincePlant = (() => {
    const pulse = (gridShadow && gridShadow.wx_pulse) || wxPulse;
    const ladder = (pulse && pulse.precip_since_by_week) || null;
    if (!ladder || !ladder.length) return null;
    // Nearest ladder week at/before the cohort's plant week (cohorts are weekly,
    // so this is normally an exact hit; the scan just tolerates any drift).
    const pick = (ws) => {
      const tt = new Date(ws).getTime();
      if (isNaN(tt)) return null;
      let best = null, bestT = -Infinity;
      for (const e of ladder) {
        const et = new Date(e.week_start).getTime();
        if (et <= tt && et > bestT) { best = e; bestT = et; }
      }
      return best || ladder[0];
    };
    let on = 0, nn = 0, ad = 0;
    for (const w of ((bookCohortSummary && bookCohortSummary.weekly) || [])) {
      if (!w.week_start || !(w.acres > 0)) continue;
      const e = pick(w.week_start);
      if (!e || !(e.normal_in > 0)) continue;
      on += e.obs_in * w.acres; nn += e.normal_in * w.acres; ad += w.acres;
    }
    if (!(ad > 0) || !(nn > 0)) return null;
    const obs = on / ad, nor = nn / ad;
    return {
      obs_in: miR1(obs),
      normal_in: miR1(nor),
      pct_of_normal: Math.round((obs / nor) * 100),
      anom_pct: Math.round((obs / nor - 1) * 100),
      acres_weighted: Math.round(ad),
    };
  })();

  // ── Analog Years ──────────────────────────────────────────────────────────
  // For each historical year, compute its "spring signature" = (precip_anom,
  // gdd_anom) from season-start through this calendar date. Find the closest
  // matches to 2026 by normalized Euclidean distance, then pair each with
  // that year's actual final WNY-avg corn yield. The model output is "we
  // think yield will be X"; analog years say "when this pattern showed up
  // before, here's what actually happened." Single most credible thing on the
  // page for an outside audience because it's not a model, it's history.
  const analogYears = (() => {
    if (!wxRows || !wxRows.length) return null;
    const t = new Date(); t.setHours(0, 0, 0, 0);
    const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    const [sm, sd] = MI_SEASON.start.split("-").map(Number);
    // Per-day WNY-avg precip + GDD, all years observed only
    const byDate = {};
    for (const w of wxRows) {
      if (w.is_forecast) continue;
      const k = w.obs_date;
      const a = byDate[k] || (byDate[k] = { p: 0, pN: 0, g: 0, gN: 0 });
      if (w.precip_in != null) { a.p += Number(w.precip_in); a.pN++; }
      const g = miModGdd(w.tmax_f, w.tmin_f);
      if (g != null) { a.g += g; a.gN++; }
    }
    const wnyP = (d) => { const r = byDate[ymd(d)]; return r && r.pN ? r.p / r.pN : null; };
    const wnyG = (d) => { const r = byDate[ymd(d)]; return r && r.gN ? r.g / r.gN : null; };
    const sigForYear = (yr) => {
      const start = new Date(yr, sm - 1, sd);
      const end = yr === year ? t : new Date(yr, t.getMonth(), t.getDate());
      let p = 0, g = 0, pN = 0, gN = 0;
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const pp = wnyP(d), gg = wnyG(d);
        if (pp != null) { p += pp; pN++; }
        if (gg != null) { g += gg; gN++; }
      }
      return pN >= 30 && gN >= 30 ? { p, g, days: pN } : null;     // need at least 30 days of data
    };
    const curSig = sigForYear(year);
    if (!curSig) return null;
    // Build WNY-avg actual yield by year from NASS yieldRows
    const ySum = {}, yN = {};
    for (const r of yieldRows || []) {
      const yr = Number(r.year), v = Number(r.value);
      if (!yr || !(v > 0)) continue;
      ySum[yr] = (ySum[yr] || 0) + v;
      yN[yr] = (yN[yr] || 0) + 1;
    }
    const yByYear = {};
    for (const yr of Object.keys(ySum)) yByYear[yr] = ySum[yr] / yN[yr];
    // Score each historical year
    const candidates = [];
    const histYrs = [...new Set(wxRows.map((w) => Number(w.obs_date.slice(0, 4))).filter((y) => y < year && y >= year - 11))];
    for (const yr of histYrs) {
      const sig = sigForYear(yr);
      if (!sig) continue;
      // Normalize both signatures into ratios so the distance is in "% off"
      // space (precip totals can swamp gdd totals otherwise).
      const pRatio = curSig.p > 0 ? sig.p / curSig.p - 1 : 0;
      const gRatio = curSig.g > 0 ? sig.g / curSig.g - 1 : 0;
      const dist = Math.sqrt(pRatio * pRatio + gRatio * gRatio);
      const finalYield = yByYear[yr] != null ? miR1(yByYear[yr]) : null;
      candidates.push({
        year: yr,
        precip_in: miR1(sig.p), precip_diff_pct: Math.round(pRatio * 100),
        gdd: Math.round(sig.g), gdd_diff_pct: Math.round(gRatio * 100),
        days: sig.days,
        final_yield: finalYield,
        distance: miR3(dist),
      });
    }
    candidates.sort((a, b) => a.distance - b.distance);
    const topN = candidates.slice(0, 5);
    // Implied yield from the top analogs (yield-weighted, inverse-distance)
    const withYield = topN.filter((c) => c.final_yield != null);
    let impliedYield = null, impliedRange = null;
    if (withYield.length >= 2) {
      // Inverse-distance weighting; floor distance to avoid div-by-zero on
      // a perfect match.
      const wts = withYield.map((c) => 1 / Math.max(0.01, c.distance));
      const wSum = wts.reduce((a, b) => a + b, 0);
      impliedYield = miR1(withYield.reduce((s, c, i) => s + c.final_yield * wts[i], 0) / wSum);
      const ys = withYield.map((c) => c.final_yield);
      impliedRange = { lo: miR1(Math.min(...ys)), hi: miR1(Math.max(...ys)) };
    }
    return {
      cur: {
        precip_in: miR1(curSig.p), gdd: Math.round(curSig.g), days: curSig.days, asof: ymd(t),
      },
      analogs: topN,
      implied_yield: impliedYield,
      implied_range: impliedRange,
      n_with_yield: withYield.length,
    };
  })();

  // Silage subtraction is now baked into pred_acres up-front in the per-county
  // loop (Kevin 2026-05-26): predAcres = planted5 × ppFrac − countySilageAcNeeded.
  // The old displacement loop subtracted "extra acres above a normal year" on
  // top of a grain-only baseline — double-counting silage and making the math
  // opaque. Book-level cs is still computed for the cow_silage payload.
  // ── THE DAIRY HERD IS PER REGION, AND IT IS COMPUTED HERE, ONCE ────────────
  // A region's silage demand is its OWN herd against its OWN weather. Both were
  // WNY before: the herd off a hardcoded county list, and the weather factor the
  // mean across every modelled county. So "All New York" printed Western NY's
  // 298k cows — a 39-county book showing a 16-county herd — and nothing in the
  // page or the email could tell you it had.
  //
  // Computed for every region here rather than in miFilterResult, because the
  // herd needs the raw NASS rows and those exist only during a model run. The
  // filter picks; it does not recompute. A region whose herd cannot be resolved
  // gets null, and every surface then omits the card instead of showing another
  // region's cows under its name.
  const regionWF = (names) => {
    const f = counties
      .filter((c) => names.has(String(c.name || "").toUpperCase()))
      .filter((c) => c.weather && c.weather.factor != null && c.weather.status !== "no_weather_data")
      .map((c) => c.weather.factor);
    return f.length ? f.reduce((a, b) => a + b, 0) / f.length : null;
  };
  const cowSilageByRegion = {};
  if (silData) {
    for (const [key, names] of Object.entries(MI_REGION_NAMES)) {
      cowSilageByRegion[key] = miCowSilage(silData.silAcRows, silData.silYldRows, silData.cowRows, year, regionWF(names), names) || null;
    }
  }
  // The unfiltered result is STATEWIDE, the same contract mi_model_snapshot.payload
  // follows — so the default here is All New York, not the book's corner of it.
  const cs = cowSilageByRegion.ny || null;
  // Book-level silage displacement fraction = total silage acres ÷ total planted acres
  // (weighted across counties). Replaces the old "extra-above-normal" framing.
  let totalSilageSub = 0, totalPredAc = 0;
  counties.forEach((c) => {
    if (c.pred_acres == null) return;
    totalSilageSub += c.silage_displaced_grain_ac || 0;
    totalPredAc += c.pred_acres + (c.silage_displaced_grain_ac || 0);  // grain + silage = total corn this year
  });
  const silageDisplaceFrac = totalPredAc > 0 ? totalSilageSub / totalPredAc : 0;
  // State-grain-acre baseline (sum of 5-yr-mean ACRES_HARVESTED grain across
  // all NY counties in harvRows) — still needed for the grainShare fallback
  // heuristic used when surveyPace doesn't have late-grain / PP breakdown.
  const _byC = {};
  for (const r of harvRows || []) { const nm = String(r.county || "").toUpperCase().trim(); const v = Number(r.value); if (!(v > 0)) continue; (_byC[nm] = _byC[nm] || []).push({ x: Number(r.year), y: v }); }
  let nyGrainAcres = 0;
  Object.values(_byC).forEach((arr) => { arr.sort((a, b) => a.x - b.x); const m5 = miMean(arr.slice(-5).map((p) => p.y)); if (m5) nyGrainAcres += m5; });
  // Prevent-plant + late-silage %s — now computed directly from the
  // willingness-driven projection (Kevin's spec). PP = grain acres past
  // their willingness window (June 20 cutoff). Late silage = silage acres
  // planted in the post-FPD willingness window (Jun 10 → Jul 5).
  // Fallback to the older grain-share heuristic if surveyPace doesn't have
  // the new fields (in case of partial data).
  // ── BOTH SIDES OF THIS RATIO MUST BE THE SAME FOOTPRINT ────────────────────
  // `nyGrainAcres` is summed over the model's counties (39). `cs.normalAcres` is
  // the normal SILAGE acres for whatever region `cs` covers — and `cs` used to be
  // Western NY, so this divided 39 counties' grain acres by 16 counties' silage
  // acres and read 75.1%. Matched footprints it is 60.1%, which is the number to
  // expect against NASS (NY grain harvested ~600k ac vs silage ~450-500k).
  //
  // The move to 60.1 was a SIDE EFFECT of making `cs` statewide, not a deliberate
  // fix, and it went unnoticed for a commit — `cs` reads as a payload-only value
  // and this is its one other consumer. It is also the reason the Yield page's
  // "NY grain share of all corn" line changed. Production is NOT affected: the
  // ppPct / lateSilagePct uses below are a FALLBACK that only runs when
  // surveyPace lacks its projection fields, and it does not (late_grain_pct is
  // non-zero, which the fallback would force to 0).
  const grainShare = cs && cs.normalAcres > 0 && nyGrainAcres > 0 ? nyGrainAcres / (nyGrainAcres + cs.normalAcres) : 1;
  const lateFrac = surveyPace && surveyPace.plantedByFpdFrac != null ? Math.max(0, 1 - surveyPace.plantedByFpdFrac) : 0;
  // ── Acre buckets that sum to 100% of intended acres ──
  // by-FPD = already-planted + projected-before-FPD (grain + silage)
  // late grain = projected grain planted after FPD (Jun 10 – Jun 20)
  // late silage = projected silage planted after FPD (Jun 10 – Jul 5)
  // PP = grain acres abandoned past June 20 (willingness drops to 0)
  // (silage shortfall, when present, fills in the rest — usually tiny)
  let ppPct, lateGrainPct, lateSilagePct, silageShortfallPct;
  if (surveyPace && surveyPace.intendedAc > 0 && surveyPace.ppAc != null && surveyPace.projGrainAfterFpd != null) {
    const I = surveyPace.intendedAc;
    ppPct = Math.round((surveyPace.ppAc / I) * 1000) / 10;
    lateGrainPct = Math.round(((surveyPace.projGrainAfterFpd || 0) / I) * 1000) / 10;
    lateSilagePct = Math.round(((surveyPace.projSilageAfterFpd || 0) / I) * 1000) / 10;
    silageShortfallPct = Math.round(((surveyPace.silageShortfallAc || 0) / I) * 1000) / 10;
  } else {
    // Fallback: old grain-share heuristic
    ppPct = Math.round(lateFrac * grainShare * 1000) / 10;
    lateGrainPct = 0;
    lateSilagePct = Math.round(lateFrac * (1 - grainShare) * 1000) / 10;
    silageShortfallPct = 0;
  }

  // ── WNY weekly GDD & rainfall series for the Model Lab charts ──
  // Apr-20 → today (+14-day forecast tail) vs the prior-years normal, WNY-
  // averaged across the 16 counties. Built from wxRows already in memory.
  // UTC date stepping so weekly buckets don't drift on DST.
  const wxChart = (() => {
    if (!wxRows || !wxRows.length) return null;
    const ymd = (dt) => dt.toISOString().slice(0, 10);
    const nowT = new Date();
    const isCurYr = year === nowT.getFullYear();
    const asOfUTC = isCurYr
      ? Date.UTC(nowT.getFullYear(), nowT.getMonth(), nowT.getDate())
      : Date.UTC(year, 8, 30); // Sep 30 of a past year
    const [sm, sd] = MI_SEASON.start.split("-").map(Number);
    // WNY-average per calendar date (avg across whatever counties reported)
    const byDate = {};
    for (const w of wxRows) {
      const yr = Number(w.obs_date.slice(0, 4));
      if (yr < year - 10 || yr > year) continue;
      const g = miModGdd(w.tmax_f, w.tmin_f);
      const r = byDate[w.obs_date] || (byDate[w.obs_date] = { g: 0, gN: 0, p: 0, pN: 0 });
      if (g != null) { r.g += g; r.gN++; }
      if (w.precip_in != null) { r.p += Number(w.precip_in); r.pN++; }
    }
    const WEEKS = 15; // ~Apr 20 → early Aug — covers planting + early growth
    function curve(yr, cur) {
      const startUTC = Date.UTC(yr, sm - 1, sd);
      const endUTC = cur ? asOfUTC + 14 * 864e5 : Date.UTC(yr, 8, 30);
      let cg = 0, cp = 0; const out = [];
      for (let wk = 0; wk <= WEEKS; wk++) {
        const wkStartUTC = startUTC + wk * 7 * 864e5;
        if (wkStartUTC > endUTC) break;
        let any = false;
        for (let k = 0; k < 7; k++) {
          const dUTC = wkStartUTC + k * 864e5;
          if (dUTC > endUTC) break;
          const rec = byDate[ymd(new Date(dUTC))];
          if (!rec) continue;
          any = true;
          if (rec.gN) cg += rec.g / rec.gN;
          if (rec.pN) cp += rec.p / rec.pN;
        }
        if (any) out.push({ label: `${new Date(wkStartUTC).getUTCMonth() + 1}/${new Date(wkStartUTC).getUTCDate()}`, gdd: Math.round(cg), pr: Math.round(cp * 10) / 10, fc: cur && wkStartUTC > asOfUTC });
      }
      return out;
    }
    const cur = curve(year, true);
    const priors = [];
    for (let y = year - 1; y >= year - 10; y--) { const c = curve(y, false); if (c.length) priors.push(c); }
    const maxW = Math.max(cur.length, ...priors.map((c) => c.length), 0);
    const normal = [];
    for (let i = 0; i < maxW; i++) {
      const gs = priors.map((c) => c[i] && c[i].gdd).filter((v) => v != null);
      const ps = priors.map((c) => c[i] && c[i].pr).filter((v) => v != null);
      normal.push({
        label: (cur[i] && cur[i].label) || (priors[0] && priors[0][i] && priors[0][i].label) || "",
        gdd: gs.length ? Math.round(gs.reduce((a, b) => a + b, 0) / gs.length) : null,
        pr: ps.length ? Math.round(ps.reduce((a, b) => a + b, 0) / ps.length * 10) / 10 : null,
      });
    }
    return cur.length ? { cur, normal } : null;
  })();


  // ── Live Signals (NASS soil moisture, NASS crop condition, USDM drought)──
  // Single-glance health panel — independent of our model.
  const liveSignals = (() => {
    const out = { soil: null, condition: null, drought: null };
    // Topsoil moisture — latest week for current crop year (state=NY)
    if (signalsData && signalsData.soilRows && signalsData.soilRows.length) {
      const cur = signalsData.soilRows.filter((r) => Number(r.year) === year && (r.stat_type || "").startsWith("TOPSOIL_PCT_"));
      if (cur.length) {
        const latestWeek = Math.max(...cur.map((r) => Number(r.week)));
        const wk = cur.filter((r) => Number(r.week) === latestWeek);
        const get = (st) => { const r = wk.find((x) => x.stat_type === st); return r ? Number(r.value) : 0; };
        out.soil = {
          week: latestWeek,
          very_short: get("TOPSOIL_PCT_VERY_SHORT"),
          short: get("TOPSOIL_PCT_SHORT"),
          adequate: get("TOPSOIL_PCT_ADEQUATE"),
          surplus: get("TOPSOIL_PCT_SURPLUS"),
        };
        out.soil.dry_pct = out.soil.very_short + out.soil.short;
        out.soil.wet_pct = out.soil.surplus;
      }
    }
    // Crop condition — latest week %G/E for current year (or placeholder)
    if (signalsData && signalsData.condRows && signalsData.condRows.length) {
      const cur = signalsData.condRows.filter((r) => Number(r.year) === year);
      if (cur.length) {
        const latestWeek = Math.max(...cur.map((r) => Number(r.week)));
        const ge = cur.find((r) => Number(r.week) === latestWeek);
        out.condition = { week: latestWeek, pct_good_excellent: ge ? Number(ge.value) : null };
      }
    }
    // USDM drought — latest week per county + WNY avg
    if (signalsData && signalsData.usdmRows && signalsData.usdmRows.length) {
      const cur = signalsData.usdmRows.filter((r) => Number(r.year) === year);
      if (cur.length) {
        const latestWeek = Math.max(...cur.map((r) => Number(r.week)));
        const wk = cur.filter((r) => Number(r.week) === latestWeek);
        const perCty = {};
        for (const r of wk) perCty[r.fips] = Number(r.value);
        const vals = Object.values(perCty);
        out.drought = {
          week: latestWeek,
          county_n: vals.length,
          wny_avg_dsci: vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0,
          max_dsci: vals.length ? Math.max(...vals) : 0,
          counties: perCty,
        };
      }
    }
    return out;
  })();

  // ── Historical WNY grain production by year ─────────────────────────
  // NASS-actual production = ACRES_HARVESTED × YIELD per county per year.
  // For county-years where NASS suppressed data (privacy), impute with that
  // county's mean of available years so the WNY annual total isn't artificially
  // deflated by missing data. Returns last 5 historical years.
  const historicalProduction = (() => {
    const wnyNames = new Set(MI_MODEL_COUNTIES.map((c) => c.name));
    const byCty = {};                  // county → { year → { yield, acres } }
    for (const r of yieldRows || []) {
      const nm = String(r.county || "").toUpperCase().trim();
      if (!wnyNames.has(nm)) continue;
      const yr = Number(r.year);
      (byCty[nm] = byCty[nm] || {})[yr] = byCty[nm][yr] || {};
      byCty[nm][yr].yield = Number(r.value);
    }
    for (const r of harvRows || []) {
      const nm = String(r.county || "").toUpperCase().trim();
      if (!wnyNames.has(nm)) continue;
      const yr = Number(r.year);
      (byCty[nm] = byCty[nm] || {})[yr] = byCty[nm][yr] || {};
      byCty[nm][yr].acres = Number(r.value);
    }
    const ctyProd = {};                // county → { year → bu }
    const ctyMean = {};                // county → mean bu across observed years
    for (const cty of Object.keys(byCty)) {
      ctyProd[cty] = {};
      const observed = [];
      for (const yr of Object.keys(byCty[cty])) {
        const d = byCty[cty][yr];
        if (d.yield > 0 && d.acres > 0) {
          const p = d.yield * d.acres;
          ctyProd[cty][Number(yr)] = p;
          observed.push(p);
        }
      }
      ctyMean[cty] = observed.length ? observed.reduce((a, b) => a + b, 0) / observed.length : 0;
    }
    const out = [];
    for (let yr = year - 5; yr < year; yr++) {
      let total = 0, observedN = 0, imputedN = 0;
      const imputedCounties = [];
      for (const m of MI_MODEL_COUNTIES) {
        const obs = ctyProd[m.name]?.[yr];
        if (obs != null) {
          total += obs;
          observedN++;
        } else if (ctyMean[m.name] > 0) {
          total += ctyMean[m.name];
          imputedN++;
          imputedCounties.push(m.name);
        }
      }
      out.push({
        year: yr,
        total_bu: Math.round(total),
        total_mbu: Math.round(total / 1e5) / 10,    // millions, 1 decimal
        observed_counties: observedN,
        imputed_counties: imputedN,
        imputed_county_names: imputedCounties,
      });
    }
    return out;
  })();

  // ── Uncertainty + phase tracker ─────────────────────────────────────
  // Total σ across factors (independence) → 87% band (±1.5σ). Today's σ is
  // dominated by silking unknown (~18 bu/ac). By harvest it should be ~5.
  const uncertainty = miUncertainty(year, surveyPace, earlyStress, bookCohortSummary, miBacktest(yieldRows, SCAP)?.mae);
  // ── Season Weather Score (0–100, ABSOLUTE) — current year only. Acre-weighted
  // book weather QUALITY (Apr 20 → today + forecast) vs agronomic ideal: 100 =
  // every day perfect, bleeds down as real stress accumulates. ──
  let _cqN = 0, _cqW = 0, _cqD = 0;
  for (const c of counties) { if (c.comfort_q == null) continue; const a = c.harv_acres_5yr || 0; if (!a) continue; _cqN += a * c.comfort_q; _cqW += a * (c.comfort_ww || 0); _cqD += a; }
  const _nowD = new Date();
  const seasonWeatherScore = (_cqD > 0 && year === _nowD.getFullYear()) ? miSeasonScoreFromQ(_cqN / _cqD, _cqW / _cqD) : null;
  return {
    ok: true, crop: "corn", year, wx_chart: (gridShadow && gridShadow.wx_chart) || wxChart, wx_pulse: (gridShadow && gridShadow.wx_pulse) || wxPulse, cohort_summary: bookCohortSummary, analog_years: analogYears, signals: liveSignals, early_stress: earlyStress, uncertainty,
    precip_since_plant: precipSincePlant,
    season_weather_score: seasonWeatherScore,
    statewide_pace: { pct_planted: statePacePct, week: paceWeek, normal_pct: paceNormalPct && miR1(paceNormalPct), factor: miR3(paceFactor), note: paceNote, ny_state_pct: nyStatePct != null ? miR1(nyStatePct) : null, ny_state_week: nyStateWeek, workable_days_full: surveyPace?.workableDaysFull, workable_days_partial: surveyPace?.workableDaysPartial, workable_days_blocked: surveyPace?.blockedDays, workability_window_days: surveyPace?.workabilityWindowDays || 30, soil_surplus_used: surveyPace?.soilSurplusUsed },
    production: prodCty > 0 ? {
      pred_bu: Math.round(prodPredSum), normal_bu: Math.round(prodNormSum),
      pct: prodNormSum > 0 ? prodPredSum / prodNormSum - 1 : null,
      counties: prodCty,
      nass_acres_counties_used: nassAcresUsed,    // 0 = pure projection; 16 = all WNY counties have NASS current-year acres
      nass_acres_total_counties: MI_MODEL_COUNTIES.length,
      planted_by_fpd_pct: surveyPace ? Math.round((surveyPace.plantedByFpdFrac != null ? surveyPace.plantedByFpdFrac : 1) * 100) : null,
      pp_ac: surveyPace && surveyPace.ppAc != null ? Math.round(surveyPace.ppAc) : null,
      pp_pct: ppPct,
      late_silage_pct: lateSilagePct,
      late_grain_pct: lateGrainPct,
      // Combined "late planted" = silage planted past FPD + grain planted
      // past FPD (during the 10%/5% willingness window). All take the same
      // yield hit, just different willingness curves driving them. This +
      // planted_by_fpd_pct + pp_pct should sum to ~100% of intended acres.
      late_planted_pct: Math.round((lateGrainPct + lateSilagePct + silageShortfallPct) * 10) / 10,
      grain_share_pct: Math.round(grainShare * 1000) / 10,
      fpd: surveyPace ? surveyPace.fpd : null,
    } : null,
    // ── MODEL MODE — A DEGRADED RUN MUST NEVER RENDER AS A FULL ONE ─────────
    // `mechActive` gates BOTH the mechanistic weather layer AND the RMA expected
    // anchor: with no planting cohorts, `miGridShadowWeather()` returns null,
    // `mechActive` stays false, and `effBaseline` silently falls all the way back
    // to the TREND baseline while `anchor_source` reads "trend". Nothing throws.
    // A frozen as-of backtest ran 1,170 county-checkpoints straight past this
    // before the residual columns gave it away (2026-08-23), and the page would
    // have shown the resulting trend forecast in the same type as a full run.
    //
    // Kevin: *"I would not allow the UI to quietly display the resulting trend
    // forecast as though it's the same model."* So the run STATES what it was.
    //
    // This is computed HERE, in the model, rather than in either caller — the
    // page and the snapshot cron both go through `miComputeYield`, and a second
    // copy of this rule is how the two come to disagree about whether a run was
    // degraded. It counts what the run actually PRODUCED (per-county
    // anchor_source and mech factor), never what it was handed, because the
    // inputs being present is not the same fact as the path having engaged.
    model_mode: (() => {
      const cs = counties.filter((c) => !c.error);
      const nFull = cs.filter((c) => c.anchor_source === "rma_expected" && c.mech_index_ratio != null).length;
      const nMech = cs.filter((c) => c.mech_index_ratio != null).length;
      const nRma = cs.filter((c) => c.anchor_source === "rma_expected").length;
      const cohorts = (surveyPace && surveyPace.cohorts && surveyPace.cohorts.length) || 0;
      const gs = gridShadow || null;
      const inputs = {
        rma_anchor: Object.keys(rmaExpBy).length > 0,
        planting_cohorts: cohorts > 0,
        grid_weather: !!gs,
        // These two are only knowable from miGridShadowWeather's own count of
        // the cell rows. With no gridShadow they are UNKNOWN, not false — the
        // cells may be fine and simply never reached. Saying "SSURGO ✗" when we
        // did not look would be the same class of claim this model is built to
        // avoid, so a null renders as "not reached", never as missing.
        ssurgo: gs ? gs.soil_cells > 0 : null,
        cdl: gs ? gs.cdl_cells > 0 : null,
      };
      const reasons = [];
      if (!inputs.planting_cohorts) reasons.push("planting cohorts unavailable — the survey produced none, so the mechanistic weather layer AND the RMA expected anchor are BOTH off and every county fell back to the trend baseline");
      if (!gs) reasons.push("no per-cell grid weather factor (miGridShadowWeather returned null)");
      if (!inputs.rma_anchor) reasons.push("no RMA expected yield published for this crop year");
      if (gs && gs.cells_total === 0) reasons.push("no grid cells");
      if (gs && gs.soil_cells === 0) reasons.push("grid cells carry no SSURGO water-holding capacity");
      const mode = cs.length && nFull === cs.length ? "full_mechanistic"
        : nFull > 0 ? "partial"
        : "degraded_trend";
      return {
        mode,
        counties_full: nFull, counties_mech: nMech, counties_rma_anchor: nRma, counties_total: cs.length,
        cohorts_n: cohorts,
        grid_cells_n: gs ? gs.cells_total : null,
        soil_cells_n: gs ? gs.soil_cells : null,
        cdl_cells_n: gs ? gs.cdl_cells : null,
        inputs, reasons,
      };
    })(),
    backtest: miBacktest(yieldRows, SCAP),
    wx_anom: anomN ? { gA: anomGsum / anomN, pA: anomPsum / anomN } : null,
    acres_actual_pct: surveyPace && surveyPace.plantedByFpdFrac != null ? surveyPace.plantedByFpdFrac - 1 : 0,
    cow_silage: cs,
    // Per-region herds, so miFilterResult can hand each region its own without
    // recomputing a model it has no inputs for. Keyed by MI_REGIONS key.
    cow_silage_by_region: cowSilageByRegion,
    silage_displace_pct: Math.round(silageDisplaceFrac * 1000) / 10,    // % of NY grain acres pulled to silage
    historical_production: historicalProduction,
    coefficients: CO, disclaimer: MI_DISCLAIMER, counties,
  };
}

// Date-windowed book corn planting pace, parameterized by a planting config
// (PC) so the base case and a Model-Lab what-if scenario can each build it.
// Identical to the prior inline logic when pcfg is omitted.
function miBuildSurveyPace(sFarmers, sIntents, sProg, year, pcfg, fcPrecip, soilSurplusPct) {
  const PC = pcfg || MI_CORN_PLANTING;
  const activeIds = new Set((sFarmers || []).filter((f) => f.is_active).map((f) => f.id));
  let intendedAc = 0;
  const intendedByFarmer = {};
  (sIntents || []).forEach((i) => { if (i.crop === "Corn" && activeIds.has(i.survey_farmer_id)) { const a = parseFloat(i.intended_acres) || 0; intendedAc += a; intendedByFarmer[i.survey_farmer_id] = (intendedByFarmer[i.survey_farmer_id] || 0) + a; } });
  const cornByFarmer = {};
  (sProg || []).forEach((p) => { if (p.crop !== "Corn" || !activeIds.has(p.survey_farmer_id)) return; (cornByFarmer[p.survey_farmer_id] = cornByFarmer[p.survey_farmer_id] || []).push(p); });
  const buckets = []; let plantedAc = 0;
  const farmerState = {};   // fid → { planted, lastDate } — for the per-farmer catch-up weighting
  Object.entries(cornByFarmer).forEach(([fid, subs]) => {
    subs.sort((a, b) => new Date(a.submitted_at) - new Date(b.submitted_at));
    let prev = 0, lastDate = null;
    subs.forEach((s) => { const cum = parseFloat(s.acres_planted) || 0; if (cum > prev) { buckets.push({ date: new Date(s.submitted_at), ac: cum - prev }); prev = cum; } lastDate = new Date(s.submitted_at); });
    plantedAc += prev;
    farmerState[fid] = { planted: prev, lastDate };
  });
  const unplantedAc = Math.max(0, intendedAc - plantedAc);
  // ── Catch-up nowcast (2026-06-07). Each farmer's survey is only current as of
  // THEIR last report. In a dry/workable stretch the bulk of "unplanted" acres
  // are from growers who reported 6-12 days ago and have planted since — they
  // just haven't logged it. So start the projection `catchupDays` BEFORE today
  // (the acre-weighted-by-UNPLANTED average staleness), letting the placement loop
  // credit that catch-up at real early-June yield potential instead of dumping it
  // into PP off a stale survey. fcPrecip carries OBSERVED precip for those recent
  // days, so the catch-up workability is real, not assumed. Fresh (today's) reports
  // contribute ~0 staleness, so a fully-current survey leaves the projection at today.
  const _today0 = new Date(); _today0.setHours(0, 0, 0, 0);
  let _staleAc = 0, _staleAcDays = 0;
  Object.keys(intendedByFarmer).forEach((fid) => {
    const fUn = Math.max(0, (intendedByFarmer[fid] || 0) - (farmerState[fid] ? farmerState[fid].planted : 0));
    if (fUn <= 0) return;
    const fLast = farmerState[fid] && farmerState[fid].lastDate ? new Date(farmerState[fid].lastDate) : _today0;
    fLast.setHours(0, 0, 0, 0);
    _staleAc += fUn;
    _staleAcDays += fUn * Math.max(0, Math.round((_today0 - fLast) / 86400000));
  });
  const catchupDays = _staleAc > 0 ? Math.round(_staleAcDays / _staleAc) : 0;
  let pSum = 0, pAc = 0;
  buckets.forEach((b) => { const pot = miCornPotential(b.date, PC); pSum += b.ac * pot; pAc += b.ac; });
  const plantedPot = pAc > 0 ? pSum / pAc : null;
  const totAc = plantedAc + unplantedAc;
  const fpdStr = getCornFPD("NY") || "June 10";
  const MON = { january: 0, february: 1, march: 2, april: 3, may: 4, june: 5, july: 6, august: 7, september: 8, october: 9, november: 10, december: 11 };
  const fpdParts = String(fpdStr).trim().split(/\s+/);
  const fpdMon = MON[(fpdParts[0] || "june").toLowerCase()];
  const fpdDate = new Date(year, fpdMon == null ? 5 : fpdMon, parseInt(fpdParts[1], 10) || 10);

  // ── Willingness-based projection (Kevin's spec) ────────────────────────
  // Same yield decay curve for both grain + silage once planted (per Kevin),
  // but WILLINGNESS to keep planting after FPD diverges:
  //   GRAIN: 100% ≤ Jun 5, 75% Jun 5-10, 10% Jun 10-15, 5% Jun 15-20, 0% after
  //   SILAGE: 100% ≤ Jun 10, 90% Jun 10-20, 25% Jun 20-25, 10% Jun 25-30, 5% Jul 1-5
  // We don't have per-farmer crop split, so apply a blended willingness using
  // the WNY silage share (~30-40% of corn acres normally). Past the calendar
  // window, unplanted-grain → PP, unplanted-silage → shortfall (dairies buy
  // grain acres from neighbors to chop = extra silage demand, handled by the
  // downstream cow-silage displacement model).
  const SILAGE_SHARE = (PC.silageShare != null) ? PC.silageShare : 0.35;
  let projPot = null, fracBeforeFpd = 1;
  let projGrainPlantedAc = 0, projSilagePlantedAc = 0;
  let grainPpAc = 0, silageShortfallAc = 0;
  let placementCohorts = [];   // {date, ac} entries that are projected (not actual)
  let workableDaysFull = 0, workableDaysPartial = 0, blockedDays = 0;
  let grainBeforeFpd = 0, silageBeforeFpd = 0;   // per-crop FPD split for the 100%-summing headline buckets (declared outer so the return block can read them)
  if (unplantedAc > 0) {
    const rainBlock = PC.rainBlockIn != null ? PC.rainBlockIn : 0.3;
    const soak = PC.soakIn != null ? PC.soakIn : 0.5;
    const dry = PC.drydownDays != null ? PC.drydownDays : 2;
    const maxH = PC.maxHorizonDays != null ? PC.maxHorizonDays : 90;   // bumped — silage window extends to early July
    // Begin `catchupDays` before today so the loop credits each grower's
    // unreported catch-up planting (see the catch-up-nowcast note above).
    const t0 = new Date(); t0.setHours(0, 0, 0, 0); t0.setDate(t0.getDate() - catchupDays);
    const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    const peakDays = PC.projectionDays || 30;   // peak rate: full-effort daily capacity
    // Split unplanted into per-crop unplanted starting pools
    let remGrain = unplantedAc * (1 - SILAGE_SHARE);
    let remSilage = unplantedAc * SILAGE_SHARE;
    const peakGrain = remGrain / peakDays;
    const peakSilage = remSilage / peakDays;
    let acPlacedSum = 0, potSum = 0, beforeFpdSum = 0;
    // Track cumulative workability stats — but ONLY count the next 30 days
    // for diagnostics. The projection loop can run up to maxH (~90) days to
    // accommodate the silage tail, but reporting 84 partial days out of a
    // 90-day window is misleading. The 30-day window is the immediate
    // catch-up question Kevin actually asks: "can they catch up before FPD?"
    const WORKABILITY_DIAG_WINDOW = 30;
    for (let k = 0; k < maxH && (remGrain > 0 || remSilage > 0); k++) {
      const d = new Date(t0); d.setDate(d.getDate() + k);
      // Continuous workability score (0-1) — accounts for today's rain,
      // recent precip (drydown), and current soil saturation.
      const wrk = miDailyWorkability(d, fcPrecip, soilSurplusPct, { rainBlockIn: rainBlock, soakIn: soak });
      // Diagnostics count the next 30 days FROM TODAY (not from the catch-up
      // start), so the "can they catch up?" card reflects the real road ahead.
      const _fromToday = Math.round((d - _today0) / 86400000);
      if (_fromToday >= 0 && _fromToday < WORKABILITY_DIAG_WINDOW) {
        if (wrk >= 0.85) workableDaysFull++;
        else if (wrk > 0.1) workableDaysPartial++;
        else blockedDays++;
      }
      if (wrk <= 0.01) continue;     // effectively blocked, no planting
      // Daily plantings per crop = peak × willingness × workability
      const wG = miPlantingWillingness(d, "grain");
      const wS = miPlantingWillingness(d, "silage");
      const todayG = Math.min(remGrain, peakGrain * wG * wrk);
      const todayS = Math.min(remSilage, peakSilage * wS * wrk);
      remGrain -= todayG;
      remSilage -= todayS;
      const todayTotal = todayG + todayS;
      if (todayTotal > 0) {
        placementCohorts.push({ date: new Date(d), ac: todayTotal });
        const pot = miCornPotential(d, PC);
        acPlacedSum += todayTotal;
        potSum += pot * todayTotal;
        if (d <= fpdDate) {
          beforeFpdSum += todayTotal;
          grainBeforeFpd += todayG;
          silageBeforeFpd += todayS;
        }
      }
    }
    projGrainPlantedAc = (unplantedAc * (1 - SILAGE_SHARE)) - remGrain;
    projSilagePlantedAc = (unplantedAc * SILAGE_SHARE) - remSilage;
    grainPpAc = remGrain;             // grain abandoned past willingness window → PP
    silageShortfallAc = remSilage;    // silage shortfall (rare; dairies buy from neighbors)
    projPot = acPlacedSum > 0 ? potSum / acPlacedSum : null;
    fracBeforeFpd = acPlacedSum > 0 ? beforeFpdSum / acPlacedSum : 1;
  }
  const projPlantedAc = projGrainPlantedAc + projSilagePlantedAc;
  const plantedByFpdAc = plantedAc + projPlantedAc * fracBeforeFpd;
  // Build cohorts: actual planted buckets + projected placements
  const cohorts = [];
  for (const b of buckets) if (b.ac > 0) cohorts.push({ date: new Date(b.date), ac: b.ac });
  for (const p of placementCohorts) cohorts.push({ date: p.date, ac: p.ac, projected: true });
  // Pace factor = average per-acre yield potential ACROSS ACRES THAT GET PLANTED.
  // Abandoned acres (grain PP, silage shortfall) reduce PRODUCTION via the
  // acres multiplier — they must NOT also drag down the per-acre yield, that's
  // double-counting. So the denominator is plantedAc + projPlantedAc, NOT totAc.
  const totalPlantedFinal = plantedAc + projPlantedAc;
  return {
    factor: totalPlantedFinal > 0 ? ((plantedPot != null ? plantedPot : 1) * plantedAc + (projPot != null ? projPot : 1) * projPlantedAc) / totalPlantedFinal : 1,
    plantedAc, unplantedAc, intendedAc,
    pctPlanted: intendedAc > 0 ? (plantedAc / intendedAc) * 100 : null,
    plantedPot, projPot, farms: Object.keys(cornByFarmer).length,
    plantedByFpdFrac: intendedAc > 0 ? Math.min(1, plantedByFpdAc / intendedAc) : 1,
    // PP = grain acres past willingness window. Silage shortfall is separate
    // (it pushes the dairy-driven silage displacement model harder, not PP).
    ppAc: Math.round(grainPpAc),
    catchupDays,   // acre-weighted survey staleness credited as catch-up planting (transparency)
    silageShortfallAc: Math.round(silageShortfallAc),
    projGrainPlantedAc: Math.round(projGrainPlantedAc),
    projSilagePlantedAc: Math.round(projSilagePlantedAc),
    // Per-crop breakdowns of WHEN projected acres land — for the
    // headline 100%-summing bucket math.
    projGrainBeforeFpd: Math.round(grainBeforeFpd),
    projSilageBeforeFpd: Math.round(silageBeforeFpd),
    projGrainAfterFpd: Math.round(projGrainPlantedAc - grainBeforeFpd),
    projSilageAfterFpd: Math.round(projSilagePlantedAc - silageBeforeFpd),
    silageShare: SILAGE_SHARE,
    fpd: fpdStr,
    cohorts,
    // Workability diagnostics for the next 30 days only — that's the
    // catch-up window. Days at full capacity (≥85%), partial (10-85%),
    // or blocked (<10%). High partial count = saturated soils limiting
    // planting capacity even on rain-free days.
    workableDaysFull, workableDaysPartial, blockedDays,
    workabilityWindowDays: 30,
    soilSurplusUsed: soilSurplusPct,
  };
}

// Default Model-Lab config (deep copy so sliders never mutate module consts).
// Scenario sliders, all "% vs normal". null = use the ACTUAL value (so the
// scenario starts equal to the Clover Ag base; drag to explore alternatives).
function miDefaultLab() {
  return { heatPct: null, rainPct: null, acresPct: null };
}

// One-line rollup of a compute result for the base-vs-scenario panel.
function miRollup(res) {
  if (!res || !res.counties) return null;
  const fc = res.counties.filter((c) => !c.error).map((c) => c.forecast_yield).filter((x) => x != null);
  return {
    fc: fc.length ? miR1(fc.reduce((a, b) => a + b, 0) / fc.length) : null,
    prodM: res.production ? res.production.pred_bu / 1e6 : null,
    pct: res.production ? res.production.pct : null,
  };
}

// Build the daily-brief snapshot payload from the BASE-case model result +
// nowcast — the exact numbers the headline shows. Written to mi_model_snapshot
// on each Yield-page load so the email mirrors the page (and inherits any
// future model refinement for free, since it's whatever the page computed).
function miSnapshotFromResult(res, nowcast, year) {
  if (!res || !res.counties) return null;
  const rows = res.counties.filter((c) => !c.error);
  const f = rows.map((c) => c.forecast_yield).filter((x) => x != null);
  // Acre-weighted regional yield (consistent with production + the page hero).
  // The weight is a parameter because not every figure here is weighted by the
  // SAME acres — see the five-year benchmark below, which must not be.
  const wavg = (k, weightKey) => {
    const wk = weightKey || "pred_acres";
    let wn = 0, wd = 0, sn = 0, ss = 0;
    for (const c of rows) { if (c[k] == null) continue; ss += c[k]; sn++; const w = c[wk] > 0 ? c[wk] : 0; wn += c[k] * w; wd += w; }
    return wd > 0 ? wn / wd : (sn > 0 ? ss / sn : null);
  };
  // Acre-weighted "actual vs normal" over the region: Σ(acres×actual) ÷ Σ(acres×normal) − 1.
  // Only counties carrying BOTH sides count, so a county missing its normal cannot
  // land in one sum and not the other. Returns null when nothing qualifies, which
  // is what sends the caller to its fallback rather than to a zero.
  const wratio = (getActual, getNormal) => {
    let an = 0, nn = 0;
    for (const c of rows) {
      const a = getActual(c), n = getNormal(c);
      if (a == null || n == null || !(n > 0)) continue;
      const w = c.pred_acres > 0 ? c.pred_acres : 0;
      if (!(w > 0)) continue;
      an += a * w; nn += n * w;
    }
    return nn > 0 ? an / nn - 1 : null;
  };
  const forecast = miR1(wavg("forecast_yield"));
  // ── A BENCHMARK THAT MOVES WHEN THE FORECAST DOES IS NOT A BENCHMARK ──────
  // `recent_5yr_mean` is a county's own settled NASS history and cannot change
  // day to day. This figure did anyway, because it was acre-weighted by
  // `pred_acres` — the acreage model's CURRENT-YEAR projection, which the model
  // revises on every run. So the "5-yr avg" line on the season chart wobbled
  // with the acreage forecast rather than sitting still: Western NY read 164.8
  // on 2026-06-17 and 160.6 on 06-18, a 4.2 bu/ac step in a five-year average,
  // with nothing on screen to say the yardstick had moved rather than the crop.
  //
  // The weight is the SAME five years as the yield: `harv_acres_5yr` is the
  // mean harvested GRAIN acres over exactly that window, so Σ(yield×acres) ÷
  // Σ(acres) reconstructs what the region actually averaged. It moves only when
  // NASS publishes another year, which is the one thing that SHOULD move it.
  // `planted_acres_5yr` would be wrong — it carries silage and prevent-plant
  // acres the grain yield was never measured over.
  //
  // Falls back to the old basis only if NOT ONE county carries the historical
  // acreage, so a thin year degrades to the previous behaviour instead of to a
  // blank; measured 2026-09-03 all 39 counties carry it. The basis is REPORTED
  // rather than assumed, because two differently-weighted averages that differ
  // by 0.04 bu/ac today could differ by more in a year nobody is watching.
  const fiveYrBasis = rows.some((c) => c.harv_acres_5yr > 0) ? "harv_acres_5yr" : "pred_acres";
  const fiveYr = miR1(wavg("recent_5yr_mean", fiveYrBasis));
  const u = res.uncertainty || null;
  const band = u && forecast != null ? u.sigma_total * 1.5 : null;
  const sp = res.statewide_pace || {};
  const pr = res.production || null;
  return {
    crop_year: year,
    phase: u ? u.phase : null,
    info_captured_pct: u ? u.confidence_pct : null,
    predicted_yield: forecast,
    yield_lo: band != null ? Math.round(Math.max(0, forecast - band)) : null,
    yield_hi: band != null ? Math.round(forecast + band) : null,
    five_yr_avg_yield: fiveYr,
    five_yr_avg_basis: fiveYrBasis,
    vs_5yr_avg: forecast != null && fiveYr != null ? miR1(forecast - fiveYr) : null,
    trend: miR1(wavg("baseline")),
    // Book composite weather factor (acre-weighted total_wx_factor) — so the email
    // brief's hero can show it, matching the page hero.
    wx_factor: (() => { const v = wavg("total_wx_factor"); return v != null ? Math.round(v * 1000) / 1000 : null; })(),
    // ── Growing-season fields (for the email brief's post-FPD card) ──
    soil_water_drag_pct: (() => {
      const cr = rows.filter((c) => c.water_balance && c.water_balance.drag_pct != null);
      if (!cr.length) return null;
      let wn = 0, wd = 0;
      for (const c of cr) { const w = c.pred_acres > 0 ? c.pred_acres : 0; wn += c.water_balance.drag_pct * w; wd += w; }
      return wd > 0 ? Math.round((wn / wd) * 10) / 10 : null;
    })(),
    // ── SEASON WEATHER IS THE REGION'S OWN, ACRE-WEIGHTED ──────────────────
    // These read `res.wx_pulse`, which is a single aggregate over every modelled
    // county and does not move when the region does — so Western NY and All New
    // York printed the same rainfall line. Each county already carries its own
    // season total and its own normal, from the same grid weather that drives the
    // forecast, so the region's number is just those summed acre-weighted.
    //
    // It is a RATIO OF WEIGHTED SUMS, not a mean of per-county percentages: the
    // question is how much rain the region got against what it normally gets, and
    // averaging percentages lets a small-normal county swing the answer.
    //
    // Measured 2026-08-20, this is not a rounding difference. wx_pulse said +2%
    // for both regions; acre-weighted it is +11% over the 39 counties and +21%
    // over the 16. The current-season inches agree closely (17.0 vs 16.5); the
    // gap is in the NORMAL, 16.7 in vs 15.0 in.
    //
    // WHY the normals differ is only HALF explained, and the first version of
    // this comment claimed all of it. Weighting is real but partial: the county
    // normals average 15.8 in unweighted and 15.0 acre-weighted, so it accounts
    // for 0.8 in of a 1.7 in gap. That half is evidenced — corn acres correlate
    // -0.718 with normal rainfall, so the high-acre counties are the drier ones.
    // The remaining ~0.9 in is a difference between two weather SOURCES that
    // nobody has settled: wx_pulse reads weather_county_daily (county-centroid
    // observations), these fields come from the 4km grid climatology. Do not
    // treat either normal as correct until an outside authority says so.
    //
    // What justifies this switch is narrower and does hold: the number is
    // region-aware, acre-weighted like every other headline beside it, and drawn
    // from the same weather that produces the forecast. GDD agrees between both
    // methods (+1.6 vs +1.3), the cross-check that a comparable number went in.
    // wx_pulse is left untouched and still drives the page's Spring Conditions card.
    season_precip_anom_pct: (() => {
      const v = wratio((c) => c.weather && c.weather.season_precip_in, (c) => c.weather && c.weather.normal_precip_in);
      if (v != null) return Math.round(v * 100);
      return res.wx_pulse && res.wx_pulse.season_precip_anom != null ? Math.round(res.wx_pulse.season_precip_anom * 100) : null;
    })(),
    season_gdd_anom_pct: (() => {
      const v = wratio((c) => c.weather && c.weather.season_gdd, (c) => c.weather && c.weather.normal_gdd);
      if (v != null) return Math.round(v * 100);
      return res.wx_pulse && res.wx_pulse.season_gdd_anom != null ? Math.round(res.wx_pulse.season_gdd_anom * 100) : null;
    })(),
    crop_condition_ge: res.signals && res.signals.condition ? res.signals.condition.pct_good_excellent : null,
    // Dairy silage demand (for the brief's silage card) — a growing herd + a stressed
    // crop pulls more corn acres into silage, reducing grain production.
    dairy: res.cow_silage ? {
      cows: res.cow_silage.currentCows,
      herd_growth_pct: res.cow_silage.herd_growth_pct,
      heifers_per_cow: res.cow_silage.heifer_per_cow,
      // The rate is what actually drives the acres — cows x tons/cow. The heifer
      // RATIO does not: while the floor is applied it cancels exactly out of
      // (cows x (1+r*0.45)) x (floor / (1+r*0.45)). So the card shows the rate,
      // not a heifers-per-cow figure that changes nothing beside it.
      silage_tons_per_cow: res.cow_silage.tonsPerCow,
      silage_tons_per_cow_calibrated: res.cow_silage.tonsPerCowCalibrated,
      silage_floor_applied: !!res.cow_silage.tonsPerCowFloorApplied,
      silage_acres_needed: res.cow_silage.requiredAcres,
      silage_acres_normal: res.cow_silage.normalAcres,
      extra_silage_acres: res.cow_silage.extraAcres,
    } : null,
    county_lo: f.length ? miR1(Math.min(...f)) : null,
    county_hi: f.length ? miR1(Math.max(...f)) : null,
    // Counties whose number is at the model's ceiling or above their own record —
    // see the `overreach` block in miComputeYield. Carried into the payload so the
    // emailed brief flags them too; a caveat that only exists on the screen is a
    // caveat the person reading the email never gets.
    overreach: rows.filter((c) => c.overreach).map((c) => ({
      name: c.name, y: c.forecast_yield, ...c.overreach,
    })),

    // Per-county forecast array — lets the Coverage Advisor pull each county's live modeled
    // yield as the 2026+ estimated ACTUAL yield (corn, NY grid). Updates whenever this page
    // recomputes, so the Advisor's trigger % tracks the yield forecast day to day.
    mi_counties: rows.map((c) => ({ name: c.name, fips: c.fips, y: c.forecast_yield, anchor: c.baseline, y5: c.recent_5yr_mean, ac: c.pred_acres })).filter((c) => c.y != null),
    weather_live: `${rows.filter((c) => c.weather && c.weather.status === "cohort").length}/${rows.length}`,
    book_pace_pct: sp.pct_planted != null ? Math.round(sp.pct_planted) : null,
    ny_state_pace_pct: sp.ny_state_pct != null ? Math.round(sp.ny_state_pct) : null,
    norm_pace_pct: sp.normal_pct != null ? Math.round(sp.normal_pct) : null,
    pace_factor: sp.factor != null ? sp.factor : null,
    production_bu: pr ? pr.pred_bu : null,
    production_counties: pr ? pr.counties : null,
    production_pct: pr ? pr.pct : null,
    production_normal_bu: pr ? pr.normal_bu : null,
    predicted_ac_by_fpd_pct: pr ? pr.planted_by_fpd_pct : null,
    predicted_late_planted_pct: pr ? (pr.late_planted_pct != null ? pr.late_planted_pct : pr.late_silage_pct) : null,
    predicted_pp_pct: pr ? pr.pp_pct : null,
    fpd: (pr && pr.fpd) || "Jun 10",
    nowcast_last_obs_pct: nowcast && nowcast.lastObsPct != null ? Math.round(nowcast.lastObsPct) : null,
    nowcast_last_obs_date: nowcast && nowcast.lastObsDate ? `${nowcast.lastObsDate.getMonth() + 1}/${nowcast.lastObsDate.getDate()}` : null,
    nowcast_next_monday_pct: nowcast && nowcast.nextMondayProjectedPct != null ? Math.round(nowcast.nextMondayProjectedPct) : null,
  };
}

// ── Which DAY a snapshot belongs to ───────────────────────────────────────
// US-Eastern, never UTC. Both writers of mi_yield_daily call this: the Yield
// page and the cron. They upsert on (crop_year, snapshot_date), so if they
// disagreed about "today" one run would silently create a SECOND row for the
// same day — and a page opened after 8pm ET (when UTC has already rolled) would
// stamp TOMORROW, exactly the day-boundary defect written up in api/_syncday.js.
// The agency's day is Eastern; this is the one place that decides it.
function miSnapshotDay(now = new Date()) {
  return now.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
}

// ── Reviving the Dates a round-trip through Postgres flattened ───────────────
//
// The model hands its callers real Dates (nowcast.lastObsDate, the daily series).
// Storing the result in Postgres serialises those to strings, and code like the
// PDF narrative's `lastObsDate.getMonth()` — or miSnapshotFromResult's own
// nowcast_last_obs_date — throws on a string. That is a crash that only appears
// on the CACHED path, which is exactly the kind of thing that ships unnoticed.
//
// It lives here rather than in src/App.jsx because the page is no longer the only
// reader of a stored result: api/_mi-brief.js rebuilds the Western NY view out of
// `mi_model_snapshot.result` and hits the same flattened dates. Strict ISO-8601
// only, so "6/15" style labels are left be.
const MI_ISO_DATE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;
function miReviveDates(v) {
  if (v == null) return v;
  if (typeof v === "string") {
    if (!MI_ISO_DATE.test(v)) return v;
    const d = new Date(v);
    return isNaN(d.getTime()) ? v : d;
  }
  if (Array.isArray(v)) return v.map(miReviveDates);
  if (typeof v === "object") {
    const o = {};
    for (const k of Object.keys(v)) o[k] = miReviveDates(v[k]);
    return o;
  }
  return v;
}

// ── The region filter, in the module BOTH callers share ──────────────────────
//
// This lived only in src/App.jsx, so the cron in api/_mi-snapshot.js — which
// writes the same tables the page writes — had no way to produce the Western NY
// view at all. That is the "one rule, two copies" shape this codebase keeps
// getting bitten by, except the second copy did not exist and the cron simply
// wrote one region's number into a row that named no region.
//
// `production` is genuinely RECOMPUTED from the surviving counties' own
// pred_prod / normal_prod rather than scaled, so the bushels are the selected
// counties' bushels. Counties the model could not run (no NASS history) carry no
// production and drop out of the count on both sides, which is why `counties` is
// recounted instead of reused.
const MI_REGIONS = {
  ny:  { label: "All New York", hint: `${MI_MODEL_COUNTIES.length} counties` },
  wny: { label: "Western NY",   hint: `${MI_WNY_FIPS.size} counties · the book` },
};
// Which dairy herd belongs to this region.
//
// A run from before `cow_silage_by_region` existed carries ONE herd and no record
// of whose it was — it was always Western NY's. So Western NY can still use it,
// and All New York gets null: the card disappears until the next model run rather
// than printing a 16-county herd under a 39-county header, which is the defect
// this exists to fix. Reading a stored snapshot is the normal case here (the page
// and the email both do it), so this cannot assume a fresh run.
function miRegionCowSilage(res, region) {
  const byRegion = res && res.cow_silage_by_region;
  if (byRegion && Object.prototype.hasOwnProperty.call(byRegion, region)) return byRegion[region] || null;
  return region === "wny" ? ((res && res.cow_silage) || null) : null;
}
function miFilterResult(res, region) {
  if (!res || !res.counties) return res;
  // Even the unfiltered region has to be handed its own herd — `res.cow_silage`
  // is the statewide one on a fresh run but Western NY's on a stored older one.
  const cow_silage = miRegionCowSilage(res, region);
  if (region === "ny") return cow_silage === res.cow_silage ? res : { ...res, cow_silage };
  const keep = (c) => MI_WNY_FIPS.has(String(c.fips));
  const counties = res.counties.filter(keep);
  const modelled = counties.filter((c) => !c.error);
  let pred = 0, normal = 0, n = 0;
  for (const c of modelled) {
    if (c.pred_prod == null || c.normal_prod == null) continue;
    pred += c.pred_prod; normal += c.normal_prod; n++;
  }
  const production = res.production && n
    ? { ...res.production, pred_bu: Math.round(pred), normal_bu: Math.round(normal),
        counties: n, pct: normal > 0 ? pred / normal - 1 : null }
    : (n ? null : res.production);
  return { ...res, counties, production, cow_silage };
}

module.exports = {
  MI_REGIONS, miFilterResult, MI_MODEL_VERSION, miInfoWeight,
  CORN_FPD_BY_STATE, HEIFER_AGE_FIRST_CALVING_MO, HEIFER_CULL_RATE, HEIFER_MORTALITY,
  HEIFER_SILAGE_RATIO, MI_COEF, MI_CORN, MI_CORN_PLANTING,
  MI_DISCLAIMER, MI_DISEASE, MI_SEASON, MI_SOY,
  MI_SOY_STAGE_LABEL, MI_STAGE_AT, MI_STAGE_HAS_TABLE, MI_STAGE_LABEL,
  MI_STAGE_TABLE, MI_MODEL_COUNTIES, MI_WNY_FIPS, MI_WNY_NAMES, MI_NY_NAMES, MI_REGION_NAMES,
  MI_MIN_YIELD_YEARS, MI_THIN_YIELD_YEARS, miCountyIsThin, MI_THIN_COUNTY_NAMES,
  MI_GRID_STATE_FIPS, MI_GRID_STATE_FILTER,
  backtestCornNowcastVsSurvey, buildCornNowcastChartSvg,
  buildCornSurveyTimeSeries, buildFieldPulse, computeCornMidWeekNowcast, computeCornStaleAdjustedAnchor,
  getCornFPD, miBacktest, miBaseline, miBktStageW,
  miBuildSurveyPace, miClamp, miClimoDoy, miCohortWeather,
  miComputeYield, miCornC2F, miCornCellIndex, miCornClamp,
  miCornComfort, miCornGdd, miCornPet, miCornPotential,
  miCornPulseSim, miCornSimulate, miCornStage, miCornTempComfort,
  miCowSilage, miDailyWorkability, miDefaultLab, miDiseasePressure,
  miDoyToMmdd, miEarlyStress, miEt0In, miGridShadowWeather,
  miGridWxPanels, miHeiferExpansionFactor, miHeiferPerCow, miKc,
  miMean, miModGdd, miOls, miPlantingWillingness,
  miPulseDoy, miR1, miR3, miRaMm,
  miReviveDates, miRollup, miRootFrac, miRunBucket, miSeasonAgg,
  miSeasonScoreFromQ, miSeasonScoreRaw, miSnapshotDay, miSnapshotFromResult,
  miSoyDaylenHr, miSoySim, miUncertainty, miVpdKpa,
};
